import { apiService } from "../../services/api";
import { authService } from "../../services/auth";
import { uploadImage } from "../../services/fileService";
import {
  buildFormDataFromRecord,
  deriveRecordIdFromRow,
  deriveScreenId,
  findSummaryListByAction,
  generateUniqueFormId,
  getParentIdFromFormData,
  getParentIdDescFromFormData,
  resolveUserCode,
  resolveView,
  screenConfigRegistry,
  walkComponentTree,
  getResolvedScreenConfig,
} from "../utils/configUtils";
import { buildPDFPayload } from "../../utils/pdfPayloadConfig";
import {
  ComponentType,
  ComponentConfig,
  LayoutTypes,
  ActionType,
} from "../../services/types/ComponentTypes";
import { AppThunk } from "../store";
import type { RootState } from "../store";
import {
  incrementFormInstanceCounter,
  resetScreenFormData,
  setScreenFormData,
  setScreenLoading,
  setScreenSaving,
  updateScreenFormField,
  setScreenEditMode,
  clearScreenEditMode,
} from "../slices/formsSlice";
import {
  navigateToView,
  resetPreviousView,
  setPreviousView,
} from "../slices/navigationSlice";
import { updateUserBranch } from "../slices/authSlice";
import {
  NotificationPayload,
  clearNotification,
  showNotification,
} from "../slices/notificationsSlice";
import {
  setCurrentBranch as setLayoutCurrentBranch,
  toggleSideMenu,
} from "../slices/layoutSlice";
import { clearLookup, fetchSubModuleRawData, addLookupItem, fetchLookup } from "../slices/lookupsSlice";

const LEAVE_APPROVAL_DRAWER_SCREEN_ID = "2212";

/**
 * Parts Issue Payload Builder
 * Builds a payload that matches Blazor format exactly:
 * - Wrapped in data object
 * - Includes next_action and next_state ONLY at root level (not in data)
 * - issued_qty = 1, bal_qty = 0 for all items (as numbers)
 * - All numeric values as numbers (not strings)
 * - Removes duplicate fields (keeps parent_id, removes parentid)
 */
interface PartsIssuePayload {
  data: {
    id?: string;
    issue_date?: string;
    parent_id?: string;
    parent_id_desc?: string;
    job_card_customername?: string;
    txt_customername?: string;
    job_card_reg_no?: string;
    txt_reg_no?: string;
    created_by?: string;
    created_at?: string;
    transaction_branch_id?: string;
    subform: Array<{
      tablename: string;
      name: string;
      data: Array<Record<string, any>>;
      manadatoryResult?: {
        success: boolean;
        fields: string[];
      };
    }>;
    [key: string]: any;
  };
  next_action?: string;
  next_state?: string;
}

/**
 * Converts a value to a number (for numeric fields)
 * Returns 0 if value is null, undefined, empty, or NaN
 */
const toNumber = (value: any): number => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }
  const numValue = parseFloat(String(value));
  return isNaN(numValue) ? 0 : numValue;
};

/**
 * Formats date to DD-MM-YYYY HH:mm:ss format
 * For issue_date: if no time component is provided, use current time (not midnight)
 * For created_at: always use current time
 */
const formatDateForPartsIssue = (dateValue: any, useCurrentTime: boolean = false): string => {
  if (!dateValue || dateValue === "") {
    // If useCurrentTime is true and no date provided, use current time
    if (useCurrentTime) {
      const now = new Date();
      const day = String(now.getDate()).padStart(2, "0");
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const year = now.getFullYear();
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");
      return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
    }
    return "";
  }

  let date: Date;
  let hasTimeComponent = false;

  if (dateValue instanceof Date) {
    date = dateValue;
    // Check if time is not midnight (has actual time component)
    hasTimeComponent = date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0;
  } else if (typeof dateValue === "string") {
    const strValue = dateValue.trim();
    if (!strValue) return "";

    // Check if string has time component
    hasTimeComponent = strValue.includes(":") && !strValue.endsWith(":00:00");

    // Handle ISO format (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss)
    if (strValue.includes("T")) {
      date = new Date(strValue);
      hasTimeComponent = date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0;
    } else if (strValue.match(/^\d{4}-\d{2}-\d{2}/)) {
      const parts = strValue.split(" ");
      if (parts[1]) {
        date = new Date(parts[0] + "T" + parts[1]);
        hasTimeComponent = true;
      } else {
        date = new Date(parts[0] + "T00:00:00");
        hasTimeComponent = false;
      }
    } else if (strValue.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/)) {
      const match = strValue.match(/^(\d{1,2})-(\d{1,2})-(\d{4})(?:\s+(\d{1,2}):(\d{1,2}):(\d{1,2}))?/);
      if (match) {
        const day = parseInt(match[1]);
        const month = parseInt(match[2]) - 1;
        const year = parseInt(match[3]);
        const hour = match[4] ? parseInt(match[4]) : 0;
        const minute = match[5] ? parseInt(match[5]) : 0;
        const second = match[6] ? parseInt(match[6]) : 0;
        date = new Date(year, month, day, hour, minute, second);
        hasTimeComponent = hour !== 0 || minute !== 0 || second !== 0;
      } else {
        date = new Date(strValue);
        hasTimeComponent = false;
      }
    } else {
      date = new Date(strValue);
      hasTimeComponent = date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0;
    }
  } else {
    date = new Date(dateValue);
    hasTimeComponent = date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0;
  }

  if (isNaN(date.getTime())) return "";

  // If no time component and useCurrentTime is true, use current time instead of midnight
  if (!hasTimeComponent && useCurrentTime) {
    const now = new Date();
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

/**
 * Builds Parts Issue payload matching Blazor format exactly
 */
export const buildPartsIssuePayload = (
  formState: any,
  nextAction: string = "",
  nextState: string = ""
): PartsIssuePayload => {
  // Initialize payload with data object and workflow fields at root level only
  const payload: PartsIssuePayload = {
    data: {
      subform: [],
    },
    next_action: nextAction,
    next_state: nextState,
  };

  // Process root-level fields - copy to data object, handling duplicates and formatting
  Object.keys(formState).forEach((key) => {
    // Skip subform data and workflow fields (they're handled separately)
    if (key === "service_spare_parts_issue" || key === "subform" || key === "next_action" || key === "next_state") {
      return;
    }

    const value = formState[key];

    // Handle duplicate parent fields - keep parent_id, remove parentid
    if (key === "parentid") {
      // Only set parent_id if it doesn't already exist
      if (!payload.data.parent_id) {
        payload.data.parent_id = value || "";
      }
      return; // Skip parentid, we use parent_id
    }

    // Handle duplicate customer/reg fields - keep both variants if they exist
    if (key === "txt_customername" || key === "txt_reg_no") {
      payload.data[key] = value;
      return;
    }

    // Format date fields consistently
    // For issue_date: use current time if no time component provided
    // For created_at: always use current time
    if (key === "issue_date") {
      payload.data[key] = formatDateForPartsIssue(value, true); // Use current time if no time component
    } else if (key === "created_at") {
      payload.data[key] = formatDateForPartsIssue(value, true); // Always use current time
    } else if (key.includes("date") || key.includes("Date")) {
      payload.data[key] = formatDateForPartsIssue(value);
    } else {
      payload.data[key] = value;
    }
  });

  // Process subform
  const subformData = formState.service_spare_parts_issue || formState.subform?.[0]?.data || [];

  if (Array.isArray(subformData) && subformData.length > 0) {
    payload.data.subform = [
      {
        tablename: "service_spare_parts_issue_details",
        name: "service_spare_parts_issue",
        data: subformData.map((item: any) => {
          const normalizedItem: Record<string, any> = { ...item };

          // Define numeric fields that should be converted to numbers
          const numericFields = [
            "qty",
            "stock_qty",
            "issued_qty",
            "bal_qty",
            "rate",
            "purchase_rate",
            "amount",
            "vat_id", // vat_id is also numeric
            "vat_amt",
            "net_amt",
          ];

          // Convert all numeric fields to numbers (not strings)
          numericFields.forEach((field) => {
            if (normalizedItem[field] !== undefined) {
              normalizedItem[field] = toNumber(normalizedItem[field]);
            }
          });



          // Remove UI-only fields and duplicates
          delete normalizedItem.next_action;
          delete normalizedItem.next_state;
          delete normalizedItem.parentid; // Remove if exists, we use parent_id

          return normalizedItem;
        }),
        manadatoryResult: {
          success: true,
          fields: [],
        },
      },
    ];
  }

  // Ensure next_action and next_state are NOT in data object
  delete payload.data.next_action;
  delete payload.data.next_state;

  return payload;
};

/**
 * Validates Parts Issue payload before submission
 */
export const validatePartsIssuePayload = (payload: PartsIssuePayload): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!payload.data) {
    errors.push("Payload must have a data object");
    return { valid: false, errors };
  }

  if (!payload.data.subform || !Array.isArray(payload.data.subform)) {
    errors.push("Payload.data.subform must be an array");
    return { valid: false, errors };
  }

  if (payload.data.subform.length === 0) {
    errors.push("Payload.data.subform.length must be > 0");
    return { valid: false, errors };
  }

  const partsIssueSubform = payload.data.subform.find(
    (sf) => sf.name === "service_spare_parts_issue" || sf.tablename === "service_spare_parts_issue_details"
  );

  if (!partsIssueSubform || !partsIssueSubform.data || partsIssueSubform.data.length === 0) {
    errors.push("Parts Issue subform must have at least one item");
    return { valid: false, errors };
  }

  // Validate each item
  partsIssueSubform.data.forEach((item: any, index: number) => {


    // Validate numeric fields are numbers, not strings
    const numericFields = ["qty", "stock_qty", "rate", "purchase_rate", "amount", "vat_id", "vat_amt", "net_amt"];
    numericFields.forEach((field) => {
      if (item[field] !== undefined && typeof item[field] !== "number") {
        errors.push(`Item ${index}: ${field} must be a number, got ${typeof item[field]}`);
      }
    });
  });

  // Validate workflow fields are only at root level, not in data
  if (payload.data.next_action !== undefined || payload.data.next_state !== undefined) {
    errors.push("next_action and next_state must only be at root level, not in data object");
  }

  // Validate no duplicate parent fields
  if (payload.data.parentid && payload.data.parent_id) {
    errors.push("Both parentid and parent_id exist - should only have parent_id");
  }

  return { valid: errors.length === 0, errors };
};

/**
 * Converts a numeric value to a decimal string (e.g., 1 -> "1.0", 1000 -> "1000.0")
 */
const toDecimalString = (value: any): string => {
  if (value === null || value === undefined || value === "") {
    return "0.0";
  }
  const numValue = parseFloat(String(value));
  if (isNaN(numValue)) {
    return "0.0";
  }
  // Format as decimal string (always include .0 for whole numbers)
  return numValue % 1 === 0 ? `${numValue}.0` : numValue.toString();
};

/**
 * Builds Parts Request payload matching Blazor format exactly
 * Similar to Parts Issue but with different subform structure
 */
export const buildPartsRequestPayload = (
  formState: any,
  nextAction: string = "",
  nextState: string = ""
): any => {
  // Initialize payload with data object and workflow fields at root level only
  const payload: any = {
    data: {
      subform: [],
    },
    next_action: nextAction,
    next_state: nextState,
  };

  // Process root-level fields - copy to data object, handling duplicates and formatting
  Object.keys(formState).forEach((key) => {
    // Skip subform data and workflow fields (they're handled separately)
    if (key === "requested_items" || key === "parts_request_details" || key === "subform" || key === "next_action" || key === "next_state") {
      return;
    }

    const value = formState[key];

    // Handle duplicate parent fields - keep both parent_id and parentid
    if (key === "parentid") {
      payload.data.parentid = value || "";
      // Also set parent_id if not already set
      if (!payload.data.parent_id) {
        payload.data.parent_id = value || "";
      }
      return;
    }

    // Format date fields consistently with full datetime (not midnight)
    // For issue_date: use current time if no time component provided
    // For created_at: always use current time
    if (key === "issue_date") {
      payload.data[key] = formatDateForPartsIssue(value, true); // Use current time if no time component
    } else if (key === "created_at") {
      payload.data[key] = formatDateForPartsIssue(value, true); // Always use current time
    } else if (key.includes("date") || key.includes("Date")) {
      payload.data[key] = formatDateForPartsIssue(value);
    } else {
      payload.data[key] = value;
    }
  });

  // Process subforms - parts request has two subforms: requested_items and parts_request_details
  const requestedItems = formState.requested_items || formState.subform?.[0]?.data || [];
  const partsRequestDetails = formState.parts_request_details || formState.subform?.[1]?.data || [];

  // Process requested_items subform
  if (Array.isArray(requestedItems) && requestedItems.length > 0) {
    const requestedItemsData = requestedItems.map((item: any) => {
      const normalizedItem: Record<string, any> = { ...item };

      // Convert req_qty to decimal string
      if (normalizedItem.req_qty !== undefined) {
        normalizedItem.req_qty = toDecimalString(normalizedItem.req_qty);
      }

      // Ensure required fields are present
      if (!normalizedItem.act) {
        normalizedItem.act = "add";
      }

      return normalizedItem;
    });

    payload.data.subform.push({
      tablename: "requested_items",
      name: "requested_items",
      data: requestedItemsData,
      manadatoryResult: {
        success: true,
        fields: [],
      },
    });
  }

  // Process parts_request_details subform
  if (Array.isArray(partsRequestDetails) && partsRequestDetails.length > 0) {
    const partsRequestDetailsData = partsRequestDetails.map((item: any) => {
      const normalizedItem: Record<string, any> = { ...item };

      // Define numeric fields that should be converted to decimal strings
      const numericFields = [
        "qty",
        "issued_qty",
        "rate",
        "amount",
        "vat_id",
        "vat_amt",
        "net_amt",
      ];

      // Convert all numeric fields to decimal strings
      numericFields.forEach((field) => {
        if (normalizedItem[field] !== undefined) {
          normalizedItem[field] = toDecimalString(normalizedItem[field]);
        }
      });

      // Ensure required fields are present
      if (!normalizedItem.act && !normalizedItem.soft_delete) {
        normalizedItem.act = "add";
      }

      return normalizedItem;
    });

    payload.data.subform.push({
      tablename: "parts_request_details",
      name: "parts_request_details",
      data: partsRequestDetailsData,
      manadatoryResult: {
        success: true,
        fields: [],
      },
    });
  }

  // Ensure issue_date is formatted correctly (not midnight - use current time if no time component)
  if (payload.data.issue_date) {
    payload.data.issue_date = formatDateForPartsIssue(payload.data.issue_date, true);
  }

  // Ensure created_at is formatted correctly (always use current time)
  if (payload.data.created_at) {
    payload.data.created_at = formatDateForPartsIssue(payload.data.created_at, true);
  }

  return payload;
};

import {
  openPDFViewer,
  setPDFViewerLoading,
  setPDFViewerError,
} from "../slices/pdfViewerSlice";
import {
  openHTMLDrawer,
  setHTMLDrawerLoading,
  setHTMLDrawerError,
} from "../slices/htmlDrawerSlice";
import { showConfirmDialog } from "../utils/confirmationDialogUtils";

const autoHideNotification = (
  dispatch: (action: any) => void,
  notification: NotificationPayload,
  timeoutMs = 3000
) => {
  dispatch(showNotification(notification));
  setTimeout(() => {
    dispatch(clearNotification());
  }, timeoutMs);
};

const ensureSlidingTimeout = (
  dispatch: (action: any) => void,
  notification: NotificationPayload,
  timeoutMs = 5000
) => {
  dispatch(showNotification(notification));
  setTimeout(() => {
    dispatch(clearNotification());
  }, timeoutMs);
};

export const executeConfigAction =
  (actionId: string, data?: any): AppThunk =>
    async (dispatch, getState) => {
      const state = getState() as RootState;
      const { currentView, previousView } = state.navigation;

      const summaryAction = data?.__summaryAction;

      if (actionId === "close_drawer") {
        dispatch(resetScreenFormData(LEAVE_APPROVAL_DRAWER_SCREEN_ID));
        dispatch(navigateToView({ view: "2201", rememberPrevious: false }));
        return;
      }

      if (actionId === "submit_leave_approval") {
        const fd = getState().forms.formDataByScreen[LEAVE_APPROVAL_DRAWER_SCREEN_ID] || {};
        const leaveId = fd.id ?? fd.ID;
        const raw = String(fd.drp_approval_action || "").toLowerCase();
        const statusNorm =
          raw === "approve" ? "approved" : raw === "reject" ? "rejected" : "";
        if (!leaveId) {
          ensureSlidingTimeout(
            dispatch,
            { message: "Missing leave record id.", type: "error" },
            4000
          );
          return;
        }
        if (!statusNorm) {
          ensureSlidingTimeout(
            dispatch,
            { message: "Please select Approve or Reject.", type: "error" },
            4000
          );
          return;
        }
        const remarks = (fd.txt_remarks ?? fd.rejection_reason ?? "").toString().trim();
        if (statusNorm === "rejected" && !remarks) {
          ensureSlidingTimeout(
            dispatch,
            { message: "Remarks are required when rejecting.", type: "error" },
            4000
          );
          return;
        }
        try {
          await apiService.post(
            `/pabws/hrms/leave/${encodeURIComponent(String(leaveId))}/decision`,
            {
              status: statusNorm,
              rejection_reason: statusNorm === "rejected" ? remarks : null,
            },
            { includeAuthToken: true, authHeaderType: "Bearer", errorHandling: "throw" }
          );
          dispatch(resetScreenFormData(LEAVE_APPROVAL_DRAWER_SCREEN_ID));
          ensureSlidingTimeout(dispatch, { message: "Leave decision saved.", type: "success" }, 4000);
          dispatch(navigateToView({ view: "2201", rememberPrevious: false }));

          const parentScreenId = "2201";
          try {
            const stats = await apiService.get<Record<string, any>>(
              `/pabws/hrms/leave/statistics`,
              undefined,
              { includeAuthToken: true, authHeaderType: "Bearer", errorHandling: "return-null" }
            );
            if (stats && typeof stats === "object") {
              const cur = getState().forms.formDataByScreen[parentScreenId] || {};
              dispatch(
                setScreenFormData({
                  screenId: parentScreenId,
                  data: {
                    ...cur,
                    total_leaves: stats.total_leaves ?? cur.total_leaves,
                    pending_leaves: stats.pending_leaves ?? cur.pending_leaves,
                    approved_leaves: stats.approved_leaves ?? cur.approved_leaves,
                    rejected_leaves: stats.rejected_leaves ?? cur.rejected_leaves,
                  },
                })
              );
            }
          } catch {
            /* optional refresh */
          }
        } catch (e: any) {
          console.error("[submit_leave_approval]", e);
          ensureSlidingTimeout(
            dispatch,
            {
              message: e?.message || "Failed to save leave decision.",
              type: "error",
            },
            5000
          );
        }
        return;
      }

      // Attendance: open session log screen (2208) as a full screen.
      // If attendance_id is available from the check-in/out widget, scope the route to that record.
      if (actionId === "attendanceSessionSummarylist") {
        const attendanceId =
          data?.attendance_id ||
          data?.attendanceId ||
          data?.attendanceID;
        const targetView = attendanceId ? `2208/${attendanceId}` : "2208";
        dispatch(resetScreenFormData("2208"));
        dispatch(navigateToView({ view: targetView, rememberPrevious: true }));
        return;
      }

      const isSummaryNavOrEdit =
        summaryAction?.ActionType &&
        (summaryAction.ActionType.toString().toLowerCase() === "edit" ||
          summaryAction.ActionType.toString().toLowerCase() === "view");

      if (isSummaryNavOrEdit) {
        const cleanRowData = { ...(data || {}) } as Record<string, any>;
        delete cleanRowData.__summaryAction;

        const summaryComponent = findSummaryListByAction(summaryAction);

        // Log vehicle summary edit action data
        const isVehicleSummary = summaryComponent?.Name === "vehicle_summary" ||
          summaryAction.Action?.Module === "Vehicle_Add" ||
          summaryAction.Action?.Navigate?.includes("vehicle_add");

        const deriveModuleName = (): string | undefined => {
          const moduleFromAction =
            summaryAction.Action?.Module ||
            summaryAction.Action?.module ||
            summaryAction.Action?.ModuleName ||
            summaryAction.Action?.modulename;

          if (moduleFromAction && typeof moduleFromAction === "string") {
            return moduleFromAction;
          }

          const navigatePath =
            summaryAction.Action?.Navigate || summaryAction.Action?.navigate;

          if (navigatePath && typeof navigatePath === "string") {
            const segments = navigatePath.split("/").filter(Boolean);
            if (segments.length >= 1) {
              // For routes like "modulev1/vehiclein_add", module name is second segment.
              if (segments[0] === "modulev1" && segments.length >= 2) {
                return segments[1];
              }
              // For routes like "emp_profile_add/<id>", module name is first segment.
              return segments[0];
            }
          }

          return undefined;
        };

        const moduleName = deriveModuleName();
        const recordId = deriveRecordIdFromRow(cleanRowData, {
          summaryAction,
        });

        if (!moduleName || !recordId) {
          ensureSlidingTimeout(
            dispatch,
            {
              message: "Unable to load record for editing.",
              type: "error",
            },
            5000
          );
          return;
        }

        const targetScreenId =
          deriveScreenId(summaryAction.ScreenId || "") ||
          summaryAction.ScreenId ||
          deriveScreenId(currentView) ||
          currentView;

        // Backend expects PascalCase "Customer_Add"; Navigate path gives "customer_add"
        // Same for "Supplier_Add"
        const apiModuleName =
          targetScreenId === "123" && moduleName === "customer_add"
            ? "Customer_Add"
            : targetScreenId === "128" && moduleName === "supplier_add"
            ? "Supplier_Add"
            : moduleName;

        const targetView = resolveView(targetScreenId);

        dispatch(setScreenLoading({ screenId: targetScreenId, isLoading: true }));

        // Set edit mode before loading form data
        dispatch(
          setScreenEditMode({
            screenId: targetScreenId,
            isEditMode: true,
            recordId: recordId,
          })
        );

        dispatch(
          setScreenFormData({
            screenId: targetScreenId,
            data: {
              ...buildFormDataFromRecord(null, {
                fallback: cleanRowData,
                summaryComponent,
                screenId: targetScreenId,
              }),
              id: recordId, // Explicitly include recordId to ensure edit mode detection
            },
          })
        );
        dispatch(navigateToView({ view: targetView }));

        try {
          // Employee profile has its own dedicated GET endpoint because the generic module
          // handler builds a dynamic SELECT from the module JSON fields. Some of those
          // fields (document_*, asset_*, same_as_current_address) are UI-only and do not
          // exist as columns in view_employee_profile, causing the generic query to fail.
          //
          // We detect this by targetScreenId === "2205" rather than apiModuleName because
          // the API-stored screen 2203 config may have a legacy Navigate value (e.g.
          // "emp_add/<id>") that derives moduleName as "emp_add", not "emp_profile_add".
          // ScreenId in the action is always authoritative and is already "2205".
          const isEmpProfile = targetScreenId === "2205";
          const endpoint = isEmpProfile
            ? `/pabws/hrms/employee/profile/${recordId}`
            : `/pabws/module/${apiModuleName}/${recordId}`;
          if (import.meta.env.DEV && isEmpProfile) {
            console.log("[EmpProfile Edit] Using dedicated endpoint:", endpoint, "| moduleName was:", apiModuleName);
          }
          // Capture isVehicleSummary for use in transformResponse closure
          const vehicleSummaryFlag = isVehicleSummary;
          const response = await apiService.get(endpoint, undefined, {
            includeAuthToken: true,
            authHeaderType: "Bearer",
            errorHandling: "throw",
            transformResponse: (raw: any) => {
              if (!raw) return null;

              let finalData = raw;

              // Check if raw itself is an encrypted string (Base64-like and doesn't look like JSON)
              if (typeof raw === 'string' && raw.length > 20 && !raw.trim().startsWith('{')) {
                try {
                  const decrypted = authService.decryptData(raw);
                  if (decrypted) {
                    finalData = decrypted;

                    // Log decrypted data for vehicle summary edit
                    if (vehicleSummaryFlag) {
                      console.log("=== VEHICLE SUMMARY EDIT - DECRYPTED DATA (raw string) ===");
                      console.log("Raw Encrypted String Length:", raw.length);
                      console.log("Raw Encrypted String Preview:", raw.substring(0, 100) + "...");
                      console.log("Decrypted Data:", JSON.stringify(decrypted, null, 2));
                      console.log("========================================================");
                    }
                  }
                } catch (e) {
                  if (isVehicleSummary) {
                    console.error("[Vehicle Summary] Failed to decrypt raw string:", e);
                  }
                }
              }
              // Check if raw.data is the encrypted string
              else if (raw && typeof raw === 'object' && raw.data && typeof raw.data === 'string' && raw.data.length > 20 && !raw.data.trim().startsWith('{')) {
                try {
                  const decrypted = authService.decryptData(raw.data);
                  if (decrypted) {
                    finalData = decrypted;

                    // Log decrypted data for vehicle summary edit
                    if (vehicleSummaryFlag) {
                      console.log("=== VEHICLE SUMMARY EDIT - DECRYPTED DATA (raw.data string) ===");
                      console.log("Raw Object:", JSON.stringify(raw, null, 2));
                      console.log("Encrypted String Length:", raw.data.length);
                      console.log("Encrypted String Preview:", raw.data.substring(0, 100) + "...");
                      console.log("Decrypted Data:", JSON.stringify(decrypted, null, 2));
                      console.log("=============================================================");
                    }
                  }
                } catch (e) {
                  if (isVehicleSummary) {
                    console.error("[Vehicle Summary] Failed to decrypt raw.data string:", e);
                  }
                }
              }
              // Otherwise use raw.data if it exists
              else if (raw && typeof raw === 'object' && raw.data) {
                finalData = raw.data;

                // Log if data is already decrypted (object format)
                if (isVehicleSummary) {
                  console.log("=== VEHICLE SUMMARY EDIT - RESPONSE DATA (already decrypted) ===");
                  console.log("Raw Response:", JSON.stringify(raw, null, 2));
                  console.log("Final Data (raw.data):", JSON.stringify(finalData, null, 2));
                  console.log("==============================================================");
                }
              }

              // After decryption/extraction, check for nested data property
              // Pattern: { data: { ...record fields... } }
              if (finalData && typeof finalData === 'object' && !Array.isArray(finalData) && finalData.data && typeof finalData.data === 'object' && !Array.isArray(finalData.data)) {
                if (isVehicleSummary) {
                  console.log("=== VEHICLE SUMMARY EDIT - NESTED DATA EXTRACTION ===");
                  console.log("Final Data (before extraction):", JSON.stringify(finalData, null, 2));
                  console.log("Extracted Data (finalData.data):", JSON.stringify(finalData.data, null, 2));
                  console.log("====================================================");
                }
                return finalData.data;
              }

              // If it's an array, return first record
              if (Array.isArray(finalData)) {
                if (isVehicleSummary) {
                  console.log("=== VEHICLE SUMMARY EDIT - ARRAY DATA ===");
                  console.log("Array Length:", finalData.length);
                  console.log("First Record:", JSON.stringify(finalData[0] || finalData, null, 2));
                  console.log("========================================");
                }
                return finalData[0] || finalData;
              }

              if (isVehicleSummary) {
                console.log("=== VEHICLE SUMMARY EDIT - FINAL TRANSFORMED DATA ===");
                console.log("Final Data:", JSON.stringify(finalData, null, 2));
                console.log("====================================================");
              }

              return finalData;
            },
          });

          // Build form data from API response
          let formDataFromRecord = buildFormDataFromRecord(response, {
            fallback: cleanRowData,
            summaryComponent,
            screenId: targetScreenId,
          });
          if (import.meta.env.DEV && isEmpProfile) {
            console.log("[EmpProfile Edit] API response sample:", {
              first_name: (response as any)?.first_name,
              last_name: (response as any)?.last_name,
              date_of_birth: (response as any)?.date_of_birth,
            });
            console.log("[EmpProfile Edit] Mapped formData sample:", {
              primary_first_name: formDataFromRecord["primary_first_name"],
              primary_last_name: formDataFromRecord["primary_last_name"],
              primary_date_of_birth: formDataFromRecord["primary_date_of_birth"],
            });
          }

          // FETCH SUBMODULE DATA AUTOMATICALLY
          // Find all submodules in the target screen config
          const screenConfig = getResolvedScreenConfig(targetScreenId);
          if (screenConfig) {
            const subModulesToFetch: any[] = [];
            walkComponentTree(screenConfig, (component) => {
              if (component.componentType === ComponentType.SUB_MODULE) {
                subModulesToFetch.push(component);
              }
              return false;
            });

            if (subModulesToFetch.length > 0) {

              // Fetch all submodule data in parallel
              const subModuleDataPromises = subModulesToFetch.map(async (sm) => {
                const subModuleName = sm.name || sm.BindValue;
                const subModuleBindValue = sm.BindValue;
                if (!subModuleName) return { name: null, bindValue: null, data: [] };

                try {
                  const data = await apiService.getSubModuleData(subModuleName, recordId);
                  return { name: subModuleName, bindValue: subModuleBindValue || null, data };
                } catch (e) {

                  return { name: subModuleName, bindValue: subModuleBindValue || null, data: [] };
                }
              });

              const subModuleResults = await Promise.all(subModuleDataPromises);

              // Merge submodule data into formData
              subModuleResults.forEach((res) => {
                if (!res.name) return;
                // Store under both name and BindValue to match ComponentRenderer lookup logic
                formDataFromRecord[res.name] = res.data;
                if (res.bindValue && res.bindValue !== res.name) {
                  formDataFromRecord[res.bindValue] = res.data;
                }
              });
            }
          }

          // CRITICAL: Ensure approve_status is explicitly included if present in response
          // This matches Blazor behavior where approve_status is directly mapped from ItemData
          if (response && (response.approve_status !== undefined || (response.data && response.data.approve_status !== undefined))) {
            const approveStatusValue = response.approve_status || (response.data && response.data.approve_status);
            if (approveStatusValue !== undefined && approveStatusValue !== null && approveStatusValue !== "") {
              formDataFromRecord.approve_status = approveStatusValue;


            }
          }

          dispatch(
            setScreenFormData({
              screenId: targetScreenId,
              data: {
                ...formDataFromRecord,
                id: recordId, // Explicitly include recordId to ensure edit mode detection
              },
            })
          );
        } catch (error) {
          dispatch(
            setScreenFormData({
              screenId: targetScreenId,
              data: {
                ...buildFormDataFromRecord(null, {
                  fallback: cleanRowData,
                  summaryComponent,
                  screenId: targetScreenId,
                }),
                id: recordId, // Explicitly include recordId to ensure edit mode detection
              },
            })
          );
          ensureSlidingTimeout(
            dispatch,
            {
              message: "Unable to load full details. Showing available data.",
              type: "error",
            },
            5000
          );
        } finally {
          dispatch(
            setScreenLoading({ screenId: targetScreenId, isLoading: false })
          );
        }

        return;
      }

      // Handle PDF_DOWNLOAD action for SUMMARY_LIST
      // Check if this is a PDF download action (either by actionId or summaryAction)
      const isPdfAction =
        (actionId === "PDF" || actionId === ActionType.PDF_DOWNLOAD) &&
        summaryAction?.ActionType &&
        (summaryAction.ActionType.toString().toUpperCase() === "PDF_DOWNLOAD" ||
          summaryAction.ActionType === ActionType.PDF_DOWNLOAD) &&
        summaryAction.Action?.Action === "PDF" &&
        summaryAction.Action?.Settings;

      if (isPdfAction) {
        const cleanRowData = { ...(data || {}) } as Record<string, any>;
        delete cleanRowData.__summaryAction;

        const settings = summaryAction.Action.Settings;
        const pdfName = settings;

        // Get user information from encrypted data
        const encryptedUserData =
          typeof window !== "undefined"
            ? localStorage.getItem("encryptedUserData")
            : null;
        if (!encryptedUserData) {
          ensureSlidingTimeout(
            dispatch,
            {
              message: "User not authenticated. Please login again.",
              type: "error",
            },
            5000
          );
          return;
        }

        const decryptedData = authService.decryptData(encryptedUserData);
        if (!decryptedData) {
          ensureSlidingTimeout(
            dispatch,
            {
              message: "Failed to decrypt user data. Please login again.",
              type: "error",
            },
            5000
          );
          return;
        }

        // Extract username and user ID from decrypted data
        const username =
          decryptedData.empName ||
          decryptedData.userName ||
          decryptedData.loginName ||
          "";
        const logined_userid =
          decryptedData.empId || decryptedData.userId || decryptedData.id || "1";

        // Build payload from row data - include all row fields plus user info
        // This makes it work for any SUMMARY_LIST, not just journal vouchers
        const payload = {
          ...cleanRowData, // Include all fields from the row
          // Ensure required fields are present (with fallbacks for different field name variations)
          id: cleanRowData.id || cleanRowData.Id || "",
          voucher_no:
            cleanRowData.voucher_no ||
            cleanRowData.voucherNo ||
            cleanRowData.VoucherNo ||
            "",
          voucher_date:
            cleanRowData.voucher_date ||
            cleanRowData.voucherDate ||
            cleanRowData.VoucherDate ||
            "",
          total_debit:
            cleanRowData.total_debit ||
            cleanRowData.totalDebit ||
            cleanRowData.TotalDebit ||
            0,
          total_credit:
            cleanRowData.total_credit ||
            cleanRowData.totalCredit ||
            cleanRowData.TotalCredit ||
            0,
          username: username,
          logined_userid: logined_userid,
        };

        try {
          // Make POST request to PDF endpoint
          const endpoint = `/pabreport/pdf`;
          const params = {
            name: pdfName,
          };

          if (import.meta.env.DEV) {
            console.log("[PDF Download] Request:", {
              endpoint,
              params,
              payload,
            });
          }

          // Use apiService.request to make POST request with query params and payload
          // Note: API returns JSON with base64-encoded PDF in data field, not raw blob
          const response = await apiService.request<{
            success?: boolean;
            data?: string; // Base64-encoded PDF string
            [key: string]: any;
          }>({
            endpoint,
            method: "POST",
            params, // Query parameters in URL
            payload, // Request body
            includeAuthToken: true,
            errorHandling: "throw",
            responseType: "json", // API returns JSON with base64 data
          });

          // Handle PDF response - convert base64 to blob and open in new tab
          let pdfBlob: Blob | null = null;

          if (response instanceof Blob) {
            // If response is already a Blob (unlikely but handle it)
            pdfBlob = response;
          } else if (response && typeof response === "object") {
            // Extract base64 string from response.data
            const base64Data = response.data || response.content || response.pdf;

            if (typeof base64Data === "string" && base64Data.length > 0) {
              try {
                // Convert base64 string to binary
                const byteCharacters = atob(base64Data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                  byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                pdfBlob = new Blob([byteArray], { type: "application/pdf" });
              } catch (error) {
                console.error("[PDF Download] Failed to decode base64:", error);
                throw new Error("Failed to decode PDF data from response");
              }
            } else {
              throw new Error("No PDF data found in response");
            }
          } else {
            throw new Error("Unexpected response format from PDF endpoint");
          }

          if (pdfBlob) {
            const url = window.URL.createObjectURL(pdfBlob);
            const fileName = `${pdfName}_${cleanRowData.voucher_no || cleanRowData.id || "document"}.pdf`;

            // Trigger download with proper filename so Save As gets correct name and extension
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            const newWindow = window.open(url, "_blank");
            if (newWindow) {
              // Revoke blob URL only when the new tab is closed so Save from tab keeps working
              const revokeWhenClosed = () => {
                const interval = setInterval(() => {
                  if (newWindow.closed) {
                    clearInterval(interval);
                    window.URL.revokeObjectURL(url);
                  }
                }, 500);
              };
              revokeWhenClosed();
              ensureSlidingTimeout(
                dispatch,
                {
                  message: "PDF opened in new tab.",
                  type: "success",
                },
                3000
              );
            } else {
              // Popup was blocked; download already triggered above, just revoke
              window.URL.revokeObjectURL(url);
              ensureSlidingTimeout(
                dispatch,
                {
                  message: "Popup blocked. PDF downloaded instead.",
                  type: "error",
                },
                3000
              );
            }
          } else {
            throw new Error("Failed to create PDF blob");
          }
        } catch (error) {
          console.error("[PDF Download] Error:", error);
          ensureSlidingTimeout(
            dispatch,
            {
              message:
                error instanceof Error
                  ? `Failed to download PDF: ${error.message}`
                  : "Failed to download PDF. Please try again.",
              type: "error",
            },
            5000
          );
        }

        return;
      }

      // Handle VIEW_REPORT action for SUMMARY_LIST
      // Check if this is a view report action (opens PDF in modal viewer)
      const isViewReportAction =
        (actionId === "Report" || actionId === ActionType.VIEW_REPORT) &&
        summaryAction?.ActionType &&
        (summaryAction.ActionType.toString().toUpperCase() === "VIEW_REPORT" ||
          summaryAction.ActionType === ActionType.VIEW_REPORT) &&
        summaryAction.Action?.Action === "Report";

      if (isViewReportAction) {
        const cleanRowData = { ...(data || {}) } as Record<string, any>;
        delete cleanRowData.__summaryAction;

        // Use Api field if available, otherwise use Settings field (like PDF_DOWNLOAD)
        const reportApi =
          summaryAction.Action.Api || summaryAction.Action.Settings;
        const reportName = reportApi || "report";

        // Get user information from encrypted data
        const encryptedUserData =
          typeof window !== "undefined"
            ? localStorage.getItem("encryptedUserData")
            : null;
        if (!encryptedUserData) {
          ensureSlidingTimeout(
            dispatch,
            {
              message: "User not authenticated. Please login again.",
              type: "error",
            },
            5000
          );
          return;
        }

        const decryptedData = authService.decryptData(encryptedUserData);
        if (!decryptedData) {
          ensureSlidingTimeout(
            dispatch,
            {
              message: "Failed to decrypt user data. Please login again.",
              type: "error",
            },
            5000
          );
          return;
        }

        // Extract username and user ID from decrypted data
        const username =
          decryptedData.empName ||
          decryptedData.userName ||
          decryptedData.loginName ||
          "";
        const logined_userid =
          decryptedData.empId || decryptedData.userId || decryptedData.id || "1";

        // Build payload from row data - include all row fields plus user info
        const payload = {
          ...cleanRowData, // Include all fields from the row
          // Ensure required fields are present (with fallbacks for different field name variations)
          id: cleanRowData.id || cleanRowData.Id || "",
          voucher_no:
            cleanRowData.voucher_no ||
            cleanRowData.voucherNo ||
            cleanRowData.VoucherNo ||
            "",
          voucher_date:
            cleanRowData.voucher_date ||
            cleanRowData.voucherDate ||
            cleanRowData.VoucherDate ||
            "",
          total_debit:
            cleanRowData.total_debit ||
            cleanRowData.totalDebit ||
            cleanRowData.TotalDebit ||
            0,
          total_credit:
            cleanRowData.total_credit ||
            cleanRowData.totalCredit ||
            cleanRowData.TotalCredit ||
            0,
          username: username,
          logined_userid: logined_userid,
        };

        try {
          // Show loading state
          dispatch(setHTMLDrawerLoading(true));

          // Make POST request to HTML report endpoint
          const endpoint = `/pabreport/report`;
          const params = {
            name: reportName,
          };

          if (import.meta.env.DEV) {
            console.log("[View Report] Request:", {
              endpoint,
              params,
              payload,
            });
          }

          // Use apiService.request to make POST request with query params and payload
          const response = await apiService.request<{
            success?: boolean;
            data?: string; // HTML string
            [key: string]: any;
          }>({
            endpoint,
            method: "POST",
            params, // Query parameters in URL
            payload, // Request body
            includeAuthToken: true,
            errorHandling: "throw",
            responseType: "json", // API returns JSON with HTML data
          });

          // Handle HTML response
          let htmlContent: string | null = null;

          if (response) {
            // Check if response is HTML directly (not wrapped in JSON)
            // Note: Cast to any since API can return either JSON object or raw HTML string
            if (typeof response === "string" && (response as any).includes("<html")) {
              htmlContent = response as string;
            }
            // Check if response is the expected format: { success: true, data: "<html>..." }
            else if (typeof response === "object" && response.success && response.data && typeof response.data === "string") {
              htmlContent = response.data;
            }
            // Check if data field contains HTML string
            else if (typeof response === "object" && response.data && typeof response.data === "string" && response.data.includes("<html")) {
              htmlContent = response.data;
            }
            // Fallback: try to extract HTML from any string field
            else if (typeof response === "object") {
              const possibleHtml = response.data || response.content || response.html || response.body;
              if (typeof possibleHtml === "string" && possibleHtml.length > 0) {
                htmlContent = possibleHtml;
              }
            }
          }

          if (htmlContent) {
            // Open HTML drawer with the content
            dispatch(
              openHTMLDrawer({
                htmlContent: htmlContent,
                reportName: reportName,
              })
            );

            if (import.meta.env.DEV) {
              console.log("[View Report] HTML drawer opened successfully");
            }
          } else {
            throw new Error("No HTML content found in response");
          }
        } catch (error) {
          console.error("[View Report] Error:", error);
          dispatch(
            setHTMLDrawerError(
              error instanceof Error
                ? `Failed to load HTML: ${error.message}`
                : "Failed to load HTML. Please try again."
            )
          );
          ensureSlidingTimeout(
            dispatch,
            {
              message:
                error instanceof Error
                  ? `Failed to view report: ${error.message}`
                  : "Failed to view report. Please try again.",
              type: "error",
            },
            5000
          );
        }

        return;
      }

      // Handle PDF action from BUTTON components
      // Pattern 1: onPressAction: "pdf_{PDFName}" (e.g., "pdf_Estimation")
      // Pattern 2: onPressAction: "pdf" with Settings field (e.g., Settings: "Invoice_info_basic")
      const isPdfButtonAction = actionId && (
        actionId.startsWith("pdf_") ||
        (actionId === "pdf" && data?.buttonConfig?.Settings)
      );

      if (isPdfButtonAction) {
        let pdfName: string;

        if (actionId.startsWith("pdf_")) {
          // Pattern 1: Extract from actionId (e.g., "Estimation" from "pdf_Estimation")
          pdfName = actionId.replace(/^pdf_/, "");
          if (import.meta.env.DEV) {
            console.log("[PDF Button] Pattern 1 - Extracted PDF name from actionId:", pdfName);
          }
        } else if (actionId === "pdf" && data?.buttonConfig?.Settings) {
          // Pattern 2: Extract from Settings field (e.g., "Invoice_info_basic" from Settings)
          pdfName = data.buttonConfig.Settings;
          if (import.meta.env.DEV) {
            console.log("[PDF Button] Pattern 2 - Extracted PDF name from Settings:", pdfName);
            console.log("[PDF Button] Button config:", data.buttonConfig);
          }
        } else {
          console.error("[PDF Button] Cannot determine PDF name.", {
            actionId,
            hasButtonConfig: !!data?.buttonConfig,
            settings: data?.buttonConfig?.Settings,
            dataKeys: data ? Object.keys(data) : [],
          });
          ensureSlidingTimeout(
            dispatch,
            {
              message: "PDF configuration error: Missing PDF name. Please check button configuration.",
              type: "error",
            },
            5000
          );
          return;
        }

        // Get current form data from state (for button actions, we use form data, not row data)
        const screenId = deriveScreenId(currentView) || currentView;
        const formData = state.forms.formDataByScreen[screenId] || data || {};

        // Get user information from encrypted data
        const encryptedUserData =
          typeof window !== "undefined"
            ? localStorage.getItem("encryptedUserData")
            : null;
        if (!encryptedUserData) {
          ensureSlidingTimeout(
            dispatch,
            {
              message: "User not authenticated. Please login again.",
              type: "error",
            },
            5000
          );
          return;
        }

        const decryptedData = authService.decryptData(encryptedUserData);
        if (!decryptedData) {
          ensureSlidingTimeout(
            dispatch,
            {
              message: "Failed to decrypt user data. Please login again.",
              type: "error",
            },
            5000
          );
          return;
        }

        // Extract username and user ID from decrypted data
        const username =
          decryptedData.empName ||
          decryptedData.userName ||
          decryptedData.loginName ||
          "";
        const logined_userid =
          decryptedData.empId || decryptedData.userId || decryptedData.id || "1";

        // Build PDF payload dynamically using reusable configuration system
        // This allows PDF-specific payload transformations to be configured per PDF type
        // The buildPDFPayload function handles default behavior if no config exists
        const payload = buildPDFPayload(pdfName, formData, logined_userid);

        try {
          // Make POST request to PDF endpoint
          const endpoint = `/pabreport/pdf`;
          const params = {
            name: pdfName,
          };

          if (import.meta.env.DEV) {
            console.log("[PDF Button] Request:", {
              endpoint,
              params,
              pdfName,
              payload: { ...payload, formDataKeys: Object.keys(formData) },
            });
          }

          // Use apiService.request to make POST request with query params and payload
          const response = await apiService.request<{
            success?: boolean;
            data?: string; // Base64-encoded PDF string
            [key: string]: any;
          }>({
            endpoint,
            method: "POST",
            params, // Query parameters in URL
            payload, // Request body
            includeAuthToken: true,
            errorHandling: "throw",
            responseType: "json", // API returns JSON with base64 data
          });

          // Handle PDF response - convert base64 to blob and open in new tab
          let pdfBlob: Blob | null = null;

          if (response instanceof Blob) {
            pdfBlob = response;
          } else if (response && typeof response === "object") {
            // Extract base64 string from response.data
            const base64Data = response.data || response.content || response.pdf;

            if (typeof base64Data === "string" && base64Data.length > 0) {
              try {
                // Convert base64 string to binary
                const byteCharacters = atob(base64Data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                  byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                pdfBlob = new Blob([byteArray], { type: "application/pdf" });
              } catch (error) {
                console.error("[PDF Button] Failed to decode base64:", error);
                throw new Error("Failed to decode PDF data from response");
              }
            } else {
              throw new Error("No PDF data found in response");
            }
          } else {
            throw new Error("Unexpected response format from PDF endpoint");
          }

          if (pdfBlob) {
            const url = window.URL.createObjectURL(pdfBlob);
            const fileName = `${pdfName}_${formData.invoice_no || formData.id || formData.parentid || "document"}.pdf`;

            // Trigger download with proper filename so Save As gets correct name and extension
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            const newWindow = window.open(url, "_blank");
            if (newWindow) {
              // Revoke blob URL only when the new tab is closed so Save from tab keeps working
              const revokeWhenClosed = () => {
                const interval = setInterval(() => {
                  if (newWindow.closed) {
                    clearInterval(interval);
                    window.URL.revokeObjectURL(url);
                  }
                }, 500);
              };
              revokeWhenClosed();
              ensureSlidingTimeout(
                dispatch,
                {
                  message: `PDF "${pdfName}" opened in new tab.`,
                  type: "success",
                },
                3000
              );
            } else {
              window.URL.revokeObjectURL(url);
              ensureSlidingTimeout(
                dispatch,
                {
                  message: "Popup blocked. PDF downloaded instead.",
                  type: "info",
                },
                3000
              );
            }
          } else {
            throw new Error("Failed to create PDF blob");
          }
        } catch (error) {
          console.error("[PDF Button] Error:", error);
          ensureSlidingTimeout(
            dispatch,
            {
              message:
                error instanceof Error
                  ? `Failed to generate PDF: ${error.message}`
                  : "Failed to generate PDF. Please try again.",
              type: "error",
            },
            5000
          );
        }

        return;
      }

      // Handle DELETE action for SUMMARY_LIST
      // Check if this is a delete action
      const isDeleteAction =
        summaryAction?.ActionType &&
        summaryAction.ActionType.toString().toLowerCase() === "delete" &&
        summaryAction.Action?.Action &&
        summaryAction.Action.Action.toString().toLowerCase() === "delete";

      if (isDeleteAction) {
        const cleanRowData = { ...(data || {}) } as Record<string, any>;
        delete cleanRowData.__summaryAction;

        // Extract module name from action configuration
        const deriveModuleName = (): string | undefined => {
          const moduleFromAction =
            summaryAction.Action?.Module ||
            summaryAction.Action?.module ||
            summaryAction.Action?.ModuleName ||
            summaryAction.Action?.modulename;

          if (moduleFromAction && typeof moduleFromAction === "string") {
            return moduleFromAction;
          }

          const navigatePath =
            summaryAction.Action?.Navigate || summaryAction.Action?.navigate;

          if (navigatePath && typeof navigatePath === "string") {
            const segments = navigatePath.split("/").filter(Boolean);
            if (segments.length >= 2) {
              const moduleSegment = segments[1];
              if (moduleSegment) {
                return moduleSegment
                  .split("_")
                  .map((segment) =>
                    segment
                      ? segment.charAt(0).toUpperCase() + segment.slice(1)
                      : segment
                  )
                  .join("_");
              }
            }
          }

          return undefined;
        };

        const moduleName = deriveModuleName();
        const recordId = deriveRecordIdFromRow(cleanRowData, {
          summaryAction,
        });

        if (!moduleName || !recordId) {
          ensureSlidingTimeout(
            dispatch,
            {
              message: "Unable to determine module or record ID for deletion.",
              type: "error",
            },
            5000
          );
          return;
        }

        // Show confirmation dialog
        const confirmMessage = `Are you sure you want to delete this record? This action cannot be undone.`;
        const confirmed = await showConfirmDialog(dispatch, confirmMessage);
        if (!confirmed) {
          return; // User cancelled
        }

        try {
          // Make DELETE request to API endpoint
          // Pattern: DELETE /pabws/module/{moduleName}/{recordId}
          const endpoint = `/pabws/module/${moduleName}/${recordId}`;

          if (import.meta.env.DEV) {
            console.log("[Delete] Request:", {
              endpoint,
              moduleName,
              recordId,
            });
          }

          const response = await apiService.delete<{
            success: boolean;
            data?: any;
            [key: string]: any;
          }>(endpoint, {
            includeAuthToken: true,
            authHeaderType: "Bearer",
            errorHandling: "throw",
          });

          if (import.meta.env.DEV) {
            console.log("[Delete] Response:", response);
          }

          // Check if deletion was successful
          if (
            response &&
            (response.success === true || response.success === undefined)
          ) {
            // Show success notification
            ensureSlidingTimeout(
              dispatch,
              {
                message: "Record deleted successfully.",
                type: "success",
              },
              3000
            );

            // Refresh the DataTable by updating refreshTimestamp
            // This triggers a re-fetch in DataTable component
            dispatch(
              navigateToView({
                view: currentView,
                rememberPrevious: false,
              })
            );
          } else {
            throw new Error("Delete operation returned unsuccessful response");
          }
        } catch (error) {
          console.error("[Delete] Error:", error);
          ensureSlidingTimeout(
            dispatch,
            {
              message:
                error instanceof Error
                  ? `Failed to delete record: ${error.message}`
                  : "Failed to delete record. Please try again.",
              type: "error",
            },
            5000
          );
        }

        return;
      }

      if (actionId.startsWith("navigate_")) {
        const view = actionId.replace("navigate_", "");
        // Extract recordId if present (e.g., "151/6")
        const parts = view.split("/");
        const targetView = parts[0];
        const recordId = parts.length > 1 ? parts[1] : undefined;

        if (targetView === "back") {
          dispatch(navigateToView({ view: "101", rememberPrevious: false }));
          dispatch(resetScreenFormData(currentView));
          return;
        }

        const targetScreenId = deriveScreenId(targetView) || targetView;

        // Leave approve/reject: open as normal screen and prefill from selected summary row.
        if (targetScreenId === LEAVE_APPROVAL_DRAWER_SCREEN_ID && data && typeof data === "object") {
          const row = { ...(data as Record<string, any>) };
          delete (row as any).__summaryAction;
          const formPatch: Record<string, any> = {
            id: row.id ?? row.ID,
            drp_approval_action: "",
            txt_employee_name: row.employee_name ?? row.employeeName ?? "",
            date_start: row.start_date ?? row.startDate,
            date_end: row.end_date ?? row.endDate,
            txt_total_days: row.total_days ?? row.totalDays,
            txt_reason: row.reason ?? "",
            txt_remarks: "",
          };
          dispatch(resetScreenFormData(targetScreenId));
          dispatch(navigateToView({ view: targetView }));
          dispatch(
            setScreenFormData({
              screenId: targetScreenId,
              data: formPatch,
            })
          );
          return;
        }

        // Store estimate_id for Proforma Invoice pre-population (before reset)
        let estimateIdForProforma: string | undefined;
        if (targetScreenId === "175") {
          const currentScreenId = deriveScreenId(currentView) || currentView;
          const currentFormData = state.forms.formDataByScreen[currentScreenId] || data?.formData || {};

          // Try to get estimate_id from various possible fields
          estimateIdForProforma =
            currentFormData.estimate_id ||
            currentFormData.estimation_id ||
            currentFormData.estimateId ||
            data?.estimate_id ||
            data?.estimation_id;
        }

        if (targetView === "100" && currentView === "101") {
          // ... (Special logic for screen 101/100 transition preserved)
          dispatch(setScreenLoading({ screenId: "100", isLoading: true }));

          try {
            const response = await apiService.get(
              "/pabws/screens/get_screens_with_id",
              {
                screenid: "101",
              },
              {
                errorHandling: "throw",
                transformResponse: (data: any) => {
                  if (data) {
                    if (data.data) {
                      return Array.isArray(data.data) ? data.data[0] : data.data;
                    } else if (Array.isArray(data)) {
                      return data[0];
                    }
                    return data;
                  }
                  return null;
                },
              }
            );

            const screenData: any = response;

            dispatch(
              setScreenFormData({
                screenId: "100",
                data: buildFormDataFromRecord(screenData, {
                  screenId: "100",
                }),
              })
            );
          } catch (error) {
            dispatch(resetScreenFormData("100"));
          } finally {
            dispatch(setScreenLoading({ screenId: "100", isLoading: false }));
          }
        } else {
          dispatch(resetScreenFormData(targetView));
        }

        // Navigate to the target view first
        dispatch(navigateToView({ view: targetView }));

        // HRMS Holiday Calendar -> Add Holiday / Add Weekly Off:
        // Carry current holiday_calendar.id into the child add screens as parent_id so inserts
        // link to the correct calendar.
        try {
          const currentScreenId = deriveScreenId(currentView) || currentView;
          const currentFormData = state.forms.formDataByScreen[currentScreenId] || data?.formData || {};
          const currentCalendarId = currentFormData?.id;
          const targetScreenId = deriveScreenId(targetView) || targetView;

          if (
            currentCalendarId &&
            (targetScreenId === "2224" || targetScreenId === "2225") &&
            (currentScreenId === "2220" || String(currentView).includes("holiday_calendar_details"))
          ) {
            dispatch(
              setScreenFormData({
                screenId: targetScreenId,
                data: {
                  parentid: String(currentCalendarId),
                  parent_id: String(currentCalendarId),
                },
              })
            );
          }
        } catch (e) {
          // Non-fatal: navigation should still proceed even if we fail to prefill parent_id
          if (import.meta.env.DEV) {
            console.warn("[Holiday Calendar] Failed to carry parent_id into child screen:", e);
          }
        }

        // Special handling for Proforma Invoice (175) - pre-populate with estimate_id
        // Do this AFTER navigation so the screen is ready to receive the data
        if (targetScreenId === "175" && !recordId) {
          const currentScreenId = deriveScreenId(currentView) || currentView;
          const currentFormData = state.forms.formDataByScreen[currentScreenId] || data?.formData || {};

          if (import.meta.env.DEV) {
            console.log("[Proforma Navigation] Current screen:", currentScreenId, "Form data:", currentFormData);
          }

          // Try to get inspection_id from various possible fields
          // The form data already contains inspection_id when QC Approve loads
          let inspectionId =
            currentFormData.inspection_id ||
            currentFormData.inspectionId ||
            currentFormData.id ||
            data?.inspection_id ||
            data?.inspectionId ||
            data?.id;

          if (import.meta.env.DEV) {
            console.log("[Proforma Navigation] inspection_id from form data:", inspectionId);
          }

          // If not found and we're on QC Approve screen (157), try to fetch from parent record
          if (!inspectionId && currentScreenId === "157") {
            const parentId = currentFormData.parentid || currentFormData.parent_id;
            if (import.meta.env.DEV) {
              console.log("[Proforma Navigation] Fetching inspection_id from parent:", parentId);
            }
            if (parentId) {
              // Try to fetch inspection_id from the vehicle summary record
              // The parent_id in QC Approve is the VIN/inspection ID
              try {
                const vehicleSummaryResponse = await apiService.get(
                  `/pabws/module/vehiclein_summary/${parentId}`,
                  {},
                  {
                    errorHandling: "return-null",
                    transformResponse: (raw: any) => {
                      if (raw && raw.data && typeof raw.data === 'string') {
                        const decrypted = authService.decryptData(raw.data);
                        return decrypted ? (Array.isArray(decrypted) ? decrypted[0] : decrypted) : raw;
                      }
                      if (raw && raw.data) {
                        return Array.isArray(raw.data) ? raw.data[0] : raw.data;
                      }
                      return Array.isArray(raw) ? raw[0] : raw;
                    }
                  }
                );
                if (vehicleSummaryResponse) {
                  inspectionId = vehicleSummaryResponse.inspection_id || vehicleSummaryResponse.id;
                  if (import.meta.env.DEV) {
                    console.log("[Proforma Navigation] Found inspection_id:", inspectionId);
                  }
                }
              } catch (error) {
                if (import.meta.env.DEV) {
                  console.log("[Proforma Navigation] Could not fetch inspection_id from parent:", error);
                }
              }
            }
          }

          if (inspectionId) {
            if (import.meta.env.DEV) {
              console.log("[Proforma Navigation] Pre-populating Proforma Invoice with inspection_id:", inspectionId);
            }

            // Fetch the lookup first to find the matching estimate_id
            const lookupName = "estimation_no_proforma_search_Select";

            // Use requestAnimationFrame to avoid blocking render
            requestAnimationFrame(() => {
              dispatch(
                fetchLookup({
                  lookupName: lookupName,
                  params: {
                    parentid: inspectionId,
                    lookuptype: "Select",
                  },
                })
              )
                .unwrap()
                .then((lookupResult) => {
                  // Find the option where option.id matches inspection_id
                  const matchedOption = lookupResult.options?.find((opt: any) => {
                    const optId = opt.id || (opt as any).inspection_id;
                    return optId === inspectionId || String(optId) === String(inspectionId);
                  });

                  if (matchedOption) {
                    // Use the estimate_id (value) from the matched option
                    const estimateId = matchedOption.value;
                    if (import.meta.env.DEV) {
                      console.log("[Proforma Navigation] Found matching option:", {
                        inspectionId,
                        estimateId,
                        option: matchedOption,
                      });
                    }

                    // Set form data with the estimate_id using requestAnimationFrame to avoid React warnings
                    requestAnimationFrame(() => {
                      (async () => {
                        // 1) Merge parentid / parent_id from the matched estimate into existing form data
                        const currentFormData =
                          getState().forms.formDataByScreen[targetScreenId] || {};
                        dispatch(
                          setScreenFormData({
                            screenId: targetScreenId,
                            data: {
                              ...currentFormData,
                              parentid: estimateId,
                              parent_id: estimateId,
                            },
                          })
                        );

                        if (import.meta.env.DEV) {
                          console.log("[Proforma Navigation] Form data set with estimate_id:", estimateId);
                        }

                        // 2) Also fetch estimation record to pre-populate discount on Proforma
                        try {
                          const { apiService } = await import("../../services/api");
                          const estimationResponse = await apiService.get(
                            `/pabws/utility/estimation_items`,
                            { id: estimateId },
                            {
                              includeAuthToken: true,
                              authHeaderType: "Bearer",
                              errorHandling: "return-empty",
                              transformResponse: (raw: any) => {
                                if (!raw) return null;
                                if (raw.success && raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                                  return raw.data[0];
                                }
                                if (raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                                  return raw.data[0];
                                }
                                return raw;
                              },
                            }
                          );

                          if (
                            estimationResponse &&
                            estimationResponse.discount !== undefined &&
                            estimationResponse.discount !== null
                          ) {
                            const discountStr = String(estimationResponse.discount);
                            const latestFormData =
                              getState().forms.formDataByScreen[targetScreenId] || {};
                            dispatch(
                              setScreenFormData({
                                screenId: targetScreenId,
                                data: {
                                  ...latestFormData,
                                  txt_discount: discountStr,
                                  discount: discountStr,
                                },
                              })
                            );
                          }
                        } catch (error) {
                          if (import.meta.env.DEV) {
                            console.warn(
                              "[Proforma Navigation] Failed to pre-populate discount from estimation (QC → Proforma):",
                              { estimateId, error }
                            );
                          }
                        }
                      })();
                    });
                  } else {
                    if (import.meta.env.DEV) {
                      console.log("[Proforma Navigation] No matching option found for inspection_id:", inspectionId);
                      console.log("[Proforma Navigation] Available options:", lookupResult.options?.slice(0, 5));
                    }
                    // Fallback: set inspection_id anyway
                    requestAnimationFrame(() => {
                      dispatch(
                        setScreenFormData({
                          screenId: targetScreenId,
                          data: {
                            parentid: inspectionId,
                            parent_id: inspectionId,
                          },
                        })
                      );
                    });
                  }
                })
                .catch((error) => {
                  if (import.meta.env.DEV) {
                    console.log("[Proforma Navigation] Lookup fetch failed, using inspection_id:", error);
                  }
                  // Fallback: set inspection_id if lookup fails
                  requestAnimationFrame(() => {
                    dispatch(
                      setScreenFormData({
                        screenId: targetScreenId,
                        data: {
                          parentid: inspectionId,
                          parent_id: inspectionId,
                        },
                      })
                    );
                  });
                });
            });
          } else {
            if (import.meta.env.DEV) {
              console.log("[Proforma Navigation] No inspection_id found to pre-populate");
            }
          }
        }

        // If we have a recordId, we need to fetch the data
        if (recordId) {
          dispatch(setScreenLoading({ screenId: targetScreenId, isLoading: true }));

          try {
            // Set edit mode
            dispatch(
              setScreenEditMode({
                screenId: targetScreenId,
                isEditMode: true,
                recordId: String(recordId),
              })
            );

            // Get the screen configuration to find module names and submodules
            const screenConfig = getResolvedScreenConfig(targetScreenId);
            let mainModuleName = "";
            let mainTableName = "";

            if (screenConfig) {
              walkComponentTree(screenConfig, (component) => {
                if (component.componentType === LayoutTypes.FORM || (component as any).TableName) {
                  mainModuleName = (component as any).Name || (component as any).TableName;
                  mainTableName = (component as any).TableName;
                  return true;
                }
                return false;
              });
            }

            // Customer add (123): backend expects PascalCase "Customer_Add", not "customer_add"
            if (targetScreenId === "123" && (!mainModuleName || mainModuleName === "customer_add")) {
              mainModuleName = "Customer_Add";
            }

            if (mainModuleName) {
              // Fetch main record
              const response = await apiService.get(
                `/pabws/module/${mainModuleName}/${recordId}`,
                {},
                {
                  errorHandling: "throw",
                  transformResponse: (raw: any) => {
                    // Decryption handled by apiService.get transformResponse if it matches standard patterns
                    // In executeConfigAction we often see explicit decryption for workflow actions
                    // but here we can rely on standard apiService transformation if configured.
                    if (raw && raw.data && typeof raw.data === 'string') {
                      const decrypted = authService.decryptData(raw.data);
                      return decrypted ? (Array.isArray(decrypted) ? decrypted[0] : decrypted) : raw;
                    }
                    if (raw && raw.data) {
                      return Array.isArray(raw.data) ? raw.data[0] : raw.data;
                    }
                    return Array.isArray(raw) ? raw[0] : raw;
                  }
                }
              );

              if (response) {
                if (import.meta.env.DEV) {
                  console.log(`[Direct Navigation] Fetched record for ${targetScreenId}:`, response);
                }

                // For Proforma (175), preserve any values already set in formData (e.g. discount from Estimate)
                const existingFormData =
                  targetScreenId === "175"
                    ? getState().forms.formDataByScreen[targetScreenId] || {}
                    : {};

                const formData = buildFormDataFromRecord(response, {
                  screenId: targetScreenId,
                  fallback: existingFormData,
                });

                if (import.meta.env.DEV) {
                  console.log(`[Direct Navigation] Mapped formData for ${targetScreenId}:`, formData, {
                    existingDiscount: {
                      txt_discount: (existingFormData as any)?.txt_discount,
                      discount: (existingFormData as any)?.discount,
                    },
                    finalDiscount: {
                      txt_discount: (formData as any)?.txt_discount,
                      discount: (formData as any)?.discount,
                    },
                  });
                }

                dispatch(
                  setScreenFormData({
                    screenId: targetScreenId,
                    data: { ...formData, id: recordId },
                  })
                );

                // Load submodules generically
                const subModules: any[] = [];
                if (screenConfig) {
                  walkComponentTree(screenConfig, (component) => {
                    if (component.componentType === ComponentType.SUB_MODULE) {
                      subModules.push(component);
                    }
                    return false;
                  });
                }

                for (const subModule of subModules) {
                  const subModuleName = subModule.name || subModule.Name;
                  const subModuleTableName = subModule.tableName || subModule.TableName;
                  const bindValue = subModule.BindValue || subModule.name;

                  if (subModuleName && subModuleTableName) {
                    try {
                      const subModuleResponse = await apiService.getSubModuleData(subModuleName, String(recordId));

                      if (subModuleResponse && Array.isArray(subModuleResponse)) {
                        let mappedRows: any[] = subModuleResponse;

                        // Map raw API rows into SubModule row shape using childControls (Json_Attribute -> field BindValue/name)
                        if (Array.isArray((subModule as any).childControls) && (subModule as any).childControls.length > 0) {
                          mappedRows = subModuleResponse.map((item: any) => {
                            const row: Record<string, any> = { ...item };

                            (subModule as any).childControls.forEach((control: any) => {
                              const fieldName = control.name || control.BindValue;
                              const jsonAttr = control.Json_Attribute;

                              if (!fieldName || !jsonAttr) return;

                              let value =
                                item[jsonAttr] ??
                                item[jsonAttr.toLowerCase?.()] ??
                                item[jsonAttr.toUpperCase?.()] ??
                                item[jsonAttr.replace?.(/_/g, "")];

                              if (value === undefined && item[fieldName] !== undefined) {
                                value = item[fieldName];
                              }

                              if (value !== undefined) {
                                row[fieldName] = value;
                              }
                            });

                            return row;
                          });
                        }

                        dispatch(updateScreenFormField({
                          screenId: targetScreenId,
                          name: bindValue,
                          value: mappedRows
                        }));
                      }
                    } catch (smError) {
                      console.error(`Failed to fetch submodule ${subModuleName}:`, smError);
                    }
                  }
                }
              }
            }
          } catch (error) {
            console.error(`Failed to load record ${recordId} for screen ${targetScreenId}:`, error);
          } finally {
            dispatch(setScreenLoading({ screenId: targetScreenId, isLoading: false }));
          }
        } else {
          // No recordId - this is add mode navigation
          // Apply Proforma Invoice pre-population if needed (after navigation)
          if (targetScreenId === "175" && estimateIdForProforma) {
            // Use setTimeout to ensure form data is set after navigation completes
            setTimeout(() => {
              (async () => {
                // 1) Merge parentid / parent_id into existing Proforma form data
                const currentFormData =
                  getState().forms.formDataByScreen[targetScreenId] || {};
                dispatch(
                  setScreenFormData({
                    screenId: targetScreenId,
                    data: {
                      ...currentFormData,
                      parentid: estimateIdForProforma,
                      parent_id: estimateIdForProforma,
                    },
                  })
                );

                // 2) Additionally, fetch the estimation record to pre-populate discount
                // This mirrors the Jobcard behaviour so Proforma always starts with
                // the same discount as the Estimate.
                try {
                  const { apiService } = await import("../../services/api");
                  const estimationResponse = await apiService.get(
                    `/pabws/utility/estimation_items`,
                    { id: estimateIdForProforma },
                    {
                      includeAuthToken: true,
                      authHeaderType: "Bearer",
                      errorHandling: "return-empty",
                      transformResponse: (raw: any) => {
                        if (!raw) return null;
                        if (raw.success && raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                          return raw.data[0];
                        }
                        if (raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                          return raw.data[0];
                        }
                        return raw;
                      },
                    }
                  );

                  if (
                    estimationResponse &&
                    estimationResponse.discount !== undefined &&
                    estimationResponse.discount !== null
                  ) {
                    const discountStr = String(estimationResponse.discount);
                    const latestFormData =
                      getState().forms.formDataByScreen[targetScreenId] || {};
                    dispatch(
                      setScreenFormData({
                        screenId: targetScreenId,
                        data: {
                          ...latestFormData,
                          txt_discount: discountStr,
                          discount: discountStr,
                        },
                      })
                    );
                  }
                } catch (error) {
                  if (import.meta.env.DEV) {
                    console.warn(
                      "[Proforma Navigation] Failed to pre-populate discount from estimation:",
                      { estimateIdForProforma, error }
                    );
                  }
                }
              })();
            }, 100);
          }
          dispatch(navigateToView({ view }));
        }
        return;
      }

      // Handle generic workflow actions from summary lists
      // Check if this is a workflow action by looking for workflow configuration in current screen
      const currentScreenConfig =
        getResolvedScreenConfig(currentView) ||
        getResolvedScreenConfig(deriveScreenId(currentView) || "");
      let workflowItem: any = null;

      if (currentScreenConfig && data && typeof data === "object") {
        walkComponentTree(currentScreenConfig, (component) => {
          if (component.componentType === ComponentType.SUMMARY_LIST) {
            const summaryList = component as any;
            if (summaryList.workflow && Array.isArray(summaryList.workflow)) {
              workflowItem = summaryList.workflow.find(
                (w: any) => w.Action === actionId
              );
              if (workflowItem) return true; // Stop searching
            }
          }
          return false;
        });
      }

      // If this is a workflow action, handle it generically
      if (workflowItem) {
        const cleanRowData = { ...(data || {}) } as Record<string, any>;

        // Extract screen ID from workflow Screen property (e.g., "modulev1/vehiclein_add" -> "107")
        let targetScreenId = "";
        if (workflowItem.Screen) {
          const screenPath = workflowItem.Screen;
          // Map common screen paths to screen IDs
          const screenPathMap: Record<string, string> = {
            "modulev1/vehiclein_add": "107",
            "modulev1/estimation_add": "116",
            "modulev1/estimateapproval": "150",
            "modulev1/jobcard_add": "156",
            "modulev1/qc_approve": "157",
            "modulev1/QualityCheckApproval": "qc_approval",
            "modulev1/invoice_add": "158",
            "modulev1/close_jobcard": "39",
          };
          targetScreenId = screenPathMap[screenPath] || deriveScreenId(screenPath) || "107"; // Try map, then dynamic, fallback to 107
        }

        if (!targetScreenId) {
          ensureSlidingTimeout(
            dispatch,
            {
              message: "Unable to determine target screen for workflow action.",
              type: "error",
            },
            5000
          );
          return;
        }

        const targetView = resolveView(targetScreenId);
        dispatch(setScreenLoading({ screenId: targetScreenId, isLoading: true }));

        // Get the record ID (inspection ID)
        const recordId = cleanRowData.id || cleanRowData.vehicle_in_id;

        // Process ActionArgs if present - replace placeholders like <id>, <jobcard_id>, etc.
        const processedActionArgs: Record<string, string> = {};
        if (workflowItem.ActionArgs) {
          Object.keys(workflowItem.ActionArgs).forEach((key) => {
            let value = workflowItem.ActionArgs[key];
            if (typeof value === "string") {
              // Replace <id> specifically (legacy support)
              if (recordId) {
                value = value.replace("<id>", recordId.toString());
              }
              // Replace any <field_name> with value from row data (e.g., <jobcard_id>)
              const placeholders = value.match(/<([^>]+)>/g);
              if (placeholders) {
                placeholders.forEach((placeholder: string) => {
                  const fieldName = placeholder.substring(
                    1,
                    placeholder.length - 1
                  );
                  if (
                    cleanRowData[fieldName] !== undefined &&
                    cleanRowData[fieldName] !== null
                  ) {
                    value = value.replace(
                      placeholder,
                      cleanRowData[fieldName].toString()
                    );
                  }
                });
              }
              processedActionArgs[key] = value;
            } else {
              processedActionArgs[key] = value;
            }
          });
        }

        // Check if child record already exists (for edit/view mode)
        // Priority: Check __workflowChildId (from completed state click), then Action_ref_column, then action-specific logic
        let childRecordId: string | number | null | undefined = null;

        // First, check if this is a workflow action with a child record ID passed from DataTable
        if (cleanRowData.__workflowChildId) {
          childRecordId = cleanRowData.__workflowChildId;
          if (import.meta.env.DEV) {
            console.log("[Workflow Navigation] Using child record ID from workflow click:", {
              actionId,
              childRecordId,
              source: "__workflowChildId",
            });
          }
        } else if (workflowItem.Action_ref_column) {
          // Check Action_ref_column (e.g., estimate_id, approved_id, etc.)
          const refColumnName = workflowItem.Action_ref_column;
          childRecordId =
            cleanRowData[refColumnName] ||
            cleanRowData[refColumnName.toLowerCase()] ||
            cleanRowData[refColumnName.toUpperCase()];

          if (import.meta.env.DEV && childRecordId) {
            console.log("[Workflow Navigation] Using child record ID from Action_ref_column:", {
              actionId,
              refColumnName,
              childRecordId,
            });
          }
        } else {
          // Fallback to action-specific logic (for backward compatibility)
          if (actionId === "Estimate") {
            childRecordId = cleanRowData.estimate_id || cleanRowData.estimation_id;
          }
        }

        let initialFormData: Record<string, any> = {};

        // CRITICAL: Only set edit mode if childRecordId exists AND it's from __workflowChildId
        // If childRecordId comes from Action_ref_column (like estimate_id), we need to verify
        // it's actually a valid record ID for the target screen before setting edit mode
        // For CREATE operations (like "Estimate" from inspection), childRecordId might be undefined
        // even if Action_ref_column exists (because no estimation record exists yet)
        const isEditModeFromWorkflow = !!(cleanRowData.__workflowChildId);
        const hasChildRecordId = !!(childRecordId && String(childRecordId).trim().length > 0);

        // Only set edit mode if we have a childRecordId AND it came from workflow (not Action_ref_column)
        // OR if Action_ref_column exists AND childRecordId exists (meaning record already exists)
        // For CREATE operations, childRecordId will be undefined/null even if Action_ref_column exists
        if (hasChildRecordId && (isEditModeFromWorkflow || !!workflowItem.Action_ref_column)) {
          // For edit/view mode, include the child record ID
          // Initially set mode to "view" (user can switch to "edit" later)
          initialFormData = {
            ...buildFormDataFromRecord(null, {
              fallback: cleanRowData,
              screenId: targetScreenId,
            }),
            id: childRecordId, // Set the child record ID for edit mode
            // Carry workflow fields for submit (edit/view)
            next_action: workflowItem.Action || actionId,
            next_state: (workflowItem.States && workflowItem.States[0]) || "",
          };

          // Set edit mode in Redux store ONLY if childRecordId exists
          // This means we're editing an existing record
          if (childRecordId) {
            dispatch(
              setScreenEditMode({
                screenId: targetScreenId,
                isEditMode: true,
                recordId: String(childRecordId),
              })
            );
          }

          if (import.meta.env.DEV) {
            console.log("[Workflow Navigation] Setting edit mode:", {
              screenId: targetScreenId,
              recordId: childRecordId,
              actionId,
              source: isEditModeFromWorkflow ? "__workflowChildId" : "Action_ref_column",
            });
          }
        } else {
          // For add mode, set parent_id from ActionArgs
          initialFormData = {
            ...buildFormDataFromRecord(null, {
              fallback: cleanRowData,
              screenId: targetScreenId,
            }),
            // Carry workflow fields for submit (add)
            next_action: workflowItem.Action || actionId,
            next_state: (workflowItem.States && workflowItem.States[0]) || "",
          };

          // Set parent_id from processed ActionArgs
          if (processedActionArgs.parentid) {
            initialFormData.parentid = processedActionArgs.parentid;
            initialFormData.parent_id = processedActionArgs.parentid;
          }

          // Job Card add (156): use inspection id as parent id (not approved_id / estimation id).
          const isJobCardAdd =
            (targetScreenId === "156" || (workflowItem.Screen && String(workflowItem.Screen).includes("jobcard_add"))) &&
            !childRecordId;
          if (isJobCardAdd && recordId) {
            const inspectionId = recordId; // cleanRowData.id || cleanRowData.vehicle_in_id
            initialFormData.parentid = inspectionId;
            initialFormData.parent_id = inspectionId;
            if (import.meta.env.DEV) {
              console.log("[Job Card add] Set parentid to inspection id:", { inspectionId });
            }
          }
        }

        // For Estimate action in add mode, fetch inspection record BEFORE setting form data
        // This ensures the lookup item is available when ComponentRenderer renders
        if (actionId === "Estimate" && !childRecordId && processedActionArgs.parentid) {
          try {
            const { apiService } = await import("../../services/api");

            // Fetch the specific inspection record by ID (like Blazor's GetLookupItem)
            const inspectionResponse = await apiService.get(
              `/pabws/utility/inspection_items`,
              { id: processedActionArgs.parentid },
              {
                includeAuthToken: true,
                authHeaderType: "Bearer",
                errorHandling: "return-empty",
                transformResponse: (raw: any) => {
                  if (!raw) return null;
                  if (raw.success && raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                    return raw.data[0]; // Return the first item
                  }
                  if (raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                    return raw.data[0];
                  }
                  return raw;
                },
              }
            );

            if (inspectionResponse) {
              // CRITICAL: Add the inspection record to lookup options FIRST (before setting form data)
              // This ensures SearchableDropdown can find it when it renders
              // Add to lookups (as LookupOption) for dropdown display
              dispatch(
                addLookupItem({
                  lookupKey: "inspection_items",
                  item: {
                    ...inspectionResponse,
                    label: inspectionResponse.name || inspectionResponse.id,
                    value: inspectionResponse.id,
                  },
                })
              );

              // Also add to rawData for full record access
              dispatch(
                addLookupItem({
                  lookupKey: "inspection_items",
                  item: inspectionResponse,
                  isRawData: true,
                })
              );

              // Store the full inspection record in formData for BindParent to access
              // This mimics Blazor's args.SelectedJson.Data
              initialFormData.parentid_selectedData = inspectionResponse;

              // Also set customer_remarks directly if available (for BindParent)
              if (inspectionResponse.customer_remarks !== undefined) {
                initialFormData.customer_remarks = inspectionResponse.customer_remarks;
              }

              if (import.meta.env.DEV) {
                console.log(
                  "[Estimate Action] Fetched and added inspection record BEFORE form data:",
                  {
                    parentid: processedActionArgs.parentid,
                    inspectionRecord: {
                      id: inspectionResponse.id,
                      name: inspectionResponse.name,
                      label: inspectionResponse.name || inspectionResponse.id,
                      value: inspectionResponse.id,
                      customer_remarks: inspectionResponse.customer_remarks,
                    },
                    addedToLookups: true,
                    addedToRawData: true,
                  }
                );
              }
            } else {
              if (import.meta.env.DEV) {
                console.warn(
                  "[Estimate Action] Inspection record not found:",
                  {
                    parentid: processedActionArgs.parentid,
                  }
                );
              }
            }
          } catch (lookupError) {
            console.error(
              "[Estimate Action] Failed to fetch inspection record for BindParent:",
              lookupError
            );
            // Continue without the inspection record - BindParent will work when user selects
          }
        }

        // For Jobcard action in add mode, fetch estimation record BEFORE setting form data
        const jobCardEstimationId =
          (actionId === "Jobcard" || actionId === "Job card" || targetScreenId === "156") && !childRecordId
            ? (initialFormData.parentid || initialFormData.parent_id || processedActionArgs.parentid)
            : null;
        if (jobCardEstimationId) {
          try {
            const { apiService } = await import("../../services/api");

            // Fetch the specific estimation record by ID (like Blazor's GetLookupItem)
            const estimationResponse = await apiService.get(
              `/pabws/utility/estimation_items`,
              { id: jobCardEstimationId },
              {
                includeAuthToken: true,
                authHeaderType: "Bearer",
                errorHandling: "return-empty",
                transformResponse: (raw: any) => {
                  if (!raw) return null;
                  if (raw.success && raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                    return raw.data[0]; // Return the first item
                  }
                  if (raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                    return raw.data[0];
                  }
                  return raw;
                },
              }
            );

            if (estimationResponse) {
              // CRITICAL: Add the estimation record to lookup options FIRST (before setting form data)
              // This ensures SearchableDropdown can find it when it renders
              dispatch(
                addLookupItem({
                  lookupKey: "estimation_items",
                  item: {
                    ...estimationResponse,
                    label: estimationResponse.name || estimationResponse.id,
                    value: estimationResponse.id,
                  },
                })
              );

              // Also add to rawData for full record access
              dispatch(
                addLookupItem({
                  lookupKey: "estimation_items",
                  item: estimationResponse,
                  isRawData: true,
                })
              );

              // Store the full estimation record in formData for BindParent to access
              initialFormData.parentid_selectedData = estimationResponse;

              // CRITICAL: Directly populate BindParent fields from estimation response
              // This ensures fields like discount are pre-populated without waiting for BindParent useEffect
              // Note: Convert to string to match expected format for form submission
              if (estimationResponse.discount !== undefined && estimationResponse.discount !== null) {
                const discountStr = String(estimationResponse.discount);
                initialFormData.txt_discount = discountStr;
                initialFormData.discount = discountStr;
              }

              if (import.meta.env.DEV) {
                console.log(
                  "[Jobcard Action] Fetched and added estimation record BEFORE form data:",
                  {
                    parentid: jobCardEstimationId,
                    estimationRecord: {
                      id: estimationResponse.id,
                      name: estimationResponse.name,
                      label: estimationResponse.name || estimationResponse.id,
                      value: estimationResponse.id,
                      discount: estimationResponse.discount,
                    },
                    addedToLookups: true,
                    addedToRawData: true,
                    discountPopulated: estimationResponse.discount,
                  }
                );
              }
            } else {
              if (import.meta.env.DEV) {
                console.warn(
                  "[Jobcard Action] Estimation record not found:",
                  jobCardEstimationId
                );
              }
            }
          } catch (lookupError) {
            console.error(
              "[Jobcard Action] Failed to fetch estimation record:",
              lookupError
            );
            // Continue without the estimation record - SubModules will fetch when parentid is available
          }
        }

        // For Approve action in add mode, fetch estimation record BEFORE setting form data
        // This ensures the lookup item is available when ComponentRenderer renders
        if (actionId === "Approve" && !childRecordId) {
          // Get estimation ID from row data (estimate_id) or ActionArgs
          const estimationId = cleanRowData.estimate_id || cleanRowData.estimation_id || processedActionArgs.parentid;

          if (estimationId) {
            try {
              const { apiService } = await import("../../services/api");

              // Fetch the specific estimation record by ID (like Blazor's GetLookupItem)
              const estimationResponse = await apiService.get(
                `/pabws/utility/estimation_items`,
                { id: estimationId },
                {
                  includeAuthToken: true,
                  authHeaderType: "Bearer",
                  errorHandling: "return-empty",
                  transformResponse: (raw: any) => {
                    if (!raw) return null;
                    if (raw.success && raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                      return raw.data[0]; // Return the first item
                    }
                    if (raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                      return raw.data[0];
                    }
                    return raw;
                  },
                }
              );

              if (estimationResponse) {
                // CRITICAL: Add the estimation record to lookup options FIRST (before setting form data)
                // This ensures SearchableDropdown can find it when it renders
                // Add to lookups (as LookupOption) for dropdown display
                dispatch(
                  addLookupItem({
                    lookupKey: "estimation_items",
                    item: {
                      ...estimationResponse,
                      label: estimationResponse.name || estimationResponse.est_no || estimationResponse.id,
                      value: estimationResponse.id,
                    },
                  })
                );

                // Also add to rawData for full record access
                dispatch(
                  addLookupItem({
                    lookupKey: "estimation_items",
                    item: estimationResponse,
                    isRawData: true,
                  })
                );

                // IMPORTANT: Also fetch the full lookup to ensure dropdown has all options
                // This ensures the dropdown works properly even if the single item wasn't enough
                const { fetchLookup } = await import("../slices/lookupsSlice");
                dispatch(
                  fetchLookup({
                    lookupName: "estimation_items",
                    params: {
                      parentid: "",
                      lookuptype: "Select",
                    },
                  })
                );

                // Store the full estimation record in formData for BindParent to access
                // This mimics Blazor's args.SelectedJson.Data
                initialFormData.parentid_selectedData = estimationResponse;

                // Set parent_id from estimation ID
                initialFormData.parentid = estimationId;
                initialFormData.parent_id = estimationId;

                if (import.meta.env.DEV) {
                  console.log(
                    "[Approve Action] Fetched and added estimation record BEFORE form data:",
                    {
                      parentid: estimationId,
                      estimationRecord: {
                        id: estimationResponse.id,
                        name: estimationResponse.name,
                        est_no: estimationResponse.est_no,
                        label: estimationResponse.name || estimationResponse.est_no || estimationResponse.id,
                        value: estimationResponse.id,
                      },
                      addedToLookups: true,
                      addedToRawData: true,
                    }
                  );
                }
              } else {
                if (import.meta.env.DEV) {
                  console.warn(
                    "[Approve Action] Estimation record not found:",
                    {
                      parentid: estimationId,
                    }
                  );
                }
              }
            } catch (lookupError) {
              console.error(
                "[Approve Action] Failed to fetch estimation record for BindParent:",
                lookupError
              );
              // Continue without the estimation record - BindParent will work when user selects
            }
          }
        }

        // For Create invoice action in add mode, fetch jobcard record BEFORE setting form data
        // This ensures the lookup item is available when ComponentRenderer renders
        if (actionId === "Create invoice" && !childRecordId) {
          // Priority: processedActionArgs.parentid (if regex replacement worked)
          // If it's still a placeholder (contains <), try to find ID in row data directly
          // Fallback: cleanRowData.jobcard_id (direct Jobcard ID)
          // Final Fallback: cleanRowData.id (Inspection ID)
          let parentid = processedActionArgs.parentid;

          if (!parentid || (typeof parentid === 'string' && parentid.includes('<'))) {
            parentid = cleanRowData.jobcard_id || cleanRowData.jobcardid || cleanRowData.job_card_id;
          }

          if (!parentid) {
            parentid = cleanRowData.id || cleanRowData.vehicle_in_id;
          }

          if (parentid) {
            try {
              const { apiService } = await import("../../services/api");

              // Fetch the specific jobcard record for the invoice (like Blazor's GetLookupItem)
              const jobcardResponse = await apiService.get(
                `/pabws/utility/invoice_jobcard`,
                { id: parentid },
                {
                  includeAuthToken: true,
                  authHeaderType: "Bearer",
                  errorHandling: "return-empty",
                  transformResponse: (raw: any) => {
                    if (!raw) return null;
                    if (
                      raw.success &&
                      raw.data &&
                      Array.isArray(raw.data) &&
                      raw.data.length > 0
                    ) {
                      return raw.data[0]; // Return the first item
                    }
                    if (raw.data && Array.isArray(raw.data) && raw.data.length > 0) {
                      return raw.data[0];
                    }
                    return raw;
                  },
                }
              );

              if (jobcardResponse && jobcardResponse.id) {
                // Add to lookups for dropdown display
                dispatch(
                  addLookupItem({
                    lookupKey: "invoice_jobcard",
                    item: {
                      ...jobcardResponse,
                      label:
                        jobcardResponse.name ||
                        jobcardResponse.jobcard_no ||
                        jobcardResponse.id,
                      value: jobcardResponse.id,
                    },
                  })
                );

                // Add to rawData for full record access
                dispatch(
                  addLookupItem({
                    lookupKey: "invoice_jobcard",
                    item: jobcardResponse,
                    isRawData: true,
                  })
                );

                // Store the full record in formData for BindParent
                initialFormData.parentid_selectedData = jobcardResponse;

                // CRITICAL FIX: Use the actual Jobcard ID from the response for the form field.
                // If we looked up using Inspection ID (fallback), 'parentid' var is Inspection ID.
                // But the dropdown expects Jobcard ID (which is 'jobcardResponse.id').
                const actualJobcardId = jobcardResponse.id;
                initialFormData.parentid = actualJobcardId;
                initialFormData.parent_id = actualJobcardId;

                // Pre-populate customer type, customer name, and invoice date from jobcard
                const customerTypeValue =
                  jobcardResponse.customertype ??
                  jobcardResponse.customer_type ??
                  jobcardResponse.customer_type_id;
                if (customerTypeValue !== undefined && customerTypeValue !== null) {
                  initialFormData.s_select_customertype = customerTypeValue;
                  initialFormData.customertype = customerTypeValue;
                }
                const insuranceNameValue = jobcardResponse.insurance_name ?? jobcardResponse.insurance_id;
                if (insuranceNameValue !== undefined && insuranceNameValue !== null) {
                  initialFormData.s_select_insurance_customertype = insuranceNameValue;
                  initialFormData.insurance_name = insuranceNameValue;
                  // FIX: insurance_customer is a dependent lookup (BindParent: parentid) - must fetch with parentid
                  // Same as customer_type; params: {} was wrong and caused dropdown to have no options
                  dispatch(
                    fetchLookup({
                      lookupName: "insurance_customer",
                      params: {
                        parentid: actualJobcardId,
                        lookuptype: "Select",
                      },
                    })
                  );
                }
                const invoiceDateValue = jobcardResponse.invoice_date ?? jobcardResponse.job_date ?? jobcardResponse.date;
                if (invoiceDateValue !== undefined && invoiceDateValue !== null) {
                  const dateStr = typeof invoiceDateValue === "string" ? invoiceDateValue : String(invoiceDateValue);
                  initialFormData.txt_jobdate = dateStr;
                  initialFormData.invoice_date = dateStr;
                }

                // Always log for debugging prepopulation
                console.log("[Create Invoice] Jobcard prepopulation check:", {
                  jobcardId: actualJobcardId,
                  jobcardKeys: Object.keys(jobcardResponse),
                  customertype: {
                    customertype: jobcardResponse.customertype,
                    customer_type: jobcardResponse.customer_type,
                    customer_type_id: jobcardResponse.customer_type_id,
                    found: customerTypeValue,
                    set: initialFormData.s_select_customertype,
                  },
                  insurance_name: {
                    insurance_name: jobcardResponse.insurance_name,
                    insurance_id: jobcardResponse.insurance_id,
                    found: insuranceNameValue,
                    set: initialFormData.s_select_insurance_customertype,
                    lookupFetched: true,
                  },
                  invoice_date: {
                    invoice_date: jobcardResponse.invoice_date,
                    job_date: jobcardResponse.job_date,
                    date: jobcardResponse.date,
                    found: invoiceDateValue,
                    set: initialFormData.txt_jobdate,
                  },
                });
              }
            } catch (error) {
              console.error(
                "[Create Invoice Action] Failed to fetch jobcard record:",
                error
              );
            }
          }
        }

        // Invoice create (158): when jobcard has no insurance fields, use customername from row
        if (targetScreenId === "158" && !childRecordId) {
          const currentCustomerName =
            initialFormData.s_select_insurance_customertype ??
            initialFormData.insurance_name;
          const rowCustomerName =
            cleanRowData.customername ?? cleanRowData.customer_name;
          if (
            (currentCustomerName === undefined ||
              currentCustomerName === null ||
              currentCustomerName === "") &&
            rowCustomerName !== undefined &&
            rowCustomerName !== null &&
            rowCustomerName !== ""
          ) {
            initialFormData.s_select_insurance_customertype = rowCustomerName;
            initialFormData.insurance_name = rowCustomerName;
            // Fetch insurance_customer with parentid so dropdown can display (if we have parentid)
            const parentidForLookup =
              initialFormData.parentid ?? initialFormData.parent_id;
            if (parentidForLookup) {
              dispatch(
                fetchLookup({
                  lookupName: "insurance_customer",
                  params: {
                    parentid: String(parentidForLookup),
                    lookuptype: "Select",
                  },
                })
              );
            }
          }
        }

        // Initially populate with row data and ActionArgs
        dispatch(
          setScreenFormData({
            screenId: targetScreenId,
            data: initialFormData,
          })
        );

        // Job Card add: put inspection id in URL so SubModule/Report use same parent id
        const viewToUse =
          (targetScreenId === "156" || (workflowItem.Screen && String(workflowItem.Screen).includes("jobcard_add"))) &&
            !childRecordId &&
            (initialFormData.parentid || initialFormData.parent_id)
            ? `${targetView}/${String(initialFormData.parentid || initialFormData.parent_id)}`
            : targetView;
        dispatch(navigateToView({ view: viewToUse }));

        try {
          let shouldFetchRecord = false;
          let fetchEndpoint = "";
          let queryParams: Record<string, any> | undefined = undefined;

          // For Inspect action, use the specific API endpoint with record ID
          if (actionId === "Inspect") {
            fetchEndpoint = "/pabws/module/vehicle_in/" + recordId;
            shouldFetchRecord = true;
            // Pass the record ID as query parameter if available
            // if (recordId) {
            //   queryParams = { id: recordId };
            // }
          } else if (actionId === "Estimate") {
            // For Estimate action, check if we're in edit mode or add mode
            if (childRecordId && childRecordId.toString().length > 0) {
              // Edit/view mode: fetch existing estimation record via MODULE API (like Inspect)
              // This ensures we get the full estimation record (including discount, totals, etc.)
              const estimationId = childRecordId.toString();
              const modulePath = workflowItem.Screen
                ? workflowItem.Screen.replace("modulev1/", "")
                : "estimation_add";
              fetchEndpoint = `/pabws/module/${modulePath}/${estimationId}`;
              shouldFetchRecord = true;
              // ID is already part of the URL, no queryParams needed
            } else {
              // Add mode: fetch with parent_id to load parent data using SCREEN API
              fetchEndpoint = "/pabws/screen/estimation_add";
              shouldFetchRecord = true;
              // Pass parent_id as query parameter to load parent inspection data
              if (processedActionArgs.parentid) {
                queryParams = { parentid: processedActionArgs.parentid };
              }
            }
          } else if (actionId === "Approve") {
            // For Approve action, check if we're in edit mode or add mode
            if (childRecordId && childRecordId.toString().length > 0) {
              // Edit/view mode: fetch existing approval record via MODULE API
              // CRITICAL: Use only the approval record ID (service_est_approval PK), NOT the inspection/parent id.
              // Do NOT use cleanRowData.id - in inspection summary rows that is the vehicle_in/inspection id.
              const approvalId =
                cleanRowData.service_est_approval_id ??
                cleanRowData.approved_id ??
                cleanRowData.approval_id ??
                cleanRowData.__workflowChildId ??
                childRecordId;
              const approvalIdStr = String(approvalId).trim();
              if (approvalIdStr) {
                fetchEndpoint = `/pabws/module/approval_add/${approvalIdStr}`;
                shouldFetchRecord = true;
              }
              // ID is part of the URL; no query params needed
            } else {
              // Add mode: fetch with parent_id to load parent estimation data via SCREEN API
              fetchEndpoint = "/pabws/screen/approval_add";
              shouldFetchRecord = true;
              // Pass parent_id as query parameter to load parent estimation data
              // parent_id should be the estimation_id from the row data
              const estimationId = cleanRowData.estimate_id || cleanRowData.estimation_id;
              if (estimationId) {
                queryParams = { parentid: estimationId };
              } else if (processedActionArgs.parentid) {
                queryParams = { parentid: processedActionArgs.parentid };
              }
            }
          } else if (actionId === "QC Approve" && childRecordId && childRecordId.toString().length > 0) {
            // Quality Check Approval edit: use /module/qc_approval_add/ (no pabws - backend expects api/module/qc_approval_add/{id})
            const qcApprovalId =
              cleanRowData.service_qc_approval_id ??
              cleanRowData.qc_approved_id ??
              cleanRowData.__workflowChildId ??
              childRecordId;
            const qcApprovalIdStr = String(qcApprovalId).trim();
            if (qcApprovalIdStr) {
              fetchEndpoint = `/pabws/module/qc_approval_add/${qcApprovalIdStr}`;
              shouldFetchRecord = true;
            }
          } else if (childRecordId && childRecordId.toString().length > 0 && workflowItem.Screen) {
            // Generic workflow action with child record ID - fetch the child record
            let modulePath = workflowItem.Screen.replace("modulev1/", "");
            // QC Approval: backend expects /module/qc_approval_add/{id} (not /pabws/module/qc_approve)
            if (modulePath === "qc_approve") {
              fetchEndpoint = `/pabws/module/qc_approval_add/${childRecordId}`;
            } else {
              fetchEndpoint = `/pabws/module/${modulePath}/${childRecordId}`;
            }
            shouldFetchRecord = true;
            // queryParams = { id: childRecordId }; // ID is already in the URL

            if (import.meta.env.DEV) {
              console.log("[Workflow Navigation] Generic workflow - Edit/View mode:", {
                actionId,
                childRecordId,
                modulePath,
                endpoint: fetchEndpoint,
                queryParams,
              });
            }
          } else if (
            (actionId === "Jobcard" || actionId === "Job card" || targetScreenId === "156") &&
            !childRecordId &&
            workflowItem.Screen &&
            (initialFormData.parentid || initialFormData.parent_id)
          ) {
            // Job Card add: main fetch uses inspection id (parentid)
            const jobCardAddId = String(initialFormData.parentid || initialFormData.parent_id);
            const modulePath = workflowItem.Screen.replace("modulev1/", "");
            fetchEndpoint = `/pabws/module/${modulePath}/${jobCardAddId}`;
            shouldFetchRecord = true;
            queryParams = { id: jobCardAddId };
            if (import.meta.env.DEV) {
              console.log("[Workflow Navigation] Job card add - main fetch with inspection id:", { jobCardAddId, fetchEndpoint });
            }
          } else if (recordId && workflowItem.Screen) {
            // For other workflow actions without child record, try to fetch the parent record using the module path
            const modulePath = workflowItem.Screen.replace("modulev1/", "");
            fetchEndpoint = `/pabws/module/${modulePath}/${recordId}`;
            shouldFetchRecord = true;
          }

          if (shouldFetchRecord && fetchEndpoint) {
            if (import.meta.env.DEV) {
              console.log("[Edit Mode] Fetching record data:", {
                endpoint: fetchEndpoint,
                queryParams,
                screenId: targetScreenId,
                actionId,
                childRecordId,
                isEditMode: !!childRecordId,
              });
            }

            const response = await apiService.get(fetchEndpoint, queryParams, {
              includeAuthToken: true,
              authHeaderType: "Bearer",
              errorHandling: "throw",
              transformResponse: (raw: any) => {
                if (!raw) return null;

                // CRITICAL: Handle encrypted response (like Inspect action)
                if (raw.data && typeof raw.data === 'string') {
                  if (import.meta.env.DEV) {
                    console.log("[HandleEdit] 🔐 Decryption Check:", { endpoint: fetchEndpoint, rawLength: raw.data.length });
                  }
                  const decrypted = authService.decryptData(raw.data);
                  if (decrypted) {
                    if (import.meta.env.DEV) {
                      console.log("[HandleEdit] Decrypted successfully");
                    }
                    raw.data = decrypted;
                  }
                }

                // Handle Blazor-style nested structure: { data: { data: { ... } } }
                // This matches Blazor's: ItemData = moduleData?["data"]?["data"]?.AsObject();
                if (raw.data) {
                  // Check for nested data.data structure (Blazor format)
                  if (raw.data.data && typeof raw.data.data === "object" && !Array.isArray(raw.data.data)) {
                    if (import.meta.env.DEV) {
                      console.log("[Edit Mode] Found nested data.data structure (Blazor format)");
                    }
                    return raw.data.data;
                  }

                  // Handle array response
                  if (Array.isArray(raw.data)) {
                    return raw.data[0] || raw.data;
                  }

                  // Handle direct data object
                  return raw.data;
                }

                // Handle array response
                if (Array.isArray(raw)) {
                  return raw[0];
                }

                // Handle direct object response
                return raw;
              },
            });

            if (import.meta.env.DEV) {
              console.log("[Edit Mode] ===== API Response Structure =====");
              console.log("[Edit Mode] Endpoint:", fetchEndpoint);
              console.log("[Edit Mode] Query Params:", queryParams);
              console.log("[Edit Mode] Response Type:", typeof response);
              console.log("[Edit Mode] Response Keys:", Object.keys(response || {}));
              console.log("[Edit Mode] Response Values Sample:", response ? Object.keys(response).slice(0, 20).reduce((acc, key) => {
                const value = response[key];
                if (Array.isArray(value)) {
                  acc[key] = `[Array(${value.length})]`;
                } else if (typeof value === 'object' && value !== null) {
                  acc[key] = `[Object with keys: ${Object.keys(value).slice(0, 5).join(', ')}]`;
                } else {
                  acc[key] = String(value).substring(0, 100);
                }
                return acc;
              }, {} as Record<string, any>) : null);
              console.log("[Edit Mode] Full Response:", response);
              console.log("[Edit Mode] ====================================");
            }

            // Update form data with API response, preserving filled data
            // Get current form data from Redux store to preserve these values (initialFormData)
            const currentStoreFormData = getState().forms.formDataByScreen[targetScreenId] || {};

            const updatedFormData = buildFormDataFromRecord(response, {
              fallback: { ...currentStoreFormData, ...cleanRowData },
              screenId: targetScreenId,
            });

            // Job Card add: keep inspection id as parentid after merge (do not use response/cleanRowData id)
            if (
              targetScreenId === "156" &&
              !childRecordId &&
              (initialFormData.parentid || initialFormData.parent_id)
            ) {
              const jobCardAddId = initialFormData.parentid || initialFormData.parent_id;
              updatedFormData.parentid = jobCardAddId;
              updatedFormData.parent_id = jobCardAddId;
              if (import.meta.env.DEV) {
                console.log("[Workflow Navigation] Job card add - preserved parentid after merge:", jobCardAddId);
              }
            }

            // Invoice (158): ensure customer type and customer name are set for dropdowns from loaded invoice record
            if (response && targetScreenId === "158") {
              const custType = response.customertype ?? response.customer_type ?? response.customer_type_id;
              if (custType !== undefined && custType !== null) {
                updatedFormData.s_select_customertype = custType;
                updatedFormData.customertype = custType;
              }
              const insName = response.insurance_name ?? response.insurance_id;
              if (insName !== undefined && insName !== null) {
                updatedFormData.s_select_insurance_customertype = insName;
                updatedFormData.insurance_name = insName;
              }
              // Debug logging for edit mode prepopulation
              console.log("[Invoice Edit] Customer prepopulation check:", {
                responseKeys: Object.keys(response),
                customertype: {
                  customertype: response.customertype,
                  customer_type: response.customer_type,
                  customer_type_id: response.customer_type_id,
                  found: custType,
                  set: updatedFormData.s_select_customertype,
                },
                insurance_name: {
                  insurance_name: response.insurance_name,
                  insurance_id: response.insurance_id,
                  found: insName,
                  set: updatedFormData.s_select_insurance_customertype,
                },
              });
            }

            // CRITICAL: Ensure approve_status is explicitly included if present in response (Estimate Approval screen 150)
            // This matches Blazor behavior where approve_status is directly mapped from ItemData
            // Also accept next_state / status from module API response
            const approveStatusRaw =
              response?.approve_status ??
              response?.data?.approve_status ??
              response?.next_state ??
              response?.Next_State ??
              response?.status ??
              response?.Status;
            if (response && (approveStatusRaw !== undefined && approveStatusRaw !== null && approveStatusRaw !== "")) {
              const approveStatusValue = approveStatusRaw;
              if (approveStatusValue !== undefined && approveStatusValue !== null && approveStatusValue !== "") {
                updatedFormData.approve_status = approveStatusValue;

                if (import.meta.env.DEV) {
                  console.log(`[Workflow Navigation] ✓ Explicitly set approve_status from API response:`, {
                    approveStatusValue,
                    fromResponse: response.approve_status,
                    fromNestedData: response.data?.approve_status,
                    finalFormDataValue: updatedFormData.approve_status,
                    actionId,
                    screenId: targetScreenId,
                  });
                }

                // CRITICAL: Pre-fetch workflow options when approve_status has a value (edit mode)
                // This ensures options are available when ComponentRenderer renders the dropdown
                if (actionId === "Approve" || targetScreenId === "150") {
                  try {
                    const { apiService } = await import("../../services/api");
                    const workflow = await apiService.getWorkflow("service");
                    if (workflow && workflow.States && Array.isArray(workflow.States)) {
                      const workflowState = workflow.States.find(
                        (state: any) => state.Action === "Approve"
                      );
                      if (workflowState && workflowState.States && Array.isArray(workflowState.States)) {
                        const workflowOptions = workflowState.States.map((state: string) => ({
                          id: state,
                          name: state,
                          value: state,
                          label: state,
                        }));
                        const workflowLookupKey = `workflow_service_Approve`;
                        // Store in lookupsState using addLookupItem for each option
                        workflowOptions.forEach((option: any) => {
                          dispatch(
                            addLookupItem({
                              lookupKey: workflowLookupKey,
                              item: option,
                            })
                          );
                        });

                        if (import.meta.env.DEV) {
                          console.log(`[Workflow Navigation] ✓ Pre-fetched workflow options for approve_status:`, {
                            workflowLookupKey,
                            optionsCount: workflowOptions.length,
                            options: workflowOptions.map((opt: any) => ({ value: opt.value, label: opt.label })),
                            approveStatusValue,
                            valueMatches: workflowOptions.some((opt: any) =>
                              String(opt.value).toLowerCase() === String(approveStatusValue).toLowerCase()
                            ),
                          });
                        }
                      }
                    }
                  } catch (workflowError) {
                    console.error(`[Workflow Navigation] Failed to pre-fetch workflow options:`, workflowError);
                    // Continue - ComponentRenderer will fetch them later
                  }
                }
              }
            }

            if (import.meta.env.DEV) {
              console.log("[Edit Mode] Form data mapped from API response:", {
                mappedKeys: Object.keys(updatedFormData),
                mappedSample: Object.keys(updatedFormData).slice(0, 15).reduce((acc, key) => {
                  const value = updatedFormData[key];
                  acc[key] = Array.isArray(value)
                    ? `[Array(${value.length})]`
                    : typeof value === 'object'
                      ? '[Object]'
                      : value;
                  return acc;
                }, {} as Record<string, any>),
              });

              // Special logging for estimation fields
              if (actionId === "Estimate" || targetScreenId === "116") {
                console.log("[Edit Mode] ===== Estimation Fields Check =====");
                console.log("[Edit Mode] txt_estimate_number (est_no):", {
                  inFormData: updatedFormData.txt_estimate_number,
                  inResponse: response?.est_no,
                  mapped: !!updatedFormData.txt_estimate_number,
                });
                console.log("[Edit Mode] dt_est_date (est_date):", {
                  inFormData: updatedFormData.dt_est_date,
                  inResponse: response?.est_date,
                  mapped: !!updatedFormData.dt_est_date,
                });
                console.log("[Edit Mode] ====================================");
              }
            }

            // CRITICAL: Only set edit mode if we actually fetched a record with an ID
            // If childRecordId exists, we're in edit mode (record already exists)
            // If childRecordId doesn't exist but we fetched data, check if response has an ID
            const responseHasId = response && response.id && String(response.id).trim().length > 0;
            const shouldBeEditMode = !!(childRecordId && childRecordId.toString().length > 0) || responseHasId;

            if (shouldBeEditMode) {
              const actualRecordId = childRecordId || response?.id;
              updatedFormData.id = actualRecordId;

              // Set edit mode in Redux store if not already set
              const editModeInfo = getState().forms.editModeByScreen[targetScreenId];
              if (!editModeInfo?.isEditMode) {
                dispatch(
                  setScreenEditMode({
                    screenId: targetScreenId,
                    isEditMode: true,
                    recordId: actualRecordId?.toString() || "",
                  })
                );
              }

              if (import.meta.env.DEV) {
                console.log("[Workflow Navigation] Loaded child record in edit/view mode:", {
                  screenId: targetScreenId,
                  childRecordId,
                  responseId: response?.id,
                  actualRecordId,
                  actionId,
                  recordData: response,
                });
              }
            } else {
              // CREATE mode: ensure edit mode is NOT set
              const editModeInfo = getState().forms.editModeByScreen[targetScreenId];
              if (editModeInfo?.isEditMode) {
                dispatch(
                  setScreenEditMode({
                    screenId: targetScreenId,
                    isEditMode: false,
                    recordId: "",
                  })
                );
              }

              if (import.meta.env.DEV) {
                console.log("[Workflow Navigation] CREATE mode - edit mode cleared:", {
                  screenId: targetScreenId,
                  actionId,
                  childRecordId,
                  responseHasId,
                });
              }
            }

            // Ensure parent_id is set from ActionArgs in add mode
            if (actionId === "Estimate" && !childRecordId && processedActionArgs.parentid) {
              updatedFormData.parentid = processedActionArgs.parentid;
              updatedFormData.parent_id = processedActionArgs.parentid;
            }

            // Job Card add: ensure parentid stays inspection id after all merges
            if (
              (actionId === "Jobcard" || actionId === "Job card" || targetScreenId === "156") &&
              !childRecordId &&
              (initialFormData.parentid || initialFormData.parent_id)
            ) {
              const jobCardAddId = initialFormData.parentid || initialFormData.parent_id;
              updatedFormData.parentid = jobCardAddId;
              updatedFormData.parent_id = jobCardAddId;
            }

            // CRITICAL: In edit mode, handle Search_Select fields with _id and _id_desc pattern
            // This ensures SearchableDropdown, BindParent, and Report components work correctly
            if (childRecordId) {
              // Get screen config to find Search_Select fields
              const screenConfig =
                getResolvedScreenConfig(targetScreenId) ||
                getResolvedScreenConfig(targetView) ||
                getResolvedScreenConfig(deriveScreenId(targetScreenId) || "") ||
                null;

              if (screenConfig) {
                // Helper function to find all SearchableDropdown fields in config
                const findSearchableDropdownFields = (components: any[]): any[] => {
                  const fields: any[] = [];
                  const walk = (comps: any[]) => {
                    comps.forEach((comp: any) => {
                      if (comp.componentType === ComponentType.SEARCHABLE_DROPDOWN) {
                        fields.push(comp);
                      }
                      if (comp.Fields && Array.isArray(comp.Fields)) {
                        walk(comp.Fields);
                      }
                      if (comp.childComponents && Array.isArray(comp.childComponents)) {
                        walk(comp.childComponents);
                      }
                      if (comp.childControls && Array.isArray(comp.childControls)) {
                        walk(comp.childControls);
                      }
                    });
                  };
                  walk(components);
                  return fields;
                };

                const searchableFields = findSearchableDropdownFields(screenConfig);

                // Process each SearchableDropdown field
                for (const field of searchableFields) {
                  const fieldName = field.name || field.BindValue;
                  const jsonAttr = field.Json_Attribute || field.json_Attribute;
                  const lookupName = field.LookupName || field.lookupName;

                  if (!fieldName || !jsonAttr || !lookupName) continue;

                  if (import.meta.env.DEV) {
                    console.log("[Edit Mode] Processing Search_Select field:", {
                      fieldName,
                      jsonAttr,
                      lookupName,
                      componentType: field.componentType,
                    });
                  }

                  // Check if we have both _id and _id_desc in the response
                  // Check original response first, then updatedFormData (which may have mapped fields)
                  // Special handling: For parent_id field, also check inspection_id (common in estimation forms)
                  let idValue = response?.[jsonAttr] || response?.[fieldName] ||
                    updatedFormData[jsonAttr] || updatedFormData[fieldName];

                  // Special case: If looking for parent_id and not found, try inspection_id
                  if ((!idValue || idValue === undefined || idValue === null || idValue === "") &&
                    (jsonAttr === "parent_id" || fieldName === "parentid")) {
                    idValue = response?.inspection_id || updatedFormData?.inspection_id;
                    if (import.meta.env.DEV && idValue) {
                      console.log("[Edit Mode] Mapped inspection_id to parent_id:", {
                        inspection_id: response?.inspection_id,
                        parent_id: idValue,
                      });
                    }
                  }

                  let descValue = response?.[`${jsonAttr}_desc`] || response?.[`${fieldName}_desc`] ||
                    updatedFormData[`${jsonAttr}_desc`] || updatedFormData[`${fieldName}_desc`] ||
                    response?.[`${jsonAttr.toLowerCase()}_desc`] || response?.[`${fieldName.toLowerCase()}_desc`];

                  // Special case: If looking for parent_id_desc and not found, try inspection_id_desc or vin_no
                  if ((!descValue || descValue === undefined || descValue === null || descValue === "") &&
                    (jsonAttr === "parent_id" || fieldName === "parentid")) {
                    descValue = response?.inspection_id_desc || response?.vin_no || updatedFormData?.vin_no;
                    if (import.meta.env.DEV && descValue) {
                      console.log("[Edit Mode] Mapped vin_no/inspection_id_desc to parent_id_desc:", {
                        vin_no: response?.vin_no,
                        inspection_id_desc: response?.inspection_id_desc,
                        parent_id_desc: descValue,
                      });
                    }
                  }

                  if (import.meta.env.DEV) {
                    console.log("[Edit Mode] Search_Select field values:", {
                      fieldName,
                      jsonAttr,
                      idValue,
                      descValue,
                      checkedKeys: {
                        [`response[${jsonAttr}]`]: response?.[jsonAttr],
                        [`response[${jsonAttr}_desc]`]: response?.[`${jsonAttr}_desc`],
                        [`updatedFormData[${fieldName}]`]: updatedFormData[fieldName],
                      },
                    });
                  }

                  if (idValue && String(idValue).trim().length > 0) {
                    // Set the field value
                    updatedFormData[fieldName] = idValue;

                    // Try to get parent record from lookup rawData first
                    let parentRecord = null;
                    const lookupsState = getState().lookups;
                    const lookupRawData = lookupsState.rawData[lookupName] || [];
                    parentRecord = lookupRawData.find((item: any) =>
                      item.id === idValue || String(item.id) === String(idValue) ||
                      item.value === idValue || String(item.value) === String(idValue)
                    );

                    // If not found and we have desc value, create a LookupItem from backend data
                    if (!parentRecord && descValue) {
                      // Create LookupItem from backend response (parent_id + parent_id_desc)
                      const lookupItem = {
                        id: idValue,
                        value: idValue,
                        label: descValue,
                        name: descValue,
                      };

                      // Add to lookup options for SearchableDropdown display
                      dispatch(
                        addLookupItem({
                          lookupKey: lookupName,
                          item: lookupItem,
                        })
                      );

                      if (import.meta.env.DEV) {
                        console.log("[Workflow Navigation] Created LookupItem from _id_desc:", {
                          fieldName,
                          jsonAttr,
                          lookupName,
                          idValue,
                          descValue,
                        });
                      }
                    }

                    // Fetch full parent record for BindParent and Report
                    if (!parentRecord) {
                      try {
                        const parentResponse = await apiService.get(
                          `/pabws/utility/${lookupName}`,
                          { id: idValue },
                          {
                            includeAuthToken: true,
                            authHeaderType: "Bearer",
                            errorHandling: "return-empty",
                            transformResponse: (raw: any) => {
                              if (!raw || !raw.success) return null;
                              if (Array.isArray(raw.data)) return raw.data[0] || null;
                              return raw.data || null;
                            },
                          }
                        );
                        parentRecord = parentResponse;

                        // Add to lookup options and rawData
                        if (parentRecord) {
                          dispatch(
                            addLookupItem({
                              lookupKey: lookupName,
                              item: {
                                ...parentRecord,
                                label: parentRecord.name || parentRecord.id || descValue,
                                value: parentRecord.id || idValue,
                              },
                            })
                          );

                          dispatch(
                            addLookupItem({
                              lookupKey: lookupName,
                              item: parentRecord,
                              isRawData: true,
                            })
                          );
                        }
                      } catch (parentError) {
                        console.error(`[Workflow Navigation] Failed to load parent record for ${fieldName}:`, parentError);
                      }
                    }

                    // Store parent record in formData for Report component and BindParent
                    if (parentRecord) {
                      if (import.meta.env.DEV) {
                        console.log("[Edit Mode] Storing parent record for Search_Select:", {
                          fieldName,
                          jsonAttr,
                          lookupName,
                          parentRecordKeys: Object.keys(parentRecord),
                          parentRecordSample: Object.keys(parentRecord).slice(0, 9999).reduce((acc, key) => {
                            acc[key] = parentRecord[key];
                            return acc;
                          }, {} as Record<string, any>),
                        });
                      }
                      const selectedDataKey = `${fieldName}_selectedData`;
                      updatedFormData[selectedDataKey] = parentRecord;

                      // Process BindParent fields
                      const findBindParentFields = (components: any[]): any[] => {
                        const bindParentFields: any[] = [];
                        const walk = (comps: any[]) => {
                          comps.forEach((comp: any) => {
                            if (comp.BindParent && comp.BindParent[fieldName]) {
                              bindParentFields.push({
                                field: comp,
                                bindColumn: comp.BindParent[fieldName],
                              });
                            }
                            if (comp.Fields && Array.isArray(comp.Fields)) {
                              walk(comp.Fields);
                            }
                            if (comp.childComponents && Array.isArray(comp.childComponents)) {
                              walk(comp.childComponents);
                            }
                            if (comp.childControls && Array.isArray(comp.childControls)) {
                              walk(comp.childControls);
                            }
                          });
                        };
                        walk(components);
                        return bindParentFields;
                      };

                      const bindParentFields = findBindParentFields(screenConfig);
                      bindParentFields.forEach(({ field, bindColumn }) => {
                        const targetFieldName = field.name || field.BindValue;
                        if (targetFieldName && parentRecord[bindColumn] !== undefined) {
                          updatedFormData[targetFieldName] = parentRecord[bindColumn];

                          if (import.meta.env.DEV) {
                            console.log("[Workflow Navigation] Populated BindParent field:", {
                              sourceField: fieldName,
                              targetField: targetFieldName,
                              bindColumn,
                              value: parentRecord[bindColumn],
                            });
                          }
                        }
                      });

                      if (import.meta.env.DEV) {
                        console.log("[Workflow Navigation] Loaded parent record for Search_Select field:", {
                          fieldName,
                          jsonAttr,
                          lookupName,
                          parentId: idValue,
                          parentRecordName: parentRecord.name || descValue,
                          selectedDataKey,
                        });
                      }
                    }
                  }
                }
              }
            }

            // CRITICAL: Preserve workflow fields (next_action, next_state) from current form data
            // Get current form data from Redux store to preserve these values
            const currentFormData = getState().forms.formDataByScreen[targetScreenId] || {};
            if (currentFormData.next_action) {
              updatedFormData.next_action = currentFormData.next_action;
            } else if (initialFormData.next_action) {
              // Fallback to initialFormData if not in store yet
              updatedFormData.next_action = initialFormData.next_action;
            }
            if (currentFormData.next_state) {
              updatedFormData.next_state = currentFormData.next_state;
            } else if (initialFormData.next_state) {
              // Fallback to initialFormData if not in store yet
              updatedFormData.next_state = initialFormData.next_state;
            }

            // Also preserve from workflowItem as final fallback
            if (!updatedFormData.next_action && (workflowItem.Action || actionId)) {
              updatedFormData.next_action = workflowItem.Action || actionId;
            }
            if (!updatedFormData.next_state && workflowItem.States && workflowItem.States[0]) {
              updatedFormData.next_state = workflowItem.States[0];
            }

            if (import.meta.env.DEV) {
              console.log("[Workflow Navigation] Preserved workflow fields:", {
                next_action: updatedFormData.next_action,
                next_state: updatedFormData.next_state,
                source: {
                  fromStore: !!(currentFormData.next_action || currentFormData.next_state),
                  fromInitial: !!(initialFormData.next_action || initialFormData.next_state),
                  fromWorkflowItem: !!(workflowItem.Action || workflowItem.States?.[0]),
                },
              });
            }

            // CRITICAL: Update formData BEFORE loading submodules/reports so components can detect the data
            if (import.meta.env.DEV) {
              console.log("[Edit Mode] ===== Setting Form Data in Redux =====");
              console.log("[Edit Mode] Screen ID:", targetScreenId);
              console.log("[Edit Mode] Form data keys:", Object.keys(updatedFormData));
              console.log("[Edit Mode] Form data summary:", {
                id: updatedFormData.id,
                recordId: childRecordId,
                isEditMode: !!childRecordId,
                fieldCount: Object.keys(updatedFormData).length,
                subModuleFields: Object.keys(updatedFormData).filter(k =>
                  k.startsWith('sub_') || k.startsWith('multi_')
                ),
                searchSelectFields: Object.keys(updatedFormData).filter(k =>
                  k.includes('_selectedData')
                ),
                sampleFields: Object.keys(updatedFormData).slice(0, 20).reduce((acc, key) => {
                  const value = updatedFormData[key];
                  acc[key] = Array.isArray(value)
                    ? `[Array(${value.length})]`
                    : typeof value === 'object' && value !== null
                      ? '[Object]'
                      : String(value).substring(0, 50);
                  return acc;
                }, {} as Record<string, any>),
              });
              console.log("[Edit Mode] Full form data:", updatedFormData);
              console.log("[Edit Mode] =======================================");
            }

            dispatch(
              setScreenFormData({
                screenId: targetScreenId,
                data: updatedFormData,
              })
            );

            // CRITICAL: In edit/view mode, fetch submodule data separately (like Blazor)
            // This is a GENERIC implementation that works for ANY screen with submodules
            // Pattern: GET /pabws/submodule/{tableName}/{recordId}
            // Works for: estimation_add, jobcard_add, invoice_add, or any other screen with SUB_MODULE components
            if (childRecordId && childRecordId.toString().length > 0) {
              if (import.meta.env.DEV) {
                console.log("[Edit Mode] ===== Loading SubModule Data =====");
                console.log("[Edit Mode] Record ID:", childRecordId);
                console.log("[Edit Mode] Screen ID:", targetScreenId);
              }
              try {
                // Get the screen config to find submodules (generic - works for any sc                // Use getResolvedScreenConfig to get proper API configuration if loaded locally
                const screenConfig =
                  getResolvedScreenConfig(targetScreenId) ||
                  getResolvedScreenConfig(targetView) ||
                  getResolvedScreenConfig(deriveScreenId(targetScreenId) || "") ||
                  null;

                if (screenConfig) {
                  // GENERIC: Find all REPORT components to load parent data
                  const findReportComponents = (components: any[]): any[] => {
                    const reports: any[] = [];
                    components.forEach((comp) => {
                      if (comp.componentType === ComponentType.REPORT) {
                        reports.push(comp);
                      }
                      if (comp.childComponents && Array.isArray(comp.childComponents)) {
                        reports.push(...findReportComponents(comp.childComponents));
                      }
                      if (comp.Fields && Array.isArray(comp.Fields)) {
                        reports.push(...findReportComponents(comp.Fields));
                      }
                      if (comp.childControls && Array.isArray(comp.childControls)) {
                        reports.push(...findReportComponents(comp.childControls));
                      }
                    });
                    return reports;
                  };

                  // GENERIC: Recursively find all SUB_MODULE components in the config
                  // This works for any screen structure (nested groups, forms, etc.)
                  const findSubModules = (components: any[]): any[] => {
                    const subModules: any[] = [];
                    components.forEach((comp) => {
                      if (
                        comp.componentType === ComponentType.SUB_MODULE ||
                        comp.componentType === ComponentType.MULTI_SUB_LIST ||
                        (comp.componentType === ComponentType.MULTI_CHECKBOX &&
                          (comp.TableName || comp.tableName))
                      ) {
                        subModules.push(comp);
                      }
                      // Recursively search nested components
                      if (comp.childComponents && Array.isArray(comp.childComponents)) {
                        subModules.push(...findSubModules(comp.childComponents));
                      }
                      if (comp.Fields && Array.isArray(comp.Fields)) {
                        subModules.push(...findSubModules(comp.Fields));
                      }
                      if (comp.childControls && Array.isArray(comp.childControls)) {
                        subModules.push(...findSubModules(comp.childControls));
                      }
                    });
                    return subModules;
                  };

                  const subModules = findSubModules(screenConfig);
                  const reportComponents = findReportComponents(screenConfig);

                  if (import.meta.env.DEV) {
                    console.log("[Edit Mode] Found SubModules:", {
                      count: subModules.length,
                      subModules: subModules.map((sm: any) => ({
                        name: sm.Name || sm.name,
                        tableName: sm.TableName || sm.tableName,
                        bindValue: sm.BindValue || sm.bindValue,
                        parentField: sm.Parent_Field || sm.parentField,
                      })),
                    });
                    console.log("[Edit Mode] Found Report components:", {
                      count: reportComponents.length,
                      reports: reportComponents.map((r: any) => ({
                        settings: r.Settings || r.settings,
                        parentField: r.Parent_Field || r.parentField,
                      })),
                    });
                  }

                  // Load parent records for Report components in edit mode
                  if (reportComponents.length > 0) {
                    const reportDataPromises = reportComponents.map(async (reportComp) => {
                      const parentField = reportComp.Parent_Field || reportComp.parentField;
                      const parentFieldLookupName = reportComp.LookupName || reportComp.lookupName;

                      if (!parentField) {
                        return null;
                      }

                      // Get parentId from updatedFormData or from current formData (which was just updated)
                      // In edit mode, parent_id comes from the loaded record
                      const currentFormData = getState().forms.formDataByScreen[targetScreenId] || {};
                      // Get parent ID using the general helper function
                      // Check both updatedFormData and currentFormData (updatedFormData takes precedence)
                      const combinedFormData = { ...currentFormData, ...updatedFormData };
                      const parentId = getParentIdFromFormData(combinedFormData, parentField);

                      if (!parentId || parentId === "" || parentId === null || parentId === undefined) {
                        if (import.meta.env.DEV) {
                          console.log("[Workflow Navigation] Report component parent field not set:", {
                            parentField,
                            reportSettings: reportComp.Settings || reportComp.settings,
                            availableFields: Object.keys({ ...updatedFormData, ...currentFormData }),
                            checkedInspectionId: parentField === "parentid",
                          });
                        }
                        return null;
                      }

                      try {
                        // Try to get from lookup rawData first
                        let parentRecord = null;
                        if (parentFieldLookupName) {
                          const lookupKey = parentFieldLookupName;
                          const rawData = getState().lookups.rawData[lookupKey] || [];
                          parentRecord = rawData.find((item: any) =>
                            item.id === parentId || String(item.id) === String(parentId)
                          );
                        }

                        // If not found, fetch from API
                        if (!parentRecord && parentFieldLookupName) {
                          try {
                            if (parentFieldLookupName === "inspection_items") {
                              const parentResponse = await apiService.get(
                                `/pabws/utility/${parentFieldLookupName}`,
                                { id: parentId },
                                {
                                  includeAuthToken: true,
                                  authHeaderType: "Bearer",
                                  errorHandling: "return-empty",
                                  transformResponse: (raw: any) => {
                                    if (!raw || !raw.success) return null;
                                    if (Array.isArray(raw.data)) return raw.data[0] || null;
                                    return raw.data || null;
                                  },
                                }
                              );
                              parentRecord = parentResponse;
                            }
                          } catch (fetchError) {
                            console.error(`[Workflow Navigation] Failed to fetch parent for Report:`, fetchError);
                          }
                        }

                        if (parentRecord) {
                          const selectedDataKey = `${parentField}_selectedData`;
                          updatedFormData[selectedDataKey] = parentRecord;

                          // Also ensure parentid field is set for SearchableDropdown
                          updatedFormData[parentField] = parentId;
                          updatedFormData.parentid = parentId;

                          // Add to lookup options for SearchableDropdown display
                          if (parentFieldLookupName) {
                            dispatch(
                              addLookupItem({
                                lookupKey: parentFieldLookupName,
                                item: {
                                  ...parentRecord,
                                  label: parentRecord.name || parentRecord.id,
                                  value: parentRecord.id,
                                },
                              })
                            );

                            dispatch(
                              addLookupItem({
                                lookupKey: parentFieldLookupName,
                                item: parentRecord,
                                isRawData: true,
                              })
                            );
                          }

                          // Populate customer_remarks via BindParent if available
                          if (parentRecord.customer_remarks !== undefined) {
                            updatedFormData.customer_remarks = parentRecord.customer_remarks;
                          }

                          if (import.meta.env.DEV) {
                            console.log("[Workflow Navigation] Loaded parent record for Report:", {
                              parentField,
                              parentId,
                              reportSettings: reportComp.Settings || reportComp.settings,
                              parentRecordName: parentRecord.name,
                              customer_remarks: parentRecord.customer_remarks,
                              selectedDataKey,
                            });
                          }
                        }

                        return { parentField, parentRecord };
                      } catch (error) {
                        return null;
                      }
                    });

                    await Promise.all(reportDataPromises);

                    // Update formData with Report parent data
                    dispatch(
                      setScreenFormData({
                        screenId: targetScreenId,
                        data: updatedFormData,
                      })
                    );
                  }

                  if (subModules.length > 0 && import.meta.env.DEV) {
                    console.log("[Workflow Navigation] Found submodules to fetch:", {
                      screenId: targetScreenId,
                      childRecordId,
                      subModules: subModules.map((sm) => ({
                        name: sm.name || sm.Name,
                        tableName: sm.TableName || sm.tableName,
                        bindValue: sm.BindValue || sm.name,
                      })),
                    });
                  }

                  // Fetch each submodule's data separately
                  const subModuleDataPromises = subModules.map(async (subModule) => {
                    const tableName = subModule.Name || subModule.name || subModule.TableName || subModule.tableName;
                    // CRITICAL: Match ComponentRenderer's EXACT logic for fieldName
                    // ComponentRenderer uses: fieldName = subModuleConfig.name || subModuleConfig.BindValue
                    // But also checks BindValue as fallback when retrieving data
                    // So we should try to use the same key that ComponentRenderer will use
                    // Priority: name first (ComponentRenderer uses this), then BindValue
                    const bindValue = subModule.name || subModule.BindValue;
                    const alternateBindValue = subModule.BindValue || subModule.bindValue;

                    // CRITICAL: Store data under BOTH keys to ensure ComponentRenderer can find it
                    // ComponentRenderer checks both name and BindValue when retrieving

                    if (import.meta.env.DEV) {
                      console.log("[Workflow Navigation] Submodule field mapping:", {
                        tableName,
                        name: subModule.name,
                        Name: subModule.Name,
                        BindValue: subModule.BindValue,
                        usingBindValue: bindValue,
                      });
                    }

                    if (!tableName) {
                      if (import.meta.env.DEV) {
                        console.warn("[Workflow Navigation] Submodule missing TableName:", {
                          subModule,
                          bindValue,
                        });
                      }
                      return null;
                    }

                    try {
                      // API Call: GET /pabws/submodule/{tableName}/{recordId}
                      const subModuleEndpoint = `/pabws/submodule/${tableName}/${childRecordId}`;

                      if (import.meta.env.DEV) {
                        console.log("[Workflow Navigation] Fetching submodule data:", {
                          tableName,
                          bindValue,
                          endpoint: subModuleEndpoint,
                          recordId: childRecordId,
                        });
                      }

                      const subModuleResponse = await apiService.get(
                        subModuleEndpoint,
                        undefined,
                        {
                          includeAuthToken: true,
                          authHeaderType: "Bearer",
                          errorHandling: "return-empty",
                          transformResponse: (raw: any) => {
                            if (!raw) return [];

                            let finalData: any = raw;

                            // Robust decryption check
                            // 1. If raw is a string
                            if (typeof raw === 'string' && raw.length > 10 && !raw.trim().startsWith('{')) {
                              try {
                                const decrypted = authService.decryptData(raw);
                                if (decrypted) {
                                  finalData = decrypted;
                                  if (import.meta.env.DEV) console.log(`[Edit Mode] Decrypted raw submodule string for ${tableName}`);
                                }
                              } catch (e) { }
                            }
                            // 2. If raw.data is a string
                            else if (raw && typeof raw === 'object' && typeof raw.data === 'string' && raw.data.length > 10 && !raw.data.trim().startsWith('{')) {
                              try {
                                const decrypted = authService.decryptData(raw.data);
                                if (decrypted) {
                                  finalData = decrypted;
                                  if (import.meta.env.DEV) console.log(`[Edit Mode] Decrypted raw.data submodule string for ${tableName}`);
                                }
                              } catch (e) { }
                            }
                            // 3. If raw.data exists and is an array or object
                            else if (raw && typeof raw === 'object' && raw.data) {
                              finalData = raw.data;
                            }

                            // Final extraction to ensure we return an array
                            if (Array.isArray(finalData)) return finalData;

                            if (finalData && typeof finalData === 'object') {
                              // Pattern: { data: [...] }
                              if (Array.isArray(finalData.data)) return finalData.data;

                              // Pattern: { table_name: [...] }
                              const arrayValue = Object.values(finalData).find(v => Array.isArray(v));
                              if (arrayValue) return arrayValue;
                            }

                            return [];
                          },
                        }
                      );

                      if (subModuleResponse && Array.isArray(subModuleResponse) && subModuleResponse.length > 0) {
                        if (import.meta.env.DEV) {
                          console.log("[Workflow Navigation] Loaded submodule data:", {
                            tableName,
                            bindValue,
                            recordCount: subModuleResponse.length,
                            firstRecord: subModuleResponse[0],
                          });
                        }

                        return {
                          bindValue,
                          data: subModuleResponse,
                        };
                      } else {
                        if (import.meta.env.DEV) {
                          console.log("[Workflow Navigation] Submodule returned empty data:", {
                            tableName,
                            bindValue,
                          });
                        }
                        return {
                          tableName,
                          bindValue,
                          alternateBindValue, // Add alternate key
                          data: [],
                        };
                      }
                    } catch (subModuleError) {
                      console.error(
                        `[Workflow Navigation] Failed to fetch submodule ${tableName}:`,
                        subModuleError
                      );
                      return {
                        bindValue,
                        data: [],
                      };
                    }
                  });

                  // Wait for all submodule data to be fetched
                  const subModuleDataResults = await Promise.all(subModuleDataPromises);

                  if (import.meta.env.DEV) {
                    console.log("[Edit Mode] SubModule data fetch results:", {
                      totalSubModules: subModuleDataResults.length,
                      successful: subModuleDataResults.filter(r => r && r.data).length,
                      results: subModuleDataResults.map((r: any) => ({
                        bindValue: r?.bindValue,
                        recordCount: Array.isArray(r?.data) ? r.data.length : 0,
                        hasData: !!r?.data,
                      })),
                    });
                  }

                  // Update form data with submodule data
                  // CRITICAL: Merge with existing formData to preserve all fields (including Report parent data)
                  const currentFormData = getState().forms.formDataByScreen[targetScreenId] || {};
                  const updatedFormDataWithSubModules = { ...currentFormData, ...updatedFormData };

                  subModuleDataResults.forEach((result) => {
                    if (result && result.bindValue && result.data) {
                      // Ensure data is an array
                      const subModuleData = Array.isArray(result.data) ? result.data : [];

                      updatedFormDataWithSubModules[result.bindValue] = subModuleData;

                      // Also store under alternate BindValue if it exists and differs
                      if (result.alternateBindValue && result.alternateBindValue !== result.bindValue) {
                        updatedFormDataWithSubModules[result.alternateBindValue] = subModuleData;
                      }

                      // Also store under TableName if available (for robustness)
                      if (result.tableName && result.tableName !== result.bindValue) {
                        updatedFormDataWithSubModules[result.tableName] = subModuleData;
                      }
                    }
                  });



                  // Update form data with submodule data - this triggers re-render
                  dispatch(
                    setScreenFormData({
                      screenId: targetScreenId,
                      data: updatedFormDataWithSubModules,
                    })
                  );

                  if (import.meta.env.DEV) {
                    console.log("[Workflow Navigation] Final formData after submodule update:", {
                      screenId: targetScreenId,
                      subModuleKeys: subModuleDataResults
                        .filter((r): r is NonNullable<typeof r> => r !== null && r !== undefined && !!r.bindValue)
                        .map((r) => ({ key: r.bindValue, count: Array.isArray(r.data) ? r.data.length : 0 })),
                      hasParentData: !!updatedFormDataWithSubModules.parentid_selectedData,
                    });
                  }

                  if (import.meta.env.DEV) {
                    console.log("[Workflow Navigation] All submodules loaded:", {
                      screenId: targetScreenId,
                      childRecordId,
                      subModulesLoaded: subModuleDataResults.filter((r) => r !== null).length,
                    });
                  }
                }
              } catch (subModuleError) {
                console.error(
                  "[Workflow Navigation] Error fetching submodules:",
                  subModuleError
                );
                // Continue even if submodule fetch fails - main record is already loaded
              }
            }
          }
        } catch (error) {
          // On error, keep the initial data from the row
          console.error(`Failed to fetch workflow data for ${actionId}:`, error);
          ensureSlidingTimeout(
            dispatch,
            {
              message:
                "Unable to load details. Showing available data from the list.",
              type: "error",
            },
            5000
          );
        } finally {
          dispatch(
            setScreenLoading({ screenId: targetScreenId, isLoading: false })
          );
        }

        return;
      }

      // Handle other specific actions
      switch (actionId) {
        case "publish_calendar_start": {
          const currentScreenId = deriveScreenId(currentView) || currentView;
          const viewStr = typeof currentView === "string" ? currentView : "";
          const recordId = viewStr.split("/").filter(Boolean).pop() || "";

          if (!recordId) {
            ensureSlidingTimeout(dispatch, { message: "Missing holiday calendar id.", type: "error" }, 5000);
            return;
          }

          const encryptedUserData =
            typeof window !== "undefined" ? localStorage.getItem("encryptedUserData") : null;
          if (!encryptedUserData) {
            ensureSlidingTimeout(dispatch, { message: "User not authenticated. Please login again.", type: "error" }, 5000);
            return;
          }

          const decryptedData = authService.decryptData(encryptedUserData);
          if (!decryptedData) {
            ensureSlidingTimeout(dispatch, { message: "Failed to decrypt user data. Please login again.", type: "error" }, 5000);
            return;
          }

          const userId =
            decryptedData.empId || decryptedData.userId || decryptedData.id || "1";

          // Update holiday_calendar.status to "Published"
          try {
            ensureSlidingTimeout(
              dispatch,
              { message: "Publishing calendar...", type: "success" },
              3000
            );

            const response = await apiService.post(
              `/pabws/module/holiday_calendar_details/update`,
              {
                data: {
                  id: recordId,
                  status: "Published",
                  modified_by: userId,
                },
              },
              {
                includeAuthToken: true,
                authHeaderType: "Bearer",
                errorHandling: "throw",
              }
            );

            if (response && (response as any).success) {
              const resAny = response as any;
              dispatch(
                setScreenFormData({
                  screenId: String(currentScreenId),
                  data: {
                    ...(getState() as RootState).forms.formDataByScreen[String(currentScreenId)] || {},
                    id: recordId,
                    status: "Published",
                  },
                })
              );

              ensureSlidingTimeout(
                dispatch,
                { message: resAny.message || "Calendar published successfully.", type: "success" },
                5000
              );
            } else {
              ensureSlidingTimeout(
                dispatch,
                { message: (response as any)?.message || "Failed to publish calendar.", type: "error" },
                5000
              );
            }
          } catch (err: any) {
            console.error("[publish_calendar_start] Failed:", err);
            ensureSlidingTimeout(
              dispatch,
              { message: err?.message || "Failed to publish calendar. Please try again.", type: "error" },
              6000
            );
          }

          return;
        }
        // ── PAYROLL: Run Payroll for current month ─────────────────────────
        case "run_payroll": {
          const currentScreenId = deriveScreenId(currentView) || currentView;
          const { forms } = getState();
          const formDataForScreen = forms.formDataByScreen[currentScreenId] || {};

          ensureSlidingTimeout(
            dispatch,
            { message: "Processing payroll for this month, please wait...", type: "success" },
            8000
          );

          try {
            const response = await apiService.post(
              "/pabws/hrms/payroll/run",
              {},
              { includeAuthToken: true, authHeaderType: "Bearer", errorHandling: "throw" }
            );

            if (response && (response as any).success) {
              const result  = response as any;
              const summary = result.data || {};

              ensureSlidingTimeout(
                dispatch,
                {
                  message: result.message ||
                    `Payroll processed: ${summary.success ?? 0}/${summary.total_employees ?? 0} employees`,
                  type: "success",
                },
                6000
              );

              // Push stats into formData so INFO_CARDS picks them up
              if (summary.total_employees !== undefined) {
                dispatch(
                  setScreenFormData({
                    screenId: currentScreenId,
                    data: {
                      ...formDataForScreen,
                      total_payout:         summary.total_net_pay     ?? formDataForScreen.total_payout,
                      pending_approvals:    0,
                      employees_processed:  `${summary.success ?? 0}/${summary.total_employees ?? 0}`,
                    },
                  })
                );
              }
            }
          } catch (err) {
            console.error("[Payroll] Failed to run payroll:", err);
            ensureSlidingTimeout(
              dispatch,
              { message: "Failed to run payroll. Please try again.", type: "error" },
              5000
            );
          }
          return;
        }

        case "save_tab_data": {
          const { forms, navigation } = getState();
          const currentScreenId = deriveScreenId(currentView) || currentView;

          // Only handle HRMS Employee Profile Add (screen 2205 / emp_profile_add) for now
          const isEmpProfileScreen =
            currentScreenId === "2205" ||
            currentView === "emp_profile_add" ||
            currentView === "modulev1/emp_profile_add" ||
            currentView.startsWith("modulev1/emp_profile_add/");

          if (!isEmpProfileScreen) {
            // For non-HRMS screens, fall back to existing behaviour (no-op here)
            return;
          }

          const tab = (data && (data as any).tab) || "";
          const isLastTab = (data && (data as any).isLastTab) === true;
          const formDataForScreen =
            forms.formDataByScreen[currentScreenId] || {};

          if (tab !== "about" && tab !== "job" && tab !== "assets" && tab !== "salary" && tab !== "leave") {
            return;
          }

          // ── ABOUT TAB ───────────────────────────────────────────────────────
          if (tab === "about") {
            // Map formData BindValues (from 2205_emp_profile_add.ts) to backend Json_Attributes
            const aboutPayload: any = {
              // Primary details
              first_name: formDataForScreen.primary_first_name,
              middle_name: formDataForScreen.primary_middle_name,
              last_name: formDataForScreen.primary_last_name,
              gender: formDataForScreen.primary_gender,
              date_of_birth: formDataForScreen.primary_date_of_birth,
              marital_status: formDataForScreen.primary_marital_status,
              marriage_date: formDataForScreen.primary_marriage_date,
              nationality: formDataForScreen.primary_nationality,
              languages_read: formDataForScreen.primary_languages_read,
              languages_write: formDataForScreen.primary_languages_write,
              languages_speak: formDataForScreen.primary_languages_speak,
              physically_handicapped: formDataForScreen.primary_physically_handicapped,
              blood_group: formDataForScreen.primary_blood_group,

              // Contact details
              email: formDataForScreen.contact_email,
              mobile: formDataForScreen.contact_phone,
              personal_email: formDataForScreen.contact_personal_email,
              residence_number: formDataForScreen.contact_residence_number,
              current_address: formDataForScreen.address_current_address,
              permanent_address: formDataForScreen.address_permanent_address,

              // Emergency contacts
              emergency_contact_1_name: formDataForScreen.emergency_contact_1_name,
              emergency_contact_1_phone: formDataForScreen.emergency_contact_1_phone,
              emergency_contact_1_address: formDataForScreen.emergency_contact_1_address,
              emergency_contact_1_relationship: formDataForScreen.emergency_contact_1_relationship,
              emergency_contact_2_name: formDataForScreen.emergency_contact_2_name,
              emergency_contact_2_phone: formDataForScreen.emergency_contact_2_phone,
              emergency_contact_2_address: formDataForScreen.emergency_contact_2_address,
              emergency_contact_2_relationship: formDataForScreen.emergency_contact_2_relationship,

              // Identity Documents (Emirates ID, Passport, Visa)
              // BindValues: identity_emirates_id -> backend key: emirates_id
              emirates_id:             formDataForScreen.identity_emirates_id,
              emirates_id_issue_date:  formDataForScreen.identity_emirates_issue_date,
              emirates_id_expiry_date: formDataForScreen.identity_emirates_expiry_date,
              passport_no:             formDataForScreen.identity_passport_no,
              passport_issue_date:     formDataForScreen.identity_passport_issue_date,
              passport_expiry_date:    formDataForScreen.identity_passport_expiry_date,
              visa_no:                 formDataForScreen.identity_visa_no,

              // PAN / Aadhaar
              pan_number: formDataForScreen.pan_number,
              pan_name: formDataForScreen.pan_name,
              pan_dob: formDataForScreen.pan_dob,
              pan_parent_name: formDataForScreen.pan_parent_name,
              aadhaar_type: formDataForScreen.aadhaar_type,
              aadhaar_name: formDataForScreen.aadhaar_name,
              aadhaar_number: formDataForScreen.aadhaar_number,
              aadhaar_dob: formDataForScreen.aadhaar_dob,
              aadhaar_parent_name: formDataForScreen.aadhaar_parent_name,

              // Personal / about sections
              about: formDataForScreen.about_about,
              what_i_love: formDataForScreen.about_what_i_love,
              interests: formDataForScreen.about_interests,
              employment_history_previous_company_name: formDataForScreen.employment_history_previous_company_name,
              employment_history_previous_employer_id: formDataForScreen.employment_history_previous_employer_id,
              employment_history_previous_joining_date: formDataForScreen.employment_history_previous_joining_date,
              employment_history_previous_ending_date: formDataForScreen.employment_history_previous_ending_date,
              employment_history_total_employment: formDataForScreen.employment_history_total_employment,
              employment_history_reason_for_leaving: formDataForScreen.employment_history_reason_for_leaving,
            };

            const employeeId =
              (data && (data as any).employee_id) ||
              formDataForScreen.id ||
              formDataForScreen.employee_id;

            try {
              const response = await apiService.post(
                "/pabws/hrms/employee/profile/about",
                { employee_id: employeeId || null, data: aboutPayload },
                { includeAuthToken: true, authHeaderType: "Bearer", errorHandling: "throw" }
              );

              if (response && (response as any).success) {
                const result = response as any;
                const newEmployeeId = result.employee_id || employeeId;
                const newName = result.name;

                if (newEmployeeId) {
                  dispatch(
                    setScreenFormData({
                      screenId: currentScreenId,
                      data: {
                        ...formDataForScreen,
                        id: newEmployeeId,
                        employee_id: newEmployeeId,
                        primary_name: newName || formDataForScreen.primary_name,
                      },
                    })
                  );
                }
                if (!isLastTab) {
                  ensureSlidingTimeout(dispatch, { message: "About tab saved.", type: "success" }, 2500);
                }
              }
            } catch (err) {
              console.error("[HRMS] Failed to save employee about tab:", err);
              ensureSlidingTimeout(
                dispatch,
                { message: "Failed to save employee profile (About tab).", type: "error" },
                5000
              );
            }
          }

          // ── JOB TAB ─────────────────────────────────────────────────────────
          if (tab === "job") {
            const employeeId =
              (data && (data as any).employee_id) ||
              formDataForScreen.id ||
              formDataForScreen.employee_id;

            if (!employeeId) {
              ensureSlidingTimeout(
                dispatch,
                { message: "Please save the About tab first before saving Job details.", type: "error" },
                5000
              );
              return;
            }

            const jobPayload: any = {
              // Job Details
              employee_number:     formDataForScreen.job_employee_number,
              date_of_joining:     formDataForScreen.job_date_of_joining,
              job_title_primary:   formDataForScreen.job_job_title_primary,
              job_title_secondary: formDataForScreen.job_job_title_secondary,
              in_probation:        formDataForScreen.job_in_probation,
              notice_period:       formDataForScreen.job_notice_period,
              worker_type:         formDataForScreen.job_worker_type,
              time_type:           formDataForScreen.job_time_type,
              contract_status:     formDataForScreen.job_contract_status,
              pay_band:            formDataForScreen.job_pay_band,
              pay_grade:           formDataForScreen.job_pay_grade,

              // Organization
              business_unit:       formDataForScreen.organization_business_unit,
              group_id:            formDataForScreen.organization_group_id,
              location:            formDataForScreen.organization_location,
              cost_center:         formDataForScreen.organization_cost_center,
              legal_entity:        formDataForScreen.organization_legal_entity,
              reports_to:          formDataForScreen.organization_reports_to,
              manager_of_manager:  formDataForScreen.organization_manager_of_manager,
              direct_reports:      formDataForScreen.organization_direct_reports,
              visa_branch:         formDataForScreen.organization_visa_branch,
              branch_id:           formDataForScreen.organization_branch_id,

              // Employee Time
              shift:                           formDataForScreen.employee_time_shift,
              weekly_off_policy:               formDataForScreen.employee_time_weekly_off_policy,
              leave_plan:                      formDataForScreen.employee_time_leave_plan,
              holiday_calendar:                formDataForScreen.employee_time_holiday_calendar,
              attendance_number:               formDataForScreen.employee_time_attendance_number,
              attendance_capture_scheme:       formDataForScreen.employee_time_attendance_capture_scheme,
              attendance_penalization_policy:  formDataForScreen.employee_time_attendance_penalization_policy,
              shift_weekly_off_rule:           formDataForScreen.employee_time_shift_weekly_off_rule,
              shift_allowance_policy:          formDataForScreen.employee_time_shift_allowance_policy,
              overtime:                        formDataForScreen.employee_time_overtime,

              // Other / Policies
              expense_policy:    formDataForScreen.other_expense_policy,
              loan_policy:       formDataForScreen.other_loan_policy,
              air_ticket_policy: formDataForScreen.other_air_ticket_policy,
              sales_included:    formDataForScreen.other_sales_included,
              user_access:       formDataForScreen.other_user_access,
            };

            try {
              const response = await apiService.post(
                "/pabws/hrms/employee/profile/job",
                { employee_id: employeeId, data: jobPayload },
                { includeAuthToken: true, authHeaderType: "Bearer", errorHandling: "throw" }
              );

              if (response && (response as any).success) {
                dispatch(
                  setScreenFormData({
                    screenId: currentScreenId,
                    data: { ...formDataForScreen, id: employeeId, employee_id: employeeId },
                  })
                );
                if (!isLastTab) {
                  ensureSlidingTimeout(dispatch, { message: "Job tab saved.", type: "success" }, 2500);
                }
              }
            } catch (err) {
              console.error("[HRMS] Failed to save employee job tab:", err);
              ensureSlidingTimeout(
                dispatch,
                { message: "Failed to save employee profile (Job tab).", type: "error" },
                5000
              );
            }
          }

          // ── ASSETS TAB ──────────────────────────────────────────────────────
          if (tab === "assets") {
            const employeeId =
              (data && (data as any).employee_id) ||
              formDataForScreen.id ||
              formDataForScreen.employee_id;

            if (!employeeId) {
              ensureSlidingTimeout(
                dispatch,
                { message: "Please save the About tab first before assigning assets.", type: "error" },
                5000
              );
              return;
            }

            const assetsPayload: any = {
              // asset_id present only when editing an existing asset record
              asset_id:               formDataForScreen.asset_id,

              // Asset fields – BindValue -> Json_Attribute (backend key)
              asset_type:             formDataForScreen.asset_type,
              asset_name:             formDataForScreen.asset_name,
              asset_category:         formDataForScreen.asset_category,
              assigned_on:            formDataForScreen.asset_assigned_on,
              acknowledgement_status: formDataForScreen.asset_acknowledgement_status,
              latest_condition:       formDataForScreen.asset_latest_condition,
            };

            try {
              const response = await apiService.post(
                "/pabws/hrms/employee/profile/assets",
                { employee_id: employeeId, data: assetsPayload },
                { includeAuthToken: true, authHeaderType: "Bearer", errorHandling: "throw" }
              );

              if (response && (response as any).success) {
                const result = response as any;
                // Store the returned asset_id so a re-save edits the same record
                dispatch(
                  setScreenFormData({
                    screenId: currentScreenId,
                    data: {
                      ...formDataForScreen,
                      id: employeeId,
                      employee_id: employeeId,
                      asset_id: result.asset_id || formDataForScreen.asset_id,
                    },
                  })
                );
                if (!isLastTab) {
                  ensureSlidingTimeout(dispatch, { message: "Assets tab saved.", type: "success" }, 2500);
                }
              }
            } catch (err) {
              console.error("[HRMS] Failed to save employee assets tab:", err);
              ensureSlidingTimeout(
                dispatch,
                { message: "Failed to save employee profile (Assets tab).", type: "error" },
                5000
              );
            }
          }

          // ── SALARY TAB ──────────────────────────────────────────────────────
          if (tab === "salary") {
            const employeeId =
              (data && (data as any).employee_id) ||
              formDataForScreen.id ||
              formDataForScreen.employee_id;

            if (!employeeId) {
              ensureSlidingTimeout(
                dispatch,
                { message: "Please save the About tab first before saving Salary details.", type: "error" },
                5000
              );
              return;
            }

            const salaryPayload: any = {
              // Basic salary info
              currency:         formDataForScreen.salary_currency,
              salary_frequency: formDataForScreen.salary_frequency,
              effective_date:   formDataForScreen.salary_effective_date,

              // Allowances
              housing_allowance:   formDataForScreen.salary_housing_allowance,
              transport_allowance: formDataForScreen.salary_transport_allowance,
              food_allowance:      formDataForScreen.salary_food_allowance,
              medical_allowance:   formDataForScreen.salary_medical_allowance,
              other_allowances:    formDataForScreen.salary_other_allowances,
              total_allowances:    formDataForScreen.salary_total_allowances,

              // Deductions
              income_tax:       formDataForScreen.salary_income_tax,
              provident_fund:   formDataForScreen.salary_provident_fund,
              insurance:        formDataForScreen.salary_insurance,
              other_deductions: formDataForScreen.salary_other_deductions,
              total_deductions: formDataForScreen.salary_total_deductions,

              // Salary summary
              gross_salary:   formDataForScreen.salary_gross_salary,
              basic_salary:   formDataForScreen.salary_basic_salary,
              net_salary:     formDataForScreen.salary_net_salary,
              per_day_salary: formDataForScreen.salary_per_day_salary,

              // Bank details
              bank_name:           formDataForScreen.salary_bank_name,
              bank_account_number: formDataForScreen.salary_bank_account_number,
              bank_ifsc_code:      formDataForScreen.salary_bank_ifsc_code,
              bank_branch_name:    formDataForScreen.salary_bank_branch_name,
              bank_account_type:   formDataForScreen.salary_bank_account_type,

              // Additional salary info
              pay_grade:            formDataForScreen.salary_pay_grade,
              pay_scale:            formDataForScreen.salary_pay_scale,
              increment_date:       formDataForScreen.salary_increment_date,
              next_increment_date:  formDataForScreen.salary_next_increment_date,
              increment_percentage: formDataForScreen.salary_increment_percentage,
              bonus_eligibility:    formDataForScreen.salary_bonus_eligibility,
              bonus_percentage:     formDataForScreen.salary_bonus_percentage,
            };

            try {
              const response = await apiService.post(
                "/pabws/hrms/employee/profile/salary",
                { employee_id: employeeId, data: salaryPayload },
                { includeAuthToken: true, authHeaderType: "Bearer", errorHandling: "throw" }
              );

              if (response && (response as any).success) {
                dispatch(
                  setScreenFormData({
                    screenId: currentScreenId,
                    data: { ...formDataForScreen, id: employeeId, employee_id: employeeId },
                  })
                );
                if (!isLastTab) {
                  ensureSlidingTimeout(dispatch, { message: "Salary tab saved.", type: "success" }, 2500);
                }
              }
            } catch (err) {
              console.error("[HRMS] Failed to save employee salary tab:", err);
              ensureSlidingTimeout(
                dispatch,
                { message: "Failed to save employee profile (Salary tab).", type: "error" },
                5000
              );
            }
          }

          // ── LEAVE TAB ───────────────────────────────────────────────────────
          if (tab === "leave") {
            const employeeId =
              (data && (data as any).employee_id) ||
              formDataForScreen.id ||
              formDataForScreen.employee_id;

            if (!employeeId) {
              ensureSlidingTimeout(
                dispatch,
                { message: "Please save the About tab first before configuring leave.", type: "error" },
                5000
              );
              return;
            }

            const leavePayload: any = {
              // Bereavement
              bereavement_allocated_days:     formDataForScreen.leave_bereavement_allocated_days,
              bereavement_carry_forward_days: formDataForScreen.leave_bereavement_carry_forward_days,
              bereavement_carryover_enabled:  formDataForScreen.leave_bereavement_carryover_enabled,
              // Casual
              casual_allocated_days:          formDataForScreen.leave_casual_allocated_days,
              casual_carry_forward_days:      formDataForScreen.leave_casual_carry_forward_days,
              casual_carryover_enabled:       formDataForScreen.leave_casual_carryover_enabled,
              // Comp Offs
              comp_offs_allocated_days:       formDataForScreen.leave_comp_offs_allocated_days,
              comp_offs_carry_forward_days:   formDataForScreen.leave_comp_offs_carry_forward_days,
              comp_offs_carryover_enabled:    formDataForScreen.leave_comp_offs_carryover_enabled,
              // Paternity
              paternity_allocated_days:       formDataForScreen.leave_paternity_allocated_days,
              paternity_carry_forward_days:   formDataForScreen.leave_paternity_carry_forward_days,
              paternity_carryover_enabled:    formDataForScreen.leave_paternity_carryover_enabled,
              // Privilege Casual
              privilege_casual_allocated_days:     formDataForScreen.leave_privilege_casual_allocated_days,
              privilege_casual_carry_forward_days: formDataForScreen.leave_privilege_casual_carry_forward_days,
              privilege_casual_carryover_enabled:  formDataForScreen.leave_privilege_casual_carryover_enabled,
              // Sick
              sick_allocated_days:            formDataForScreen.leave_sick_allocated_days,
              sick_carry_forward_days:        formDataForScreen.leave_sick_carry_forward_days,
              sick_carryover_enabled:         formDataForScreen.leave_sick_carryover_enabled,
              // Unpaid
              unpaid_allocated_days:          formDataForScreen.leave_unpaid_allocated_days,
              unpaid_carry_forward_days:      formDataForScreen.leave_unpaid_carry_forward_days,
              unpaid_carryover_enabled:       formDataForScreen.leave_unpaid_carryover_enabled,
            };

            try {
              const response = await apiService.post(
                "/pabws/hrms/employee/profile/leave",
                { employee_id: employeeId, data: leavePayload },
                { includeAuthToken: true, authHeaderType: "Bearer", errorHandling: "throw" }
              );

              if (response && (response as any).success) {
                dispatch(
                  setScreenFormData({
                    screenId: currentScreenId,
                    data: {
                      ...formDataForScreen,
                      id: employeeId,
                      employee_id: employeeId,
                    },
                  })
                );

                // ── Final Submit: show success toast + navigate away ─────────
                if (isLastTab) {
                  ensureSlidingTimeout(
                    dispatch,
                    { message: "Employee profile saved successfully!", type: "success" },
                    4000
                  );
                  const prevView = getState().navigation.previousView;
                  setTimeout(() => {
                    dispatch(
                      navigateToView({
                        view: prevView || "modulev1/hrms_emp_summary",
                        rememberPrevious: false,
                      })
                    );
                  }, 700);
                }
                // ─────────────────────────────────────────────────────────────
              }
            } catch (err) {
              console.error("[HRMS] Failed to save employee leave tab:", err);
              ensureSlidingTimeout(
                dispatch,
                { message: "Failed to save employee profile (Leave tab).", type: "error" },
                5000
              );
            }
          }

          return;
        }

        case "toggle_sidebar":
          dispatch(toggleSideMenu());
          return;
        case "html_drawer": {
          const { htmlDrawer } = getState();
          const { forms } = getState();
          const activeScreenId = deriveScreenId(currentView) || currentView;
          const formData = forms.formDataByScreen[activeScreenId] || {};

          // Config is passed in data.buttonConfig
          const buttonConfig = data?.buttonConfig;
          const reportName = buttonConfig?.Settings;

          if (!reportName) {
            dispatch(showNotification({ message: "Report name not configured", type: "error" }));
            return;
          }

          // Open drawer with empty content and set loading
          dispatch(openHTMLDrawer({ htmlContent: "", reportName }));
          dispatch(setHTMLDrawerLoading(true));

          try {
            // Get ID from formData - try multiple fields
            const id = formData.id || formData.recordId || formData._id || formData.jobcard_id || formData.job_card_id;

            if (!id) {
              throw new Error("Record ID not found");
            }

            const response = await apiService.post(
              `/pabreport/report?name=${reportName}`,
              { id }
            );

            if (response.success) {
              dispatch(
                openHTMLDrawer({
                  htmlContent: response.data,
                  reportName,
                })
              );
            } else {
              throw new Error(response.message || "Failed to fetch report");
            }
          } catch (error: any) {
            console.error("[HTML Drawer] Error fetching report:", error);
            dispatch(setHTMLDrawerError(error.message || "An unknown error occurred"));
            dispatch(showNotification({ message: error.message || "Failed to load report", type: "error" }));
          }
          return;
        }

        case "set_branch":
          if (typeof data === "string") {
            const { availableBranches } = getState().layout;
            const branchObj = availableBranches.find(
              (b: any) => b.id === data || b.name === data
            );

            if (branchObj) {
              dispatch(setLayoutCurrentBranch(branchObj));
              dispatch(
                updateUserBranch({
                  branch_id: branchObj.id,
                  branch_name: branchObj.name,
                })
              );

              // Reload to refresh data context, matching App.tsx handleBranchChange
              setTimeout(() => {
                if (typeof window !== "undefined") {
                  window.location.reload();
                }
              }, 100);
            }

            dispatch(
              updateScreenFormField({
                screenId: currentView,
                name: "branch",
                value: data,
              })
            );
          }
          return;
        case "save": {
          const { forms, auth, navigation, layout } = getState();
          const activeScreenId =
            navigation.currentView === "garage"
              ? deriveScreenId("garage") || "100"
              : deriveScreenId(navigation.currentView) || navigation.currentView;

          // Prevent double submission
          if (forms.savingByScreen[activeScreenId]) {
            console.warn(`[Save Action] execution prevented: Already loading for screen ${activeScreenId}`);
            return;
          }

          // Set loading state immediately
          dispatch(setScreenSaving({ screenId: activeScreenId, isSaving: true }));

          const formData = forms.formDataByScreen[activeScreenId] || {};

          const encryptedUserData =
            typeof window !== "undefined"
              ? localStorage.getItem("encryptedUserData")
              : null;
          if (!encryptedUserData) {
            autoHideNotification(dispatch, {
              message: "User not authenticated. Please login again.",
              type: "error",
            });
            dispatch(setScreenSaving({ screenId: activeScreenId, isSaving: false }));
            return;
          }

          const decryptedData = authService.decryptData(encryptedUserData);
          if (!decryptedData) {
            autoHideNotification(dispatch, {
              message: "Failed to decrypt user data. Please login again.",
              type: "error",
            });
            dispatch(setScreenSaving({ screenId: activeScreenId, isSaving: false }));
            return;
          }

          // Get form configuration
          // Use resolved config: API cache first, local fallback
          const screenConfig = getResolvedScreenConfig(activeScreenId);
          if (!screenConfig) {
            ensureSlidingTimeout(
              dispatch,
              {
                message: "Form configuration not found.",
                type: "error",
              },
              5000
            );
            dispatch(setScreenSaving({ screenId: activeScreenId, isSaving: false }));
            return;
          }

          // Extract form component and TableName dynamically
          let formComponent: any = null;
          let tableName = "";
          let moduleEndpoint = "";

          walkComponentTree(screenConfig, (component) => {
            if (
              component.componentType === LayoutTypes.FORM ||
              (component as any).TableName
            ) {
              formComponent = component;
              tableName = (component as any).TableName || "";
              // Use Name property if available, otherwise use TableName
              const formName = (component as any).Name;
              moduleEndpoint = formName || tableName;
              return true;
            }
            return false;
          });

          if (!formComponent || !tableName) {
            ensureSlidingTimeout(
              dispatch,
              {
                message: "Form configuration not found or missing TableName.",
                type: "error",
              },
              5000
            );
            dispatch(setScreenSaving({ screenId: activeScreenId, isSaving: false }));
            return;
          }

          // Check if we're in edit mode
          const editModeInfo = forms.editModeByScreen[activeScreenId];

          // Determine recordId: prioritize edit mode recordId, then formData.id, only generate new ID for new records
          let recordId: string | number | undefined;

          if (editModeInfo?.isEditMode) {
            // In edit mode: use recordId from edit mode info, fallback to formData.id
            recordId = editModeInfo?.recordId || formData.id;
          } else {
            // GLOBAL FIX: Always generate new ID for create mode, ignoring any potentially reused formData.id
            // This ensures that subsequent submits on the same screen instance create NEW records
            recordId = undefined;
          }

          // CRITICAL: Edit mode detection - only use editModeByScreen state
          // Do NOT use recordId as fallback because new records may have generated IDs
          // isEditMode should only be true if explicitly set in Redux store (when opening existing record)
          const isEditMode = editModeInfo?.isEditMode || false;

          // CRITICAL: Only generate new ID if we're creating a new record (not in edit mode) and no ID exists
          // Also ensure the ID is not the same as parent_id (which would cause conflicts)
          if ((!recordId || String(recordId).trim() === "") && !isEditMode) {
            dispatch(incrementFormInstanceCounter());
            const instance = getState().forms.formInstanceCounter;
            const generatedId = generateUniqueFormId(
              resolveUserCode(decryptedData),
              instance
            );

            // Double-check: ensure generated ID is not the same as parent_id
            const parentId = formData.parent_id || formData.parentid;
            if (generatedId === parentId) {
              // If somehow the generated ID matches parent_id, generate again
              dispatch(incrementFormInstanceCounter());
              const newInstance = getState().forms.formInstanceCounter;
              recordId = generateUniqueFormId(
                resolveUserCode(decryptedData),
                newInstance
              );
            } else {
              recordId = generatedId;
            }

            if (import.meta.env.DEV) {
              console.log("[Save Action] Generated new unique ID for create mode:", {
                recordId,
                parentId,
                isDifferent: recordId !== parentId,
              });
            }
            dispatch(
              setScreenFormData({
                screenId: activeScreenId,
                data: {
                  ...formData,
                  id: recordId,
                },
              })
            );
          }

          const now = new Date();
          const formattedDate = `${String(now.getDate()).padStart(
            2,
            "0"
          )}-${String(now.getMonth() + 1).padStart(
            2,
            "0"
          )}-${now.getFullYear()} ${String(now.getHours()).padStart(
            2,
            "0"
          )}:${String(now.getMinutes()).padStart(2, "0")}:${String(
            now.getSeconds()
          ).padStart(2, "0")}`;

          // Helper to generate subform item ID
          const generateSubformItemId = (
            index: number,
            userCode: string | undefined,
            instance: number
          ): string => {
            return generateUniqueFormId(userCode, instance + index);
          };

          // Extract all fields from form configuration dynamically.
          // Must support both Json_Attribute (static/local config) and json_Attribute (API config).
          // When screen config is loaded from API (e.g. payment screen 144), the API returns
          // camelCase property names; using only Json_Attribute would miss them and fall back
          // to bindValue, causing payload keys like "txt_amount" instead of "amount", so
          // amount/discount would appear "missing" to the backend.
          const extractFieldMappings = (
            fields: any[]
          ): Record<string, string> => {
            const mappings: Record<string, string> = {};
            const processField = (field: any) => {
              const bindValue = field.BindValue || field.name;
              const jsonAttribute =
                field.Json_Attribute ?? field.json_Attribute ?? bindValue;
              if (bindValue && jsonAttribute) {
                mappings[bindValue] = jsonAttribute;
              }
              // Process nested fields
              if (field.Fields && Array.isArray(field.Fields)) {
                field.Fields.forEach(processField);
              }
              if (field.childComponents && Array.isArray(field.childComponents)) {
                field.childComponents.forEach(processField);
              }
              // SubModule uses childControls
              if (field.childControls && Array.isArray(field.childControls)) {
                field.childControls.forEach(processField);
              }
            };
            if (fields && Array.isArray(fields)) {
              fields.forEach(processField);
            }
            return mappings;
          };

          // Extract subform components dynamically
          const extractSubformComponents = (fields: any[]): any[] => {
            const subforms: any[] = [];
            const processComponent = (component: any) => {
              if (
                component.componentType === ComponentType.SUB_MODULE ||
                component.componentType === ComponentType.MULTI_SUB_LIST ||
                component.componentType === ComponentType.MULTI_CHECKBOX
              ) {
                subforms.push(component);
              }
              // Process nested components
              if (component.Fields && Array.isArray(component.Fields)) {
                component.Fields.forEach(processComponent);
              }
              if (
                component.childComponents &&
                Array.isArray(component.childComponents)
              ) {
                component.childComponents.forEach(processComponent);
              }
              // SubModule uses childControls
              if (
                component.childControls &&
                Array.isArray(component.childControls)
              ) {
                component.childControls.forEach(processComponent);
              }
            };
            if (fields && Array.isArray(fields)) {
              fields.forEach(processComponent);
            }
            return subforms;
          };

          // Get fields from form component - support both Fields and childComponents
          const formFields =
            (formComponent as any).Fields ||
            (formComponent as any).childComponents ||
            [];
          const fieldMappings = extractFieldMappings(formFields);
          const subformComponents = extractSubformComponents(formFields);

          // Helper function to get field config by bindValue
          const getFieldConfig = (bindValue: string): any => {
            const findField = (fields: any[]): any => {
              for (const field of fields) {
                if ((field.BindValue || field.name) === bindValue) {
                  return field;
                }
                if (field.Fields && Array.isArray(field.Fields)) {
                  const found = findField(field.Fields);
                  if (found) return found;
                }
                if (
                  field.childComponents &&
                  Array.isArray(field.childComponents)
                ) {
                  const found = findField(field.childComponents);
                  if (found) return found;
                }
                // SubModule uses childControls
                if (field.childControls && Array.isArray(field.childControls)) {
                  const found = findField(field.childControls);
                  if (found) return found;
                }
              }
              return null;
            };
            return findField(formFields);
          };

          // Helper function to format date to API format (DD-MM-YYYY HH:mm:ss)
          const formatDateForAPI = (dateValue: any): string => {
            if (!dateValue || dateValue === "") return "";

            let date: Date;
            if (dateValue instanceof Date) {
              date = dateValue;
            } else if (typeof dateValue === "string") {
              const strValue = dateValue.trim();
              if (!strValue) return "";

              // Handle ISO format (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss)
              if (strValue.includes("T")) {
                date = new Date(strValue);
              } else if (strValue.match(/^\d{4}-\d{2}-\d{2}/)) {
                // ISO date format YYYY-MM-DD (with optional time)
                const parts = strValue.split(" ");
                date = new Date(parts[0] + "T" + (parts[1] || "00:00:00"));
              } else if (strValue.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/)) {
                // DD-MM-YYYY format
                const match = strValue.match(/^(\d{1,2})-(\d{1,2})-(\d{4})(?:\s+(\d{1,2}):(\d{1,2}):(\d{1,2}))?/);
                if (match) {
                  const day = parseInt(match[1]);
                  const month = parseInt(match[2]) - 1;
                  const year = parseInt(match[3]);
                  const hour = match[4] ? parseInt(match[4]) : 0;
                  const minute = match[5] ? parseInt(match[5]) : 0;
                  const second = match[6] ? parseInt(match[6]) : 0;
                  date = new Date(year, month, day, hour, minute, second);
                } else {
                  date = new Date(strValue);
                }
              } else if (strValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)) {
                // DD/MM/YYYY format
                const match = strValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
                if (match) {
                  date = new Date(parseInt(match[3]), parseInt(match[2]) - 1, parseInt(match[1]));
                } else {
                  date = new Date(strValue);
                }
              } else {
                date = new Date(strValue);
              }
            } else {
              date = new Date(dateValue);
            }

            if (isNaN(date.getTime())) return "";

            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, "0");
            const minutes = String(date.getMinutes()).padStart(2, "0");
            const seconds = String(date.getSeconds()).padStart(2, "0");

            return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
          };

          const userIdentifier = decryptedData.userid || decryptedData.uuid || decryptedData.empId || decryptedData.userId || "1";

          // Build payload dynamically
          const payload: any = {
            data: {
              subform: [],
              id: recordId, // Always include recordId
            },
            next_action: "",
            next_state: "",
          };

          if (isEditMode) {
            payload.data.modified_by = userIdentifier;
            payload.data.modified_at = formattedDate;
          } else {
            payload.data.created_by = userIdentifier;
            payload.data.created_at = formattedDate;
            payload.data.transaction_branch_id = layout.currentBranch || "1";
          }

          // CRITICAL: Ensure parentid is included in payload from the start
          // Backend needs parentid to update service_details table (for jobcard)
          // Use getParentIdFromFormData utility to robustly find parentid
          // This handles various field name variations (parentid, parent_id, estimate_id, etc.)
          let parentId = getParentIdFromFormData(formData, "parentid");

          // If not found, also check Redux store for this screen
          if (!parentId) {
            const storedFormData = getState().forms.formDataByScreen[activeScreenId] || {};
            parentId = getParentIdFromFormData(storedFormData, "parentid");
          }

          // CRITICAL: Only add parentid if it's different from the record's own id
          // This prevents master records (like lookups) from sending their own id as parentid
          if (parentId && parentId !== recordId) {
            payload.data.parentid = parentId;
            payload.data.parent_id = parentId;

            // Also initialize parent_id_desc if available
            const storedFormData = getState().forms.formDataByScreen[activeScreenId] || {};
            const parentIdDesc = getParentIdDescFromFormData(formData, "parentid") || getParentIdDescFromFormData(storedFormData, "parentid");
            if (parentIdDesc) {
              payload.data.parent_id_desc = parentIdDesc;
            }
            if (import.meta.env.DEV) {
              console.log("[Save Action] Initialized payload with parentid:", parentId, {
                source: "formData",
                formDataKeys: Object.keys(formData).filter(k => k.toLowerCase().includes("parent") || k.toLowerCase().includes("estimat")),
              });
            }
          }
          else if (import.meta.env.DEV) {
            console.warn("[Save Action] parentid not found in formData:", {
              formDataKeys: Object.keys(formData),
              activeScreenId,
              storedFormDataKeys: Object.keys(getState().forms.formDataByScreen[activeScreenId] || {}),
            });
          }

          // Get lookups state for dropdown descriptions
          const lookupsState = getState().lookups;

          // Check if form contains file components
          let hasFileComponents = false;
          walkComponentTree(screenConfig, (component) => {
            if (component.componentType === ComponentType.FILES) {
              hasFileComponents = true;
              return true; // Stop traversal once found
            }
          });

          // Upload pending image files (from FileUpload) before building payload
          const pendingFiles = (data && typeof data === "object" && data.pendingFiles) ? data.pendingFiles as Record<string, File> : {};
          const uploadedImageIdsByField: Record<string, string> = {};
          const uploadErrors: Array<{ fieldName: string; error: string }> = [];

          if (Object.keys(pendingFiles).length > 0) {
            // Upload all files and track any failures
            await Promise.allSettled(
              Object.entries(pendingFiles).map(async ([fieldName, file]) => {
                try {
                  const imageId = await uploadImage(file);
                  uploadedImageIdsByField[fieldName] = imageId;
                } catch (err: any) {
                  const errorMessage = err?.message || "Unknown error occurred";
                  uploadErrors.push({ fieldName, error: errorMessage });
                  if (import.meta.env.DEV) {
                    console.error(`[Save Action] Image upload failed for field ${fieldName}:`, err);
                  }
                }
              })
            );

            // If there are upload errors, show alert and stop the save process
            if (uploadErrors.length > 0) {
              const errorMessages = uploadErrors.map(e => `${e.fieldName}: ${e.error}`).join("\n");
              const alertMessage = uploadErrors.length === 1
                ? `Failed to upload file for ${uploadErrors[0].fieldName}. ${uploadErrors[0].error}`
                : `Failed to upload ${uploadErrors.length} file(s). Please check and try again.\n\n${errorMessages}`;

              ensureSlidingTimeout(dispatch, {
                message: alertMessage,
                type: "error",
              }, 8000);

              // Also show browser alert for critical errors
              alert(alertMessage);
              dispatch(setScreenSaving({ screenId: activeScreenId, isSaving: false }));
              return; // Stop save process if uploads failed
            }
          }

          // Map direct fields dynamically
          Object.entries(fieldMappings).forEach(([bindValue, jsonAttribute]) => {
            // Skip subform bind values (they are handled separately)
            const isSubformField = subformComponents.some(
              (subform) => (subform.BindValue || subform.name) === bindValue
            );
            if (!isSubformField) {
              const fieldConfig = getFieldConfig(bindValue);
              const isDateField =
                fieldConfig?.componentType === ComponentType.DATE_FIELD;
              const isDropdown =
                fieldConfig?.componentType === ComponentType.SELECT_DROPDOWN ||
                fieldConfig?.componentType === ComponentType.SEARCHABLE_DROPDOWN;
              const isMandatory =
                fieldConfig?.IsMandatory || fieldConfig?.required;
              let value = formData[bindValue];
              if (fieldConfig?.componentType === ComponentType.FILES && uploadedImageIdsByField[bindValue] !== undefined) {
                value = uploadedImageIdsByField[bindValue];
              }

              // Debug logging for date fields
              if (isDateField && import.meta.env.DEV) {
                console.log(
                  `[Save Action] Date field: ${bindValue} -> ${jsonAttribute}`,
                  {
                    value,
                    isMandatory,
                    formatted: value ? formatDateForAPI(value) : "empty",
                  }
                );
              }

              // Handle dropdown fields - include both ID and description
              if (isDropdown) {
                const descAttribute = `${jsonAttribute}_desc`;

                if (value !== null && value !== undefined && value !== "") {
                  // Get description from lookup options
                  const lookupName =
                    fieldConfig?.lookupName || fieldConfig?.LookupName;
                  let description = "";
                  let actualValue = value; // Default to the value from formData

                  if (lookupName) {
                    // Determine lookup key - handle dependent lookups
                    let lookupOptions: any[] = [];
                    let usedLookupKey = lookupName;

                    // Check if this is a dependent lookup
                    const parentField =
                      fieldConfig?.parentField ||
                      (fieldConfig?.BindParent
                        ? Object.keys(fieldConfig.BindParent)[0]
                        : undefined);

                    if (parentField) {
                      const parentValue = formData[parentField];
                      if (
                        parentValue !== undefined &&
                        parentValue !== null &&
                        parentValue !== ""
                      ) {
                        usedLookupKey = `${lookupName}_${parentValue}`;
                        lookupOptions = lookupsState.lookups[usedLookupKey] || [];

                        // Fallback: If specific dependent lookup is empty, try the base lookup name
                        // This handles cases where data might be in the base lookup (e.g. from initial load)
                        if (lookupOptions.length === 0) {
                          const baseOptions = lookupsState.lookups[lookupName] || [];
                          if (baseOptions.length > 0) {
                            if (import.meta.env.DEV) {
                              console.log(`[Save Action] Using base lookup as fallback for ${bindValue}:`, {
                                dependentKey: usedLookupKey,
                                baseKey: lookupName,
                              });
                            }
                            lookupOptions = baseOptions;
                          }
                        }
                      } else {
                        // Parent value missing, fallback to base lookup
                        lookupOptions = lookupsState.lookups[lookupName] || [];
                      }
                    } else {
                      // Not dependent, or parent value missing
                      lookupOptions = lookupsState.lookups[lookupName] || [];
                    }

                    if (import.meta.env.DEV && isDropdown && lookupName === "vehicletype") {
                      console.log(`[Save Action] Debug dependent lookup for vehicletype:`, {
                        bindValue,
                        lookupName,
                        parentField,
                        parentValue: parentField ? formData[parentField] : "N/A",
                        usedLookupKey,
                        optionsCount: lookupOptions.length,
                      });
                    }

                    // Try to find option by value/id first (normal case)
                    let selectedOption = lookupOptions.find(
                      (opt: any) =>
                        opt.value === value ||
                        String(opt.value) === String(value) ||
                        opt.id === value ||
                        String(opt.id) === String(value)
                    );

                    // If not found, try matching by label (when formData has label instead of ID)
                    if (!selectedOption) {
                      selectedOption = lookupOptions.find(
                        (opt: any) =>
                          opt.label === value ||
                          String(opt.label) === String(value) ||
                          (opt as any).name === value ||
                          String((opt as any).name) === String(value)
                      );

                      // If found by label, use the ID (value or id) for the payload
                      if (selectedOption) {
                        actualValue =
                          selectedOption.value ||
                          (selectedOption as any).id ||
                          value;
                        if (import.meta.env.DEV) {
                          console.log(
                            `[Save Action] Dropdown value was label, converted to ID for ${bindValue} (${jsonAttribute}):`,
                            {
                              originalValue: value,
                              convertedValue: actualValue,
                              lookupName,
                            }
                          );
                        }
                      }
                    }

                    if (selectedOption) {
                      description =
                        selectedOption.label ||
                        (selectedOption as any).name ||
                        "";
                    } else if (import.meta.env.DEV) {
                      console.warn(
                        `[Save Action] Dropdown option not found for ${bindValue} (${jsonAttribute}):`,
                        {
                          lookupName,
                          value,
                          availableOptions: lookupOptions.slice(0, 3), // Log first 3 options
                        }
                      );
                    }
                  }

                  // Use the actual ID value (not the label) for the payload.
                  // Preserve numeric/boolean dropdown values (e.g. 1/0) instead of coercing to string.
                  payload.data[jsonAttribute] =
                    actualValue === undefined || actualValue === null
                      ? ""
                      : typeof actualValue === "number" || typeof actualValue === "boolean"
                        ? actualValue
                        : String(actualValue);
                  // Always include description field (even if empty)
                  payload.data[descAttribute] = description;

                  // Duplicate field with BindValue if it differs from Json_Attribute
                  if (bindValue && bindValue !== jsonAttribute) {
                    payload.data[bindValue] = actualValue;
                  }

                  // CRITICAL: If this is the parentid field, also add it as parentid (not just parent_id)
                  // Backend needs parentid in params for SQL queries (e.g., update service_details where id = %(parentid)s)
                  if (bindValue === "parentid" || jsonAttribute === "parent_id") {
                    // Only overwrite if actualValue is not empty, OR if we don't have a parentid yet
                    if ((actualValue !== undefined && actualValue !== null && actualValue !== "") || !payload.data.parentid) {
                      payload.data.parentid = actualValue || payload.data.parentid || "";
                      payload.data.parent_id = actualValue || payload.data.parentid || "";
                    }
                    // CRITICAL: Ensure parent_id_desc is always included (backend expects it)
                    // If description is empty, try to get it from lookup options or formData
                    if (!description || description === "") {
                      // Try to get description from formData (might be stored as parentid_selectedData)
                      const selectedData = formData.parentid_selectedData || formData.parent_id_selectedData;
                      if (selectedData && typeof selectedData === 'object') {
                        description = (selectedData as any).name || (selectedData as any).label || (selectedData as any).estimation_no || "";
                      }
                      // If still empty, try to get from lookup options
                      if (!description && lookupName) {
                        // Try both the dependent lookup key and base lookup name
                        let lookupOptions: any[] = [];
                        const fieldParentField = fieldConfig?.parentField || fieldConfig?.Parent_Field;
                        if (fieldParentField && formData[fieldParentField]) {
                          const dependentKey = `${lookupName}_${formData[fieldParentField]}`;
                          lookupOptions = lookupsState.lookups[dependentKey] || [];
                        }
                        if (lookupOptions.length === 0) {
                          lookupOptions = lookupsState.lookups[lookupName] || [];
                        }
                        const foundOption = lookupOptions.find((opt: any) =>
                          opt.value === actualValue || String(opt.value) === String(actualValue) ||
                          opt.id === actualValue || String(opt.id) === String(actualValue)
                        );
                        if (foundOption) {
                          description = foundOption.label || (foundOption as any).name || "";
                        }
                      }
                    }
                    payload.data[descAttribute] = description || "";
                    if (import.meta.env.DEV) {
                      console.log("[Save Action] Added parentid from field mapping:", {
                        parentid: payload.data.parentid,
                        parent_id: payload.data.parent_id,
                        parent_id_desc: description || payload.data.parent_id_desc || "",
                      });
                    }

                    // Final assignment to ensure consistency
                    payload.data[jsonAttribute] = payload.data.parent_id;
                    payload.data[descAttribute] = description || payload.data.parent_id_desc || "";
                  }
                } else if (isMandatory) {
                  // For mandatory dropdowns, include empty values
                  payload.data[jsonAttribute] = "";
                  payload.data[descAttribute] = "";
                } else if (bindValue === "parentid" || jsonAttribute === "parent_id") {
                  // CRITICAL: For parentid field (even if not mandatory), always include parent_id and parent_id_desc
                  // Backend expects these fields even if empty
                  payload.data[jsonAttribute] = value || "";
                  payload.data[descAttribute] = "";
                  payload.data.parentid = value || "";
                  if (import.meta.env.DEV) {
                    console.log("[Save Action] Added parentid field (no value):", {
                      parentid: value || "",
                      parent_id: value || "",
                      parent_id_desc: "",
                    });
                  }
                }
              }
              // For mandatory date fields, always include them
              else if (isMandatory && isDateField) {
                if (
                  value &&
                  value !== "" &&
                  value !== null &&
                  value !== undefined
                ) {
                  payload.data[jsonAttribute] = formatDateForAPI(value);
                } else {
                  // Include empty string for mandatory date fields (API will validate)
                  payload.data[jsonAttribute] = "";
                }
              }
              // Handle boolean/checkbox fields - convert to "True"/"False" string
              else if (typeof value === "boolean") {
                payload.data[jsonAttribute] = value ? "True" : "False";
              }
              // For non-mandatory date fields or other fields
              else if (value !== null && value !== undefined) {
                if (value !== "") {
                  // Format date fields
                  if (isDateField) {
                    const formattedDateValue = formatDateForAPI(value);
                    payload.data[jsonAttribute] = formattedDateValue;
                    if (bindValue && bindValue !== jsonAttribute) {
                      payload.data[bindValue] = formattedDateValue;
                    }
                  } else {
                    // Fix: Ensure empty arrays are sent as null
                    // And for image/file uploads, ensure single items are sent as strings, not arrays
                    let finalValue = value;

                    if (Array.isArray(value)) {
                      if (value.length === 0) {
                        finalValue = null;
                      } else if (
                        value.length === 1 &&
                        (fieldConfig.componentType === ComponentType.IMAGE_UPLOAD ||
                          fieldConfig.componentType === ComponentType.FILES)
                      ) {
                        finalValue = value[0];
                      }
                    }

                    payload.data[jsonAttribute] = finalValue;
                    if (bindValue && bindValue !== jsonAttribute) {
                      payload.data[bindValue] = finalValue;
                    }
                  }
                }
                // For date/time fields (non-mandatory), include empty string
                else if (
                  (jsonAttribute.toLowerCase().includes("date") ||
                    jsonAttribute.toLowerCase().includes("time")) &&
                  !isMandatory
                ) {
                  payload.data[jsonAttribute] = "";
                }
                // For numeric fields with value 0
                else if (typeof value === "number") {
                  payload.data[jsonAttribute] = value;
                }
              }
              // Handle string values that represent booleans (for checkbox fields using TEXT_FIELD)
              else if (
                jsonAttribute === "is_cash" &&
                (value === "true" ||
                  value === "True" ||
                  value === "false" ||
                  value === "False")
              ) {
                payload.data[jsonAttribute] =
                  value === "true" || value === "True" ? "True" : "False";
              }
              // For mandatory non-date fields that are missing, include empty string
              else if (isMandatory && !isDateField && !isDropdown) {
                payload.data[jsonAttribute] = "";
              }
              // For is_cash field, always include "False" if not provided
              else if (
                jsonAttribute === "is_cash" &&
                (value === null || value === undefined || value === "")
              ) {
                payload.data[jsonAttribute] = "False";
              }
            }
          });

          // ROBUST FIX: Explicitly remove parentid for Product Add (106)
          // This handles cases where it might have been added by initial injection or field mapping (drp_group)
          if (activeScreenId === "106_add_product" || moduleEndpoint === "product_add") {
            if (payload.data.parentid) {
              delete payload.data.parentid;
              if (import.meta.env.DEV) {
                console.log("[Save Action] Explicitly removed parentid for Product Add");
              }
            }
            // Also ensure purchase_rate and sale_rate are strings if present
            if (payload.data.purchase_rate !== undefined && typeof payload.data.purchase_rate === 'number') {
              payload.data.purchase_rate = String(payload.data.purchase_rate);
            }
            if (payload.data.sale_rate !== undefined && typeof payload.data.sale_rate === 'number') {
              payload.data.sale_rate = String(payload.data.sale_rate);
            }
            // Also fix the bind values if they exist
            if (payload.data.txt_purchase_rate !== undefined && typeof payload.data.txt_purchase_rate === 'number') {
              payload.data.txt_purchase_rate = String(payload.data.txt_purchase_rate);
            }
            if (payload.data.txt_sale_rate !== undefined && typeof payload.data.txt_sale_rate === 'number') {
              payload.data.txt_sale_rate = String(payload.data.txt_sale_rate);
            }
          }

          // Process subforms dynamically
          const instance = getState().forms.formInstanceCounter;
          const subformItems: any[] = [];

          for (const subformConfig of subformComponents) {
            // Try both BindValue and name to find the data
            // ComponentRenderer and SubModule use name || BindValue priority, so data is stored under name
            // But configThunks was using BindValue || name, causing mismatch
            const bindValue = subformConfig.BindValue || subformConfig.name;
            const nameValue = subformConfig.name || subformConfig.BindValue;
            // Check both TableName (uppercase) and tableName (lowercase)
            const tableName =
              subformConfig.TableName || subformConfig.tableName || "";
            const subformName = subformConfig.Name || subformConfig.name || "";
            // Try both keys to find the data.
            // IMPORTANT: SubModule writes user-edited rows under BindValue,
            // while initial lookup data may be under the "name". We must
            // prioritize BindValue so the payload always reflects latest edits.
            const formDataValue = formData[bindValue] || formData[nameValue];

            // Handle empty arrays - include subform with empty data array instead of skipping
            const isEmptySubform =
              !formDataValue ||
              !Array.isArray(formDataValue) ||
              formDataValue.length === 0;

            if (subformConfig.componentType === ComponentType.SUB_MODULE) {
              // For SUB_MODULE, include even if empty (with empty data array)
              if (isEmptySubform) {
                subformItems.push({
                  tablename: tableName,
                  name: subformName,
                  data: [],
                  manadatoryResult: {
                    success: true,
                    fields: [],
                  },
                });
                continue;
              }
              // Extract field mappings for subform
              // SubModule uses childControls, but also check childComponents and Fields for compatibility
              const subformFields =
                subformConfig.childControls ||
                subformConfig.childComponents ||
                subformConfig.Fields ||
                [];
              const subformFieldMappings = extractFieldMappings(subformFields);

              // Resolve value from row trying all keys the UI might use (SubModule uses control.name || control.BindValue;
              // API config can use different casing, so try bindVal, jsonAttr, and field config keys).
              const getValueFromRow = (
                row: Record<string, any>,
                bv: string,
                ja: string,
                fieldCfg?: any
              ): any => {
                const keys = [
                  bv,
                  ja,
                  fieldCfg?.name,
                  fieldCfg?.BindValue,
                  fieldCfg?.Json_Attribute,
                  fieldCfg?.json_Attribute,
                  ja.charAt(0).toUpperCase() + ja.slice(1),
                  bv.charAt(0).toUpperCase() + bv.slice(1),
                ].filter(Boolean) as string[];
                for (const k of keys) {
                  if (row[k] !== undefined && row[k] !== null) return row[k];
                }
                return undefined;
              };

              // Debug: Log if no field mappings found
              if (
                Object.keys(subformFieldMappings).length === 0 &&
                import.meta.env.DEV
              ) {
                console.warn(
                  `[Subform] No field mappings found for ${subformName}`,
                  {
                    hasChildControls: !!subformConfig.childControls,
                    hasChildComponents: !!subformConfig.childComponents,
                    hasFields: !!subformConfig.Fields,
                    childControlsLength: subformConfig.childControls?.length || 0,
                    childComponentsLength:
                      subformConfig.childComponents?.length || 0,
                    fieldsLength: subformConfig.Fields?.length || 0,
                  }
                );
              }

              let subformData = formDataValue.map(
                (item: any, index: number) => {
                  // In edit mode, preserve existing itemid if present
                  // Check both itemid and id fields for existing items
                  const existingItemId = item.itemid || item.id;
                  const isExistingItem =
                    isEditMode &&
                    existingItemId &&
                    String(existingItemId).trim() !== "";

                  // CRITICAL FIX: Always generate new ID for new items.
                  // Do not use item.id as it might be from the lookup source (dup) which causes "record already exists" error.
                  const itemId = isExistingItem
                    ? existingItemId
                    : generateSubformItemId(
                      index,
                      resolveUserCode(decryptedData),
                      instance
                    );

                  const mappedItem: any = {
                    id: itemId,
                    act: isExistingItem ? item.act || "edit" : item.act || "add",
                    created_at: formattedDate,
                    created_by:
                      decryptedData.userid || decryptedData.uuid || decryptedData.empId || decryptedData.userId || "1",
                  };

                  // Preserve itemid for existing items in edit mode
                  if (isExistingItem && item.itemid) {
                    mappedItem.itemid = item.itemid;
                  }

                  // CRITICAL: First pass - convert all dropdown labels to IDs
                  // This ensures VAT ID and other dropdowns are converted before saving or ExpressionV2 calculations
                  const convertedItem: Record<string, any> = { ...item };
                  Object.entries(subformFieldMappings).forEach(
                    ([bindVal, jsonAttr]) => {
                      const fieldConfig = subformFields.find(
                        (f: any) =>
                          f.BindValue === bindVal ||
                          f.name === bindVal ||
                          f.Json_Attribute === jsonAttr ||
                          f.json_Attribute === jsonAttr
                      );
                      const isDropdown =
                        fieldConfig?.componentType ===
                        ComponentType.SELECT_DROPDOWN ||
                        fieldConfig?.componentType ===
                        ComponentType.SEARCHABLE_DROPDOWN;
                      const lookupName =
                        fieldConfig?.lookupName || fieldConfig?.LookupName;

                      if (isDropdown && lookupName) {
                        let value = getValueFromRow(item, bindVal, jsonAttr, fieldConfig);
                        if (
                          value &&
                          typeof value === "string" &&
                          value !== ""
                        ) {
                          const lookupOptions =
                            lookupsState.lookups[lookupName] || [];

                          const selectedOption = lookupOptions.find(
                            (opt: any) =>
                              opt.label === value ||
                              String(opt.label) === String(value) ||
                              (opt as any).name === value ||
                              String((opt as any).name) === String(value)
                          );
                          if (selectedOption) {
                            const idValue =
                              selectedOption.value ||
                              (selectedOption as any).id ||
                              value;
                            convertedItem[bindVal] = idValue;
                            convertedItem[jsonAttr] = idValue;
                            if (import.meta.env.DEV && idValue !== value) {
                              console.log(
                                `[Subform Save] Pre-converted ${jsonAttr} from label to ID:`,
                                { original: value, converted: idValue, lookupName }
                              );
                            }
                          }
                        }
                      }
                    }
                  );

                  // Map fields dynamically - use convertedItem which has IDs instead of labels
                  // Note: For Parts Issue, business logic (issued_qty="1.0", bal_qty="0.0") is handled by buildPartsIssuePayload
                  Object.entries(subformFieldMappings).forEach(
                    ([bindVal, jsonAttr]) => {
                      const fieldConfig = subformFields.find(
                        (f: any) =>
                          f.BindValue === bindVal ||
                          f.name === bindVal ||
                          f.Json_Attribute === jsonAttr ||
                          f.json_Attribute === jsonAttr
                      );
                      // Resolve value from row using all keys UI might use (fixes amount/discount not passed when API config or different casing)
                      let value = getValueFromRow(convertedItem, bindVal, jsonAttr, fieldConfig);
                      if (value === undefined || value === null) {
                        value = getValueFromRow(item, bindVal, jsonAttr, fieldConfig);
                      }
                      // CRITICAL FIX: Include calculated numeric fields even if they're "0" or "0.00"
                      // This ensures amount, discount, vat_amt, net_amt etc. are always included for SUM calculations
                      const isNumericField =
                        jsonAttr === "amount" ||
                        jsonAttr === "discount" ||
                        jsonAttr === "discounted_amount" ||
                        jsonAttr === "net_discount" ||
                        jsonAttr === "net_amount" ||
                        jsonAttr === "total_amount" ||
                        jsonAttr === "amount_paid" ||
                        jsonAttr === "due_amount" ||
                        jsonAttr === "vat_amt" ||
                        jsonAttr === "net_amt" ||
                        jsonAttr === "rate" ||
                        jsonAttr === "qty" ||
                        jsonAttr === "issued_qty" ||
                        jsonAttr === "bal_qty" ||
                        jsonAttr === "purchase_rate" ||
                        jsonAttr === "stock_qty";

                      // For numeric fields, always include (even when value is missing - default to "0")
                      // so that submodule payloads (e.g. payment split amount) are never missing the column
                      if (isNumericField && (value === undefined || value === null)) {
                        value = "0";
                      }
                      const shouldInclude =
                        value !== undefined &&
                        value !== null &&
                        (value !== "" || isNumericField);

                      if (shouldInclude) {
                        // For Parts Issue, normalize numeric fields to strings (backend expects string format)
                        const isPartsIssue = activeScreenId === "120_add_parts_issue" || moduleEndpoint === "parts_issue";

                        let finalValue = value;
                        if (isNumericField) {
                          if (value === "" || value === null || value === undefined) {
                            finalValue = "0";
                          } else {
                            const numValue = parseFloat(String(value));
                            if (!isNaN(numValue)) {
                              // For Parts Issue, send as string (backend expects string format)
                              finalValue = String(numValue);
                            } else {
                              finalValue = "0";
                            }
                          }
                        }

                        // If multiple fields map to same Json_Attribute, combine them
                        if (mappedItem[jsonAttr]) {
                          mappedItem[jsonAttr] = [
                            mappedItem[jsonAttr],
                            finalValue,
                          ]
                            .filter(Boolean)
                            .join(" ");
                        } else {
                          // CRITICAL: For Parts Issue numeric fields, ensure they're strings
                          // For other fields, also send as string
                          mappedItem[jsonAttr] = (finalValue !== undefined && finalValue !== null) ? String(finalValue) : "";
                        }
                      }
                    }
                  );
                  // FINAL SAFETY NET (GENERIC, AGGRESSIVE):
                  // Whatever is present on the submodule row object MUST go to the payload.
                  // We don't rely on field mappings here; we just mirror the entire row (except meta keys)
                  // into mappedItem so that amount/discount and any other user-edited values are never lost.
                  const rawRow = item || {};
                  Object.entries(rawRow).forEach(([rawKey, rawValue]) => {
                    if (
                      rawKey === "id" ||
                      rawKey === "itemid" ||
                      rawKey === "act" ||
                      rawKey === "created_at" ||
                      rawKey === "created_by"
                    ) {
                      return;
                    }

                    const normalized =
                      rawValue === undefined || rawValue === null
                        ? ""
                        : String(rawValue);

                    mappedItem[rawKey] = normalized;
                  });

                  return mappedItem;
                }
              );

              // For Parts Issue, ensure correct tablename and name
              let finalTableName = tableName;
              let finalSubformName = subformName;

              if (activeScreenId === "120_add_parts_issue" || moduleEndpoint === "parts_issue") {
                // For service_spare_parts_issue subform, use correct structure
                if (subformName === "service_spare_parts_issue" ||
                  tableName === "service_spare_parts_issue_details" ||
                  subformName === "service_spare_parts_issue_details") {
                  finalTableName = "service_spare_parts_issue_details";
                  finalSubformName = "service_spare_parts_issue";
                }
              }

              subformItems.push({
                tablename: finalTableName,
                name: finalSubformName,
                data: subformData,
                manadatoryResult: {
                  success: true,
                  fields: [],
                },
              });
            } else if (
              subformConfig.componentType === ComponentType.MULTI_SUB_LIST
            ) {
              // For MULTI_SUB_LIST, skip if empty
              if (isEmptySubform) {
                continue;
              }

              // Extract field mappings for multi-sub-list
              const subformFields =
                subformConfig.childComponents || subformConfig.Fields || [];
              const subformFieldMappings = extractFieldMappings(subformFields);

              const subformData = formDataValue.map(
                (item: any, index: number) => {
                  // In edit mode, preserve existing itemid if present
                  // Check both itemid and id fields for existing items
                  const existingItemId = item.itemid || item.id;
                  const isExistingItem =
                    isEditMode &&
                    existingItemId &&
                    String(existingItemId).trim() !== "";

                  const itemId = isExistingItem
                    ? existingItemId
                    : item.id ||
                    generateSubformItemId(
                      index,
                      resolveUserCode(decryptedData),
                      instance
                    );

                  const mappedItem: any = {
                    id: itemId,
                    act: isExistingItem ? item.act || "edit" : item.act || "add",
                    created_at: formattedDate,
                    created_by:
                      decryptedData.userid || decryptedData.uuid || decryptedData.empId || decryptedData.userId || "1",
                  };

                  // Preserve itemid for existing items in edit mode, otherwise add if not present
                  if (isExistingItem && item.itemid) {
                    mappedItem.itemid = item.itemid;
                  } else if (!mappedItem.itemid) {
                    mappedItem.itemid =
                      item.itemid || String(item.id || index + 1);
                  }

                  // CRITICAL: First pass - convert all dropdown labels to IDs
                  // This ensures VAT ID and other dropdowns are converted before ExpressionV2 calculations
                  const convertedItem: Record<string, any> = { ...item };
                  Object.entries(subformFieldMappings).forEach(
                    ([bindVal, jsonAttr]) => {
                      const fieldConfig = subformFields.find(
                        (f: any) =>
                          f.BindValue === bindVal ||
                          f.name === bindVal ||
                          f.Json_Attribute === jsonAttr
                      );
                      const isDropdown =
                        fieldConfig?.componentType ===
                        ComponentType.SELECT_DROPDOWN ||
                        fieldConfig?.componentType ===
                        ComponentType.SEARCHABLE_DROPDOWN;
                      const lookupName =
                        fieldConfig?.lookupName || fieldConfig?.LookupName;

                      if (isDropdown && lookupName) {
                        let value = item[bindVal] ?? item[jsonAttr];
                        if (value && typeof value === "string" && value !== "") {
                          const lookupOptions = lookupsState.lookups[lookupName] || [];

                          // If lookup is empty, log warning (might need to fetch it)
                          if (lookupOptions.length === 0 && import.meta.env.DEV && jsonAttr === "vat_id") {
                            console.warn(
                              `[Subform Save] Lookup ${lookupName} is empty for VAT ID conversion:`,
                              { value, bindVal, jsonAttr, availableLookups: Object.keys(lookupsState.lookups) }
                            );
                          }

                          const selectedOption = lookupOptions.find(
                            (opt: any) =>
                              opt.label === value ||
                              String(opt.label) === String(value) ||
                              (opt as any).name === value ||
                              String((opt as any).name) === String(value)
                          );
                          if (selectedOption) {
                            const idValue =
                              selectedOption.value || (selectedOption as any).id || value;
                            convertedItem[bindVal] = idValue;
                            convertedItem[jsonAttr] = idValue;
                            if (import.meta.env.DEV && idValue !== value) {
                              console.log(
                                `[Subform Save] Pre-converted ${jsonAttr} from label to ID:`,
                                { original: value, converted: idValue, lookupName }
                              );
                            }
                          }
                        }
                      }
                    }
                  );

                  // Map fields dynamically - try both BindValue and Json_Attribute (direct field name)
                  // Use convertedItem which has IDs instead of labels
                  Object.entries(subformFieldMappings).forEach(
                    ([bindVal, jsonAttr]) => {
                      // Try BindValue first, then fallback to Json_Attribute (direct field name)
                      // Use convertedItem which has IDs converted from the first pass
                      // Also check original item as fallback
                      let value = convertedItem[bindVal];
                      if (value === undefined || value === null) {
                        value = convertedItem[jsonAttr]; // Fallback to direct field name
                      }
                      // If still not found, try original item (shouldn't happen if conversion worked)
                      if (value === undefined || value === null) {
                        value = item[bindVal] ?? item[jsonAttr];
                      }

                      if (value !== undefined && value !== null) {
                        // Attempt to convert dropdown labels to IDs for subform fields
                        const fieldConfig = subformFields.find(
                          (f: any) =>
                            f.BindValue === bindVal ||
                            f.name === bindVal ||
                            f.Json_Attribute === jsonAttr
                        );
                        const isDropdown =
                          fieldConfig?.componentType ===
                          ComponentType.SELECT_DROPDOWN ||
                          fieldConfig?.componentType ===
                          ComponentType.SEARCHABLE_DROPDOWN;
                        const lookupName =
                          fieldConfig?.lookupName || fieldConfig?.LookupName;

                        if (
                          isDropdown &&
                          lookupName &&
                          typeof value === "string" &&
                          value !== ""
                        ) {
                          const lookupOptions = lookupsState.lookups[lookupName] || [];

                          // Try to find by value/id first
                          let selectedOption = lookupOptions.find(
                            (opt: any) =>
                              opt.value === value ||
                              String(opt.value) === String(value) ||
                              opt.id === value ||
                              String(opt.id) === String(value)
                          );

                          // If not found, try matching by label/name (e.g., "5%" -> "1")
                          if (!selectedOption) {
                            selectedOption = lookupOptions.find(
                              (opt: any) =>
                                opt.label === value ||
                                String(opt.label) === String(value) ||
                                (opt as any).name === value ||
                                String((opt as any).name) === String(value)
                            );

                            // If found by label, convert to ID
                            if (selectedOption) {
                              const convertedValue =
                                selectedOption.value ||
                                (selectedOption as any).id ||
                                value;
                              value = convertedValue;
                              if (import.meta.env.DEV) {
                                console.log(
                                  `[Subform Save] Converted dropdown label to ID for ${bindVal}/${jsonAttr}:`,
                                  {
                                    originalValue: item[bindVal] ?? item[jsonAttr],
                                    convertedValue,
                                    lookupName,
                                    fieldName: bindVal,
                                  }
                                );
                              }
                            } else if (import.meta.env.DEV) {
                              console.warn(
                                `[Subform Save] Dropdown label not found in lookup for ${jsonAttr}:`,
                                {
                                  originalValue: value,
                                  lookupName,
                                  availableLabels: lookupOptions.slice(0, 5).map((opt: any) => opt.label),
                                }
                              );
                            }
                          }

                          // CRITICAL: Always set the value for dropdowns (either original ID or converted from label)
                          // Don't set empty strings
                          // FINAL SAFETY CHECK: If value still looks like a label (contains %), try one more conversion
                          if (value !== "" && value !== undefined && value !== null) {
                            // If value still looks like a label (contains % or is short non-numeric string), 
                            // try one more conversion attempt as a safety net
                            if (typeof value === "string" && lookupName &&
                              (value.includes("%") || (value.length < 10 && !/^\d+$/.test(value)))) {
                              const lookupOptions = lookupsState.lookups[lookupName] || [];
                              const labelOption = lookupOptions.find(
                                (opt: any) =>
                                  opt.label === value ||
                                  String(opt.label) === String(value) ||
                                  (opt as any).name === value ||
                                  String((opt as any).name) === String(value)
                              );
                              if (labelOption) {
                                const finalIdValue = labelOption.value || (labelOption as any).id || value;
                                if (finalIdValue !== value) {
                                  value = finalIdValue;
                                  if (import.meta.env.DEV && jsonAttr === "vat_id") {
                                    console.log(`[Subform Save] Final safety conversion for vat_id:`, {
                                      original: item[bindVal] ?? item[jsonAttr],
                                      converted: value,
                                      lookupName,
                                    });
                                  }
                                }
                              } else if (import.meta.env.DEV && jsonAttr === "vat_id") {
                                console.warn(`[Subform Save] VAT ID label not found in lookup (final safety check):`, {
                                  value,
                                  lookupName,
                                  availableLabels: lookupOptions.slice(0, 5).map((opt: any) => opt.label),
                                  availableValues: lookupOptions.slice(0, 5).map((opt: any) => opt.value),
                                });
                              }
                            }

                            // Use the converted value (which should be the ID now)
                            mappedItem[jsonAttr] = String(value);

                            if (import.meta.env.DEV && jsonAttr === "vat_id") {
                              console.log(`[Subform Save] Final vat_id value set:`, {
                                jsonAttr,
                                value,
                                valueType: typeof value,
                                isNumeric: /^\d+$/.test(String(value)),
                              });
                            }
                          }
                        } else {
                          // Not a dropdown - handle boolean/checkbox fields - convert to "True"/"False" string
                          if (typeof value === "boolean") {
                            mappedItem[jsonAttr] = value ? "True" : "False";
                          } else if (value !== "") {
                            mappedItem[jsonAttr] = String(value);
                          }
                        }
                      }
                    }
                  );

                  // CRITICAL: Recalculate vat_amt and net_amt if VAT ID was converted
                  // ExpressionV2: [txt_amount] * ([drp_vat.taxrate]/100)
                  // ExpressionV2: [txt_amount] + [txt_vat_amt]
                  const vatIdField = subformFields.find(
                    (f: any) =>
                      (f.BindValue === "drp_vat" || f.name === "drp_vat") &&
                      f.Json_Attribute === "vat_id"
                  );
                  const amountField = subformFields.find(
                    (f: any) =>
                      (f.BindValue === "txt_amount" || f.name === "txt_amount") &&
                      f.Json_Attribute === "amount"
                  );
                  const vatAmtField = subformFields.find(
                    (f: any) =>
                      (f.BindValue === "txt_vat_amt" || f.name === "txt_vat_amt") &&
                      f.Json_Attribute === "vat_amt"
                  );
                  const netAmtField = subformFields.find(
                    (f: any) =>
                      (f.BindValue === "txt_net_amt" || f.name === "txt_net_amt") &&
                      f.Json_Attribute === "net_amt"
                  );

                  if (vatIdField && amountField && vatAmtField && netAmtField) {
                    // Get VAT ID from mappedItem (already converted to ID)
                    const vatId = mappedItem["vat_id"];
                    // Get amount from mappedItem
                    const amount = parseFloat(String(mappedItem["amount"] || 0));

                    if (vatId && amount > 0) {
                      // Get tax rate from lookup
                      const lookupName = vatIdField.lookupName || vatIdField.LookupName || "tax_rates";
                      const lookupOptions = lookupsState.lookups[lookupName] || [];
                      const vatOption = lookupOptions.find(
                        (opt: any) =>
                          opt.value === vatId ||
                          String(opt.value) === String(vatId) ||
                          opt.id === vatId ||
                          String(opt.id) === String(vatId)
                      );

                      if (vatOption) {
                        const taxRate = parseFloat(String((vatOption as any).taxrate || (vatOption as any).tax_rate || (vatOption as any).rate || 0));
                        if (taxRate > 0) {
                          // Recalculate vat_amt: amount * (taxrate / 100)
                          const vatAmt = (amount * taxRate) / 100;
                          mappedItem["vat_amt"] = vatAmt.toFixed(2);

                          // Recalculate net_amt: amount + vat_amt
                          const netAmt = amount + vatAmt;
                          mappedItem["net_amt"] = netAmt.toFixed(2);

                          if (import.meta.env.DEV) {
                            console.log(
                              `[Subform Save] Recalculated VAT and Net amounts:`,
                              {
                                vatId,
                                amount,
                                taxRate,
                                vatAmt: vatAmt.toFixed(2),
                                netAmt: netAmt.toFixed(2),
                                lookupName,
                              }
                            );
                          }
                        } else if (import.meta.env.DEV) {
                          console.warn(
                            `[Subform Save] Tax rate is 0 or not found for VAT ID:`,
                            { vatId, vatOption, lookupName }
                          );
                        }
                      } else if (import.meta.env.DEV) {
                        console.warn(
                          `[Subform Save] VAT option not found in lookup:`,
                          { vatId, lookupName, availableOptions: lookupOptions.slice(0, 3) }
                        );
                      }
                    }
                  }

                  // Also include any fields that directly match Json_Attribute names (fallback)
                  // This handles cases where data uses direct field names instead of BindValue
                  subformFields.forEach((field: any) => {
                    const jsonAttr = field.Json_Attribute;
                    if (jsonAttr && !mappedItem.hasOwnProperty(jsonAttr)) {
                      const value = item[jsonAttr];
                      if (value !== undefined && value !== null) {
                        if (typeof value === "boolean") {
                          mappedItem[jsonAttr] = value ? "True" : "False";
                        } else if (value !== "") {
                          mappedItem[jsonAttr] = String(value);
                        }
                      }
                    }
                  });

                  return mappedItem;
                }
              );

              subformItems.push({
                tablename: tableName,
                name: subformName,
                data: subformData,
                manadatoryResult: {
                  success: true,
                  fields: [],
                },
              });
            } else if (
              subformConfig.componentType === ComponentType.MULTI_CHECKBOX
            ) {
              // For MULTI_CHECKBOX, skip if empty
              if (isEmptySubform) {
                continue;
              }

              // For MULTI_CHECKBOX, formDataValue is an array of selected values
              const jsonAttribute =
                subformConfig.Json_Attribute || "work_requested_id";
              const subformData = formDataValue.map(
                (selectedValue: string | number, index: number) => {
                  const itemId = generateSubformItemId(
                    index,
                    resolveUserCode(decryptedData),
                    instance
                  );
                  return {
                    [jsonAttribute]: String(selectedValue),
                    act: "add",
                    id: itemId,
                    created_by:
                      decryptedData.userid || decryptedData.empId || decryptedData.userId || "1",
                    created_at: formattedDate,
                  };
                }
              );

              subformItems.push({
                tablename: tableName,
                name: subformName,
                data: subformData,
                manadatoryResult: {
                  success: true,
                  fields: [],
                },
              });
            }
          }

          payload.data.subform = subformItems;

          // CRITICAL: Ensure parentid is always included in payload.data
          // Even if it was processed as a field, make sure it's present
          // Backend needs parentid to update service_details table (for jobcard)
          if (!payload.data.parentid && !payload.data.parent_id) {
            // Try to get from formData using utility function
            let parentId = getParentIdFromFormData(formData, "parentid");

            // If not found, check Redux store
            if (!parentId) {
              const storedFormData = getState().forms.formDataByScreen[activeScreenId] || {};
              parentId = getParentIdFromFormData(storedFormData, "parentid");
            }

            // If still not found, check if parentid field exists in formData (might be from dropdown)
            if (!parentId) {
              parentId = formData.parentid || formData.parent_id || formData.parentId;
            }

            if (parentId) {
              payload.data.parentid = parentId;
              payload.data.parent_id = parentId;
              if (import.meta.env.DEV) {
                console.log("[Save Action] Added parentid after field processing:", parentId);
              }
            } else if (import.meta.env.DEV) {
              console.warn("[Save Action] parentid still missing after field processing:", {
                payloadDataKeys: Object.keys(payload.data),
                formDataKeys: Object.keys(formData).filter(k => k.toLowerCase().includes("parent") || k.toLowerCase().includes("estimat")),
              });
            }
          } else if (import.meta.env.DEV) {
            console.log("[Save Action] parentid already in payload:", {
              parentid: payload.data.parentid,
              parent_id: payload.data.parent_id,
            });
          }

          // Evaluate ExpressionV2 expressions for fields
          const evaluateExpressionV2 = (
            expression: string,
            formData: Record<string, any>,
            lookupsState: any
          ): any => {
            if (!expression || typeof expression !== "string") {
              return "";
            }

            // Helper function to extract property value (robustly handling 0 values and variations)
            const extractValue = (item: any, property: string): any => {
              if (!item) return null;
              const variations = [
                property,
                property.toLowerCase(),
                property.toUpperCase(),
                property.replace(/_/g, ""), // e.g. purchase_rate -> purchaserate
                property.charAt(0).toUpperCase() + property.slice(1), // e.g. rate -> Rate
              ];

              for (const v of variations) {
                if (item[v] !== undefined && item[v] !== null) {
                  return item[v];
                }
              }
              return null;
            };

            /** Convert time string (HH:mm or HH:mm:ss) to decimal hours for ExpressionV2 duration math. */
            const timeStringToDecimalHours = (val: any): number | null => {
              if (val === undefined || val === null || val === "") return null;
              const s = String(val).trim();
              const match = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
              if (!match) return null;
              const hours = parseInt(match[1], 10);
              const minutes = parseInt(match[2], 10);
              const seconds = match[3] ? parseInt(match[3], 10) : 0;
              return hours + minutes / 60 + seconds / 3600;
            };

            // Handle cross-subform expressions like [(service_spare_est_req).txt_sub_amount]
            // Pattern: [(subformName).fieldBindValue]
            // DYNAMIC: Resolve BindValue to Json_Attribute from FooterControls config
            const subformRefMatch = expression.match(/\[\(([^)]+)\)\.([^\]]+)\]/g);
            if (subformRefMatch) {
              let result = expression;
              for (const match of subformRefMatch) {
                const subformMatch = match.match(/\[\(([^)]+)\)\.([^\]]+)\]/);
                if (subformMatch) {
                  const subformName = subformMatch[1]; // e.g., "service_spare_est_req"
                  const fieldBindValue = subformMatch[2]; // e.g., "txt_sub_amount" (BindValue)

                  // DYNAMIC: Resolve BindValue to Json_Attribute by finding SubModule config
                  let jsonAttribute = fieldBindValue; // Default to BindValue if not found

                  // Find SubModule config to get FooterControls
                  const screenConfig = getResolvedScreenConfig(activeScreenId);
                  if (screenConfig) {
                    let subModuleConfig: any = null;
                    const foundSubModules: any[] = [];

                    walkComponentTree(screenConfig, (component) => {
                      if (component.componentType === ComponentType.SUB_MODULE) {
                        const compAny = component as any;
                        foundSubModules.push({
                          name: compAny.name,
                          Name: compAny.Name,
                          TableName: compAny.TableName,
                          tableName: compAny.tableName,
                          BindValue: compAny.BindValue,
                        });

                        // Match by name, Name, TableName, or tableName (case-insensitive)
                        const matches =
                          compAny.name?.toLowerCase() === subformName.toLowerCase() ||
                          compAny.Name?.toLowerCase() === subformName.toLowerCase() ||
                          compAny.TableName?.toLowerCase() === subformName.toLowerCase() ||
                          compAny.tableName?.toLowerCase() === subformName.toLowerCase();

                        if (matches) {
                          subModuleConfig = component;
                          return true; // Stop searching
                        }
                      }
                      return false;
                    });

                    if (subModuleConfig) {
                      const footerControls = (subModuleConfig as any).FooterControls || [];

                      if (import.meta.env.DEV) {
                        console.log(`[Save Action] Found SubModule config:`, {
                          subformName,
                          subModuleConfig: {
                            name: (subModuleConfig as any).name,
                            Name: (subModuleConfig as any).Name,
                            TableName: (subModuleConfig as any).TableName,
                            FooterControlsCount: footerControls.length,
                          },
                          footerControls: footerControls.map((fc: any) => ({
                            name: fc.name,
                            BindValue: fc.BindValue,
                            Json_Attribute: fc.Json_Attribute,
                          })),
                        });
                      }

                      // Find footer field by BindValue
                      const footerField = footerControls.find((fc: any) =>
                        (fc.name || fc.BindValue) === fieldBindValue
                      );

                      if (footerField) {
                        jsonAttribute = footerField.Json_Attribute || footerField.json_Attribute || fieldBindValue;

                        if (import.meta.env.DEV) {
                          console.log(`[Save Action] ✓ Resolved cross-subform reference:`, {
                            match,
                            subformName,
                            fieldBindValue,
                            jsonAttribute,
                            formDataHasKey: jsonAttribute in formData,
                            formDataValue: formData[jsonAttribute],
                            formDataType: typeof formData[jsonAttribute],
                          });
                        }
                      } else if (import.meta.env.DEV) {
                        console.warn(`[Save Action] Footer field not found:`, {
                          subformName,
                          fieldBindValue,
                          availableFooterFields: footerControls.map((fc: any) => ({
                            name: fc.name,
                            BindValue: fc.BindValue,
                            Json_Attribute: fc.Json_Attribute,
                          })),
                        });
                      }
                    } else if (import.meta.env.DEV) {
                      console.warn(`[Save Action] SubModule config not found:`, {
                        subformName,
                        fieldBindValue,
                        foundSubModules,
                        activeScreenId,
                      });
                    }
                  } else if (import.meta.env.DEV) {
                    console.warn(`[Save Action] Screen config not found:`, {
                      activeScreenId,
                      availableScreens: Object.keys(screenConfigRegistry),
                    });
                  }

                  // Get value from formData using Json_Attribute (what SubModule actually stores)
                  const value = formData[jsonAttribute];
                  const numValue = value !== undefined && value !== null && value !== ""
                    ? parseFloat(String(value)) || 0
                    : 0;

                  if (import.meta.env.DEV && numValue === 0 && jsonAttribute !== fieldBindValue) {
                    console.warn(`[Save Action] Cross-subform reference resolved to 0:`, {
                      match,
                      subformName,
                      fieldBindValue,
                      jsonAttribute,
                      formDataKeys: Object.keys(formData).filter(k =>
                        k.toLowerCase().includes(subformName.toLowerCase()) ||
                        k.toLowerCase().includes(fieldBindValue.toLowerCase()) ||
                        k.toLowerCase().includes(jsonAttribute.toLowerCase())
                      ),
                    });
                  }

                  result = result.replace(match, String(numValue));
                }
              }

              // Handle local field references like [txt_discount] or time fields [start_time], [end_time]
              const localFieldMatch = result.match(/\[([^\]]+)\]/g);
              if (localFieldMatch) {
                for (const match of localFieldMatch) {
                  const fieldName = match.slice(1, -1);
                  // Don't re-process subform references (they contain parentheses)
                  if (!fieldName.includes("(")) {
                    const value = formData[fieldName];
                    const timeHours = timeStringToDecimalHours(value);
                    const numValue =
                      timeHours !== null
                        ? timeHours
                        : value !== undefined && value !== null && value !== ""
                          ? parseFloat(String(value)) || 0
                          : 0;
                    result = result.replace(match, String(numValue));

                    if (import.meta.env.DEV) {
                      console.log(`[Save Action] Resolved local field reference:`, {
                        match,
                        fieldName,
                        value,
                        numValue,
                      });
                    }
                  }
                }
              }

              // Evaluate the expression (e.g., "50 + 0 + 0" = "50")
              try {
                // Replace any remaining field references with 0 (safeguard)
                const cleanedExpr = result.replace(/\[[^\]]+\]/g, "0");
                const evaluated = Function(`"use strict"; return (${cleanedExpr})`)();
                return String(evaluated || 0);
              } catch (error) {
                if (import.meta.env.DEV) {
                  console.error(`[ExpressionV2] Error evaluating: ${expression}`, error);
                }
                return "0";
              }
            }

            // Handle simple field references like [drp_supplier.address]
            if (expression.startsWith("[") && expression.endsWith("]")) {
              // Remove brackets
              const expr = expression.slice(1, -1);

              // Handle field references like [drp_supplier.address]
              if (expr.includes(".")) {
                const [fieldName, attribute] = expr.split(".");
                const fieldValue = formData[fieldName];

                // CRITICAL: Handle 0 value (e.g. 0% VAT)
                if (fieldValue === undefined || fieldValue === null || fieldValue === "") {
                  return "";
                }

                // Get lookup options for the field
                const fieldConfig = getFieldConfig(fieldName);
                const lookupName =
                  fieldConfig?.lookupName || fieldConfig?.LookupName;
                const parentField =
                  fieldConfig?.parentField || fieldConfig?.Parent_Field;

                if (lookupName) {
                  // Determine lookup key (handle dependent lookups)
                  let lookupKey = lookupName;
                  if (parentField) {
                    const parentValue = formData[parentField];
                    if (
                      parentValue !== undefined &&
                      parentValue !== null &&
                      parentValue !== ""
                    ) {
                      lookupKey = `${lookupName}_${parentValue}`;
                    }
                  }

                  const lookupOptions = lookupsState.lookups[lookupKey] || [];
                  const rawData = lookupsState.rawData[lookupKey] || [];

                  // First try to find in rawData (has all properties like purchase_rate, sale_rate, etc.)
                  let rawItem = rawData.find(
                    (item: any) =>
                      item.id === fieldValue ||
                      String(item.id) === String(fieldValue) ||
                      item.value === fieldValue ||
                      String(item.value) === String(fieldValue) ||
                      item.label === fieldValue ||
                      String(item.label) === String(fieldValue) ||
                      item.name === fieldValue ||
                      String(item.name) === String(fieldValue)
                  );

                  // If not found in rawData, try lookupOptions
                  let selectedOption = null;
                  if (!rawItem) {
                    selectedOption = lookupOptions.find(
                      (opt: any) =>
                        opt.value === fieldValue ||
                        String(opt.value) === String(fieldValue) ||
                        opt.id === fieldValue ||
                        String(opt.id) === String(fieldValue) ||
                        opt.label === fieldValue ||
                        String(opt.label) === String(fieldValue)
                    );
                  }

                  // Robustly extract property value
                  if (rawItem || selectedOption) {
                    let propValue = extractValue(rawItem, attribute);
                    if (propValue === null && selectedOption) {
                      propValue = extractValue(selectedOption, attribute);
                    }

                    if (propValue !== null && propValue !== undefined) {
                      return String(propValue);
                    }
                  }
                }
              }
            }

            return "";
          };

          // Process ExpressionV2 fields
          const processComponent = (comp: any) => {
            if (comp.ExpressionV2 && comp.Json_Attribute) {
              const value = evaluateExpressionV2(
                comp.ExpressionV2,
                formData,
                lookupsState
              );
              if (value !== "" && value !== null && value !== undefined) {
                payload.data[comp.Json_Attribute] = value;
              }
            }

            // Process nested components
            if (comp.Fields && Array.isArray(comp.Fields)) {
              comp.Fields.forEach(processComponent);
            }
            if (comp.childComponents && Array.isArray(comp.childComponents)) {
              comp.childComponents.forEach(processComponent);
            }
          };

          formFields.forEach(processComponent);

          // Calculate SUM expressions from FooterControls
          const calculateSum = (
            fieldBindValue: string,
            subformConfig: any,
            subformItem: any
          ): number => {
            let sum = 0;

            // Find the Json_Attribute for the field name (bindValue)
            const subformFields =
              subformConfig.childControls || subformConfig.Fields || [];
            let jsonAttribute = fieldBindValue; // Default to bindValue

            for (const field of subformFields) {
              const bindValue = field.name || field.BindValue;
              if (bindValue === fieldBindValue && field.Json_Attribute) {
                jsonAttribute = field.Json_Attribute;
                break;
              }
            }

            // Sum up values from the specific subform item's data
            if (
              subformItem &&
              subformItem.data &&
              Array.isArray(subformItem.data)
            ) {
              for (const item of subformItem.data) {
                // Try both Json_Attribute and bindValue (mapped data uses Json_Attribute, but check both)
                const value =
                  item[jsonAttribute] !== undefined
                    ? item[jsonAttribute]
                    : item[fieldBindValue];

                if (value !== undefined && value !== null && value !== "") {
                  const numValue = parseFloat(String(value));
                  if (!isNaN(numValue)) {
                    sum += numValue;
                  }
                }
              }
            }

            return sum;
          };

          // Process FooterControls for SubModules
          for (const subformConfig of subformComponents) {
            const subformName = subformConfig.Name || subformConfig.name || "";
            // Find matching subform item by name
            const subformItem = subformItems.find(
              (item) => item.name === subformName
            );

            if (
              subformConfig.FooterControls &&
              Array.isArray(subformConfig.FooterControls)
            ) {
              for (const footerControl of subformConfig.FooterControls) {
                if (footerControl.ExpressionV2 && footerControl.Json_Attribute) {
                  const expr = footerControl.ExpressionV2;
                  const jsonAttribute = footerControl.Json_Attribute;

                  // CRITICAL FIX: First try to use stored footer value from formData
                  // SubModule already calculated and stored these values via onFormDataChange
                  const storedFooterValue = formData[jsonAttribute];

                  // Check if stored value exists (including 0 as a valid value)
                  const hasStoredValue =
                    storedFooterValue !== undefined &&
                    storedFooterValue !== null &&
                    storedFooterValue !== "";

                  if (hasStoredValue) {
                    // Use stored value (already formatted by SubModule)
                    // Handle both number (0) and string ("0.00") formats
                    let formattedValue: string;
                    if (typeof storedFooterValue === "number") {
                      formattedValue = storedFooterValue.toFixed(2);
                    } else if (typeof storedFooterValue === "string") {
                      // If it's already a string, ensure it's properly formatted
                      const numValue = parseFloat(storedFooterValue);
                      formattedValue = isNaN(numValue)
                        ? storedFooterValue
                        : numValue.toFixed(2);
                    } else {
                      formattedValue = String(storedFooterValue);
                    }

                    payload.data[jsonAttribute] = formattedValue;

                    console.log(
                      `[Form Submit Footer] Using stored value for ${jsonAttribute}: ${formattedValue}`,
                      {
                        expression: expr,
                        storedValue: storedFooterValue,
                        storedValueType: typeof storedFooterValue,
                        source: "formData",
                      }
                    );
                  } else {
                    // Fallback: Recalculate from subform data if stored value not available
                    // Handle SUM expressions like [SUM:txt_amount]
                    const sumMatch = expr.match(/\[SUM:(\w+)\]/);
                    if (sumMatch) {
                      const fieldBindValue = sumMatch[1]; // Extract field name from [SUM:fieldName]
                      const sumValue = calculateSum(
                        fieldBindValue,
                        subformConfig,
                        subformItem
                      );
                      // Format to 2 decimal places and convert to string
                      const formattedValue = sumValue.toFixed(2);
                      payload.data[jsonAttribute] = formattedValue;

                      // Debug logging
                      console.log(
                        `[Form Submit Footer] Recalculated ${jsonAttribute}: ${formattedValue}`,
                        {
                          expression: expr,
                          fieldBindValue,
                          subformDataLength: subformItem?.data?.length || 0,
                          source: "recalculation",
                          warning:
                            "Stored footer value not found, recalculated from subform data",
                        }
                      );
                    }
                  }
                }
              }
            }
          }

          // Set timestamp and user fields based on operation type
          // CREATE: set created_at and created_by
          // UPDATE: set modified_at and modified_by
          const userId = decryptedData.userid || decryptedData.empId || decryptedData.userId || "1";

          if (isEditMode) {
            // Update operation: set modified_at and modified_by
            // DO NOT include created_at, created_by, or transaction_branch_id in update payloads
            payload.data.modified_at = formattedDate;
            payload.data.modified_by = userId;

            if (import.meta.env.DEV) {
              console.log("[Save Action] Edit mode - Set modified_at/modified_by:", {
                modified_at: formattedDate,
                modified_by: userId,
              });
            }
          } else {
            // Create operation: set created_at and created_by
            // CRITICAL: These are required for insert operations
            payload.data.created_at = formattedDate;
            payload.data.created_by = userId;

            if (import.meta.env.DEV) {
              console.log("[Save Action] Create mode - Set created_at/created_by:", {
                created_at: formattedDate,
                created_by: userId,
              });
            }

            // Get branch ID - prioritize ID fields over name
            // Check multiple possible field names for branch ID (snake_case first, then camelCase)
            // Do NOT use layout.currentBranch as it contains the branch name (e.g., "Branch 2"), not the ID
            const branchId =
              decryptedData.branch_id || // snake_case (most common)
              decryptedData.branchId || // camelCase
              decryptedData.transaction_branch_id || // transaction branch ID
              decryptedData.transactionBranchId ||
              decryptedData.transaction_branchid ||
              (decryptedData.loginUser &&
                typeof decryptedData.loginUser === "object" &&
                (decryptedData.loginUser as any).branch_id) || // from loginUser object
              "";

            // If branch ID is not found, log warning with available branch-related fields
            if (!branchId && import.meta.env.DEV) {
              const branchFields = Object.keys(decryptedData).filter((key) =>
                key.toLowerCase().includes("branch")
              );
              console.warn(
                "[Save Action] Branch ID not found in decryptedData.",
                {
                  availableBranchFields: branchFields,
                  branchFieldValues: branchFields.reduce((acc, key) => {
                    acc[key] = decryptedData[key];
                    return acc;
                  }, {} as Record<string, any>),
                  layoutCurrentBranch: layout.currentBranch,
                  note: "layout.currentBranch contains branch name (e.g., 'Branch 2'), not ID. Using empty string for branch_id.",
                }
              );
            }

            payload.data.transaction_branch_id = branchId;
          }
          payload.data.id = recordId;

          // Comprehensive logging for submitted data
          console.log("=== FORM SUBMISSION LOG ===");
          console.log("[Save Action] Screen ID:", activeScreenId);
          console.log("[Save Action] Table Name:", tableName);
          console.log("[Save Action] Module Endpoint:", moduleEndpoint);
          console.log("[Save Action] Record ID:", recordId);
          console.log("[Save Action] Form Data (Raw):", formData);
          console.log("[Save Action] Form Data next_action/next_state:", {
            next_action: formData.next_action || formData.Next_Action || formData.nextAction,
            next_state: formData.next_state || formData.Next_State || formData.nextState,
          });
          const storedFormDataCheck = getState().forms.formDataByScreen[activeScreenId] || {};
          console.log("[Save Action] Stored Form Data next_action/next_state:", {
            next_action: storedFormDataCheck.next_action || storedFormDataCheck.Next_Action || storedFormDataCheck.nextAction,
            next_state: storedFormDataCheck.next_state || storedFormDataCheck.Next_State || storedFormDataCheck.nextState,
          });
          console.log("[Save Action] Payload Data:", payload.data);
          console.log("[Save Action] Subform Items:", subformItems);
          console.log("[Save Action] Field Mappings:", fieldMappings);
          console.log("[Save Action] Created At:", formattedDate);
          console.log("[Save Action] Created By:", payload.data.created_by);
          console.log(
            "[Save Action] Branch ID (transaction_branch_id):",
            payload.data.transaction_branch_id
          );
          console.log("[Save Action] Branch ID Source:", {
            branch_id: decryptedData.branch_id,
            branchId: decryptedData.branchId,
            transaction_branch_id: decryptedData.transaction_branch_id,
            transactionBranchId: decryptedData.transactionBranchId,
            loginUser_branch_id:
              decryptedData.loginUser &&
                typeof decryptedData.loginUser === "object"
                ? (decryptedData.loginUser as any).branch_id
                : undefined,
            layoutCurrentBranch: layout.currentBranch,
            note: "layout.currentBranch is branch name, not ID",
          });
          console.log("=== END SUBMISSION LOG ===");

          // CRITICAL: Ensure payload structure matches expected format
          // Expected format: { data: { ... }, next_action: "", next_state: "" }
          // Verify payload has the correct structure
          if (!payload.data) {
            // If payload.data doesn't exist, restructure the payload
            const restructuredPayload = {
              data: {
                ...payload,
                subform: payload.subform || [],
              },
              next_action: payload.next_action || "",
              next_state: payload.next_state || "",
            };
            // Remove subform from root level if it exists
            if (restructuredPayload.data.subform) {
              // subform is already in data, keep it there
            }
            // Remove next_action and next_state from data if they exist
            delete restructuredPayload.data.next_action;
            delete restructuredPayload.data.next_state;
            Object.assign(payload, restructuredPayload);
          }

          // Check for workflow state fields (like approve_status) and use their value as next_state
          // Find workflow state fields in the form configuration
          let workflowStateValue: string | undefined;
          walkComponentTree(screenConfig, (component) => {
            if (component.componentType === ComponentType.SELECT_DROPDOWN) {
              const compAny = component as any;
              const lookupName = compAny.lookupName || compAny.LookupName;

              // Check if form has Workflow property
              const findFormComponent = (comps: ComponentConfig[]): any => {
                for (const comp of comps) {
                  if (comp.componentType === LayoutTypes.FORM) {
                    return comp;
                  }
                  const compAny = comp as any;
                  if (compAny.Fields) {
                    const found = findFormComponent(compAny.Fields);
                    if (found) return found;
                  }
                  if (comp.childComponents) {
                    const found = findFormComponent(comp.childComponents);
                    if (found) return found;
                  }
                }
                return null;
              };
              const formComp = findFormComponent(screenConfig);
              const workflowName = formComp?.Workflow || formComp?.workflow;

              // If this is a workflow state field (has Workflow and LookupName)
              if (workflowName && lookupName) {
                const fieldName = compAny.name || compAny.BindValue;
                const jsonAttr = compAny.Json_Attribute;
                // Get the value from formData
                const fieldValue = formData[fieldName] || formData[jsonAttr];
                if (fieldValue !== undefined && fieldValue !== null && fieldValue !== "") {
                  workflowStateValue = String(fieldValue);
                  return true; // Stop searching
                }
              }
            }
            return false;
          });

          // Ensure next_action and next_state are at root level
          // CRITICAL: Always get from formData (set during workflow navigation), even if payload already has empty strings
          // Get from formData first, then check if we need to get from Redux store as fallback
          const formDataNextAction =
            formData.next_action ||
            formData.Next_Action ||
            formData.nextAction;
          let formDataNextState =
            formData.next_state ||
            formData.Next_State ||
            formData.nextState;

          // CRITICAL: If workflow state field has a value, use it as next_state
          // This ensures that when user selects "Rejected" or "Put on hold", next_state matches the selection
          if (workflowStateValue) {
            formDataNextState = workflowStateValue;
            if (import.meta.env.DEV) {
              console.log("[Save Action] Using workflow state field value as next_state:", {
                workflowStateValue,
                fieldName: "approve_status",
                previousNextState: formData.next_state,
              });
            }
          }

          // If not in formData, try to get from Redux store
          if (!formDataNextAction || !formDataNextState) {
            const storedFormData = getState().forms.formDataByScreen[activeScreenId] || {};
            payload.next_action = formDataNextAction ||
              storedFormData.next_action ||
              storedFormData.Next_Action ||
              storedFormData.nextAction ||
              "";
            payload.next_state = formDataNextState ||
              storedFormData.next_state ||
              storedFormData.Next_State ||
              storedFormData.nextState ||
              "";
          } else {
            payload.next_action = formDataNextAction;
            payload.next_state = formDataNextState;
          }

          if (import.meta.env.DEV) {
            console.log("[Save Action] Setting next_action and next_state:", {
              next_action: payload.next_action,
              next_state: payload.next_state,
              source: {
                fromFormData: !!(formData.next_action || formData.next_state),
                fromStore: !!(getState().forms.formDataByScreen[activeScreenId]?.next_action),
              },
            });
          }

          // Remove empty string values from payload.data
          // This ensures fields like "codeid": "", "codeid_desc": "", "item_name": "" are not included
          // CRITICAL: Always preserve parentid and parent_id fields even if empty, as backend needs them
          const cleanedPayloadData: Record<string, any> = {};
          const removedFields: string[] = [];

          // Fields that should always be preserved even if empty (backend requires them)
          // Include description fields for parentid as backend expects parent_id_desc
          const criticalFields = ['parentid', 'parent_id', 'parent_id_desc', 'id'];

          Object.entries(payload.data).forEach(([key, value]) => {
            // Always preserve critical fields even if empty
            if (criticalFields.includes(key.toLowerCase())) {
              cleanedPayloadData[key] = value !== undefined && value !== null ? value : "";
              return;
            }

            // Exclude only empty strings for other fields
            // Keep everything else: numbers (including 0), booleans, null, undefined, objects, arrays, non-empty strings
            if (value === "") {
              // Skip empty strings
              removedFields.push(key);
            } else {
              // Keep all other values
              cleanedPayloadData[key] = value;
            }
          });

          // CRITICAL: Ensure parentid is included from formData if it exists
          // Backend needs parentid to update service_details table
          if (!cleanedPayloadData.parentid && !cleanedPayloadData.parent_id) {
            // Use getParentIdFromFormData utility to robustly find parentid
            let parentId = getParentIdFromFormData(formData, "parentid");

            // If not found, also check Redux store for this screen
            if (!parentId) {
              const storedFormData = getState().forms.formDataByScreen[activeScreenId] || {};
              parentId = getParentIdFromFormData(storedFormData, "parentid");
            }

            if (parentId) {
              cleanedPayloadData.parentid = parentId;
              cleanedPayloadData.parent_id = parentId;
              // CRITICAL: Also ensure parent_id_desc is included (backend expects it)
              if (!cleanedPayloadData.parent_id_desc) {
                // Try to get description from selectedData
                const selectedData = formData.parentid_selectedData || formData.parent_id_selectedData;
                if (selectedData && typeof selectedData === 'object') {
                  cleanedPayloadData.parent_id_desc = (selectedData as any).name || (selectedData as any).label || (selectedData as any).estimation_no || "";
                } else {
                  cleanedPayloadData.parent_id_desc = "";
                }
              }
              if (import.meta.env.DEV) {
                console.log("[Save Action] Added parentid from formData (after cleaning):", {
                  parentid: parentId,
                  parent_id: parentId,
                  parent_id_desc: cleanedPayloadData.parent_id_desc,
                });
              }
            } else if (import.meta.env.DEV) {
              console.warn("[Save Action] parentid still missing after cleaning, backend may fail:", {
                activeScreenId,
                formDataSample: Object.keys(formData).slice(0, 10),
              });
            }
          }

          // CRITICAL: Ensure parent_id_desc is always present if parent_id exists
          // Backend expects parent_id_desc even if empty
          if ((cleanedPayloadData.parentid || cleanedPayloadData.parent_id) && !cleanedPayloadData.parent_id_desc) {
            cleanedPayloadData.parent_id_desc = "";
            if (import.meta.env.DEV) {
              console.log("[Save Action] Added empty parent_id_desc to match parent_id");
            }
          }

          payload.data = cleanedPayloadData;

          if (import.meta.env.DEV && removedFields.length > 0) {
            console.log("[Save Action] Removed empty string fields:", removedFields);
          }

          if (import.meta.env.DEV) {
            console.log("[Save Action] Critical fields in payload:", {
              parentid: cleanedPayloadData.parentid,
              parent_id: cleanedPayloadData.parent_id,
              parent_id_desc: cleanedPayloadData.parent_id_desc,
              id: cleanedPayloadData.id,
            });
          }

          // Dynamic endpoint and method selection based on edit mode
          // Both edit and create mode use the same endpoint: /pabws/module/{moduleEndpoint}
          // The backend determines create vs update based on the presence of ID in payload
          const endpoint = `/pabws/module/${moduleEndpoint}`;

          // CRITICAL: For 107_add_inspection, user requires a FLAT payload structure
          // Unwrap payload.data and send it directly, discarding the wrapper and next_action/next_state
          // This fixes the issue where { data: {...}, next_action: ... } was being sent instead of {...}
          let finalPayload = payload;
          if (activeScreenId === "107_add_inspection") {
            finalPayload = payload.data;
            if (import.meta.env.DEV) {
              console.log("[Save Action] Unwrapped payload for 107_add_inspection (sending flat data)");
            }
          } else if (activeScreenId === "120_add_parts_issue" || moduleEndpoint === "parts_issue") {
            // CRITICAL: For Parts Issue, use dedicated payload builder to match Blazor format exactly
            // Blazor sends: { data: {...}, next_action: "...", next_state: "..." }
            // - All fields wrapped in data object
            // - next_action and next_state at root level
            // - issued_qty = "1.0", bal_qty = "0.0" for all items
            // - All numeric values as decimal strings

            // Build form state from payload.data and formData
            const formStateForBuilder = {
              ...payload.data,
              service_spare_parts_issue: payload.data.subform?.[0]?.data || formData.service_spare_parts_issue || [],
            };

            // Use dedicated payload builder
            finalPayload = buildPartsIssuePayload(
              formStateForBuilder,
              payload.next_action || "",
              payload.next_state || ""
            );

            // Runtime validation - fail fast before API call
            const validation = validatePartsIssuePayload(finalPayload);
            if (!validation.valid) {
              const errorMessage = `Parts Issue payload validation failed:\n${validation.errors.join("\n")}`;
              console.error("[Save Action] Parts Issue Payload Validation Failed:", validation.errors);
              ensureSlidingTimeout(
                dispatch,
                {
                  message: errorMessage,
                  type: "error",
                },
                10000
              );
              dispatch(setScreenSaving({ screenId: activeScreenId, isSaving: false }));
              return;
            }

            if (import.meta.env.DEV) {
              console.log("[Save Action] Built Parts Issue payload using dedicated builder:", {
                hasData: !!finalPayload.data,
                hasSubform: !!finalPayload.data?.subform,
                subformLength: finalPayload.data?.subform?.length || 0,
                next_action: finalPayload.next_action,
                next_state: finalPayload.next_state,
              });
            }
          } else if (activeScreenId === "160_add_parts_request" || moduleEndpoint === "parts_request") {
            // CRITICAL: For Parts Request, use dedicated payload builder to match Blazor format exactly
            // Blazor sends: { data: {...}, next_action: "...", next_state: "..." }
            // - All fields wrapped in data object
            // - next_action and next_state at root level
            // - All numeric values as decimal strings (e.g., "1.0", "1000.0", "50.0")
            // - issue_date with full datetime (not midnight)

            // Build form state from payload.data and formData
            const formStateForBuilder = {
              ...payload.data,
              requested_items: payload.data.subform?.[0]?.data || formData.requested_items || [],
              parts_request_details: payload.data.subform?.[1]?.data || formData.parts_request_details || [],
            };

            // Use dedicated payload builder
            finalPayload = buildPartsRequestPayload(
              formStateForBuilder,
              payload.next_action || "",
              payload.next_state || ""
            );

            if (import.meta.env.DEV) {
              console.log("[Save Action] Built Parts Request payload using dedicated builder:", {
                hasData: !!finalPayload.data,
                hasSubform: !!finalPayload.data?.subform,
                subformLength: finalPayload.data?.subform?.length || 0,
                next_action: finalPayload.next_action,
                next_state: finalPayload.next_state,
                issue_date: finalPayload.data?.issue_date,
              });
            }
          }

          const operationType = isEditMode ? "UPDATE" : "CREATE";

          try {
            console.log(`[Save Action] Operation Type: ${operationType}`);
            console.log(`[Save Action] Edit Mode: ${isEditMode}`);
            console.log(`[Save Action] Record ID: ${recordId}`);
            console.log(`[Save Action] API Endpoint: ${endpoint}`);
            console.log(
              "[Save Action] Full Payload:",
              JSON.stringify(finalPayload, null, 2)
            );

            // GENERIC: Use PUT for create operations, POST for update operations
            // This works for all screens: estimation_add, jobcard_add, invoice_add, etc.
            // EXCEPTION: parts_request always uses the base endpoint with PUT (idempotent request)
            const isPartsRequest = moduleEndpoint === "parts_request";
            const response = isEditMode && !isPartsRequest
              ? await apiService.post(endpoint + "/update", finalPayload, {
                includeAuthToken: true,
                authHeaderType: "Bearer",
                errorHandling: "throw",
                onSuccess: (data) => {
                  console.log(
                    `[Save Action] ${operationType} API Success Response:`,
                    data
                  );
                },
                onError: (error) => {
                  console.error(
                    `[Save Action] ${operationType} API Error Response:`,
                    error
                  );
                  return null;
                },
              })
              : await apiService.put(endpoint, finalPayload, {
                includeAuthToken: true,
                authHeaderType: "Bearer",
                errorHandling: "throw",
                onSuccess: (data) => {
                  console.log(
                    `[Save Action] ${operationType} API Success Response:`,
                    data
                  );
                },
                onError: (error) => {
                  console.error(
                    `[Save Action] ${operationType} API Error Response:`,
                    error
                  );
                  return null;
                },
              });

            console.log(
              `[Save Action] ${operationType} Full Response:`,
              response
            );

            if (response.success) {
              console.log(
                `[Save Action] ${operationType} successful! Response:`,
                {
                  operationType,
                  success: response.success,
                  message: response.message,
                  data: response.data || response,
                }
              );

              // Clear all lookup caches to ensure fresh data on next load
              // This is important because after insert/update, lookup data might have changed
              const lookupNames = new Set<string>();

              // Extract all lookup names from the screen configuration
              walkComponentTree(screenConfig, (component) => {
                const comp = component as any;

                // Check for SEARCHABLE_DROPDOWN and SELECT_DROPDOWN
                if (
                  comp.componentType === ComponentType.SEARCHABLE_DROPDOWN ||
                  comp.componentType === ComponentType.SELECT_DROPDOWN ||
                  comp.componentType === "searchableDropdown" ||
                  comp.componentType === "selectDropdown"
                ) {
                  const lookupName = comp.lookupName || comp.LookupName;
                  if (lookupName) {
                    lookupNames.add(lookupName);

                    // For independent lookups, clear by lookupName
                    dispatch(clearLookup(lookupName));

                    // For dependent lookups, we need to clear all possible parent value combinations
                    // Since we don't know all parent values, we'll clear the base lookup
                    // The dependent lookups will be cleared when they're refetched
                    const parentField = comp.parentField || comp.Parent_Field;
                    if (parentField) {
                      // Clear any cached dependent lookups by checking formData
                      const parentValue = formData[parentField];
                      if (
                        parentValue !== undefined &&
                        parentValue !== null &&
                        parentValue !== ""
                      ) {
                        const dependentLookupKey = `${lookupName}_${parentValue}`;
                        dispatch(clearLookup(dependentLookupKey));
                      }
                    }
                  }
                }

                // Also check SUB_MODULE childControls for lookups
                if (
                  comp.componentType === ComponentType.SUB_MODULE &&
                  comp.childControls
                ) {
                  // CRITICAL: specific fix for SUB_MODULEs that use lookups (like customer_invoice_details)
                  const subModuleLookupName = comp.lookupName || comp.LookupName;
                  if (subModuleLookupName) {
                    lookupNames.add(subModuleLookupName);
                    // Clear independent lookup
                    dispatch(clearLookup(subModuleLookupName));

                    // Clear dependent lookup if parent field exists
                    const parentField = comp.parentField || comp.Parent_Field;
                    if (parentField) {
                      const parentValue = formData[parentField];
                      if (
                        parentValue !== undefined &&
                        parentValue !== null &&
                        parentValue !== ""
                      ) {
                        const dependentLookupKey = `${subModuleLookupName}_${parentValue}`;
                        dispatch(clearLookup(dependentLookupKey));
                      }
                    }
                  }

                  comp.childControls.forEach((childControl: any) => {
                    if (
                      childControl.componentType ===
                      ComponentType.SEARCHABLE_DROPDOWN ||
                      childControl.componentType ===
                      ComponentType.SELECT_DROPDOWN ||
                      childControl.componentType === "searchableDropdown" ||
                      childControl.componentType === "selectDropdown"
                    ) {
                      const childLookupName =
                        childControl.lookupName || childControl.LookupName;
                      if (childLookupName) {
                        lookupNames.add(childLookupName);

                        // Clear independent lookup
                        dispatch(clearLookup(childLookupName));

                        // Clear dependent lookup if parent field exists
                        const childParentField =
                          childControl.parentField ||
                          childControl.Parent_Field ||
                          comp.parentField;
                        if (childParentField) {
                          const childParentValue = formData[childParentField];
                          if (
                            childParentValue !== undefined &&
                            childParentValue !== null &&
                            childParentValue !== ""
                          ) {
                            const childDependentLookupKey = `${childLookupName}_${childParentValue}`;
                            dispatch(clearLookup(childDependentLookupKey));
                          }
                        }
                      }
                    }
                  });
                }

                return false;
              });

              console.log(
                "[Save Action] Cleared lookup caches:",
                Array.from(lookupNames)
              );

              // CRITICAL: Update formData with response data (e.g., est_no) before navigating
              // This ensures fields like Estimate No. are displayed after save
              const responseData = response.data || response;
              if (responseData && typeof responseData === "object") {
                // Build formData from response to map API fields (e.g., est_no) to form fields (e.g., txt_estimate_number)
                const updatedFormData = buildFormDataFromRecord(responseData, {
                  screenId: activeScreenId,
                  fallback: formData, // Preserve existing formData as fallback
                });

                // Merge with existing formData to preserve any user-entered data
                const mergedFormData = {
                  ...formData,
                  ...updatedFormData,
                };

                // Update formData with response data so Estimate No. and other auto-generated fields are displayed
                dispatch(
                  setScreenFormData({
                    screenId: activeScreenId,
                    data: mergedFormData,
                  })
                );

                if (import.meta.env.DEV) {
                  console.log("[Save Action] Updated formData with response data:", {
                    responseData: Object.keys(responseData),
                    updatedFormData: Object.keys(updatedFormData),
                    est_no: responseData.est_no || updatedFormData.est_no || updatedFormData.txt_estimate_number,
                  });
                }
              }

              // Customize success message based on screen
              let successMessage = response.message;
              if (!successMessage) {
                if (activeScreenId === "151") {
                  successMessage = isEditMode
                    ? "Vehicle make updated successfully!"
                    : "Vehicle make added successfully!";
                } else if (activeScreenId === "152") {
                  successMessage = isEditMode
                    ? "Vehicle type updated successfully!"
                    : "Vehicle type added successfully!";
                } else {
                  successMessage = isEditMode
                    ? "Record updated successfully!"
                    : "Record created successfully!";
                }
              }

              // If form has file components and files were uploaded, append confirmation
              // Only show this if files were actually uploaded (not just if form has file components)
              if (hasFileComponents && Object.keys(pendingFiles).length > 0 && Object.keys(uploadedImageIdsByField).length > 0) {
                const fileCount = Object.keys(uploadedImageIdsByField).length;
                successMessage += ` ${fileCount} file(s) uploaded successfully.`;
              }

              autoHideNotification(dispatch, {
                message: successMessage,
                type: "success",
              });

              // Don't reset formData - keep it so Estimate No. is displayed
              // For vehicle type (152) and vehicle make (151), always stay on the same screen
              let targetView: string;
              let isBackNavigation = false;

              if (activeScreenId === "152" || activeScreenId === "151") {
                // Always stay on the same screen for vehicle make and vehicle type
                targetView = activeScreenId;
                isBackNavigation = false;
              } else if (data?.buttonConfig?.Navigate) {
                // Allow overriding navigation target via button config
                targetView = data.buttonConfig.Navigate;
                isBackNavigation = false;
              } else {
                // For other screens, use previous view or default
                targetView = previousView || "101";
                isBackNavigation = !!previousView;
              }

              console.log("[Save Action] Navigating to:", targetView);
              dispatch(navigateToView({ view: targetView, isBack: isBackNavigation }));

              // After navigation, the formData will be cleared by the navigation logic
              // But if we want to keep it visible, we could delay the reset or not reset at all
              // For now, we'll reset after a short delay to allow the user to see the Estimate No.
              setTimeout(() => {
                dispatch(resetScreenFormData(activeScreenId));
              }, 2000); // Reset after 2 seconds to allow user to see the Estimate No.
            } else {
              console.error(`[Save Action] ${operationType} failed! Response:`, {
                operationType,
                success: response.success,
                message: response.message,
                data: response.data || response,
              });

              ensureSlidingTimeout(
                dispatch,
                {
                  message:
                    response.message ||
                    (isEditMode
                      ? "Failed to update record. Please try again."
                      : "Failed to create record. Please try again."),
                  type: "error",
                },
                5000
              );
            }
          } catch (error: any) {
            console.error(`[Save Action] Exception during ${operationType}:`, {
              operationType,
              error,
              message: error instanceof Error ? error.message : String(error),
              stack: error instanceof Error ? error.stack : undefined,
              endpoint,
              payload: payload.data,
            });

            ensureSlidingTimeout(
              dispatch,
              {
                message:
                  error instanceof Error
                    ? error.message
                    : isEditMode
                      ? "An error occurred while updating. Please try again."
                      : "An error occurred while creating. Please try again.",
                type: "error",
              },
              5000
            );
          } finally {
            dispatch(setScreenSaving({ screenId: activeScreenId, isSaving: false }));
          }

          return;
        }
        case "cancel":
        case "navigate_back": {
          const targetView = previousView || "105"; // Default to products list
          dispatch(resetScreenFormData(currentView));
          dispatch(navigateToView({ view: targetView, isBack: true }));
          return;
        }
        default:
          break;
      }
    };
